<template>
  <div class="ceo-root" :class="{ 'board-mode': boardMode }">
    <!-- ── Top bar ─────────────────────────────────────────────────────── -->
    <header class="ceo-topbar">
      <div class="brand">
        <div class="brand-logo-box"><img :src="wtiLogo" class="brand-logo" alt="WTI" /></div>
        <div class="brand-text">
          <div class="brand-title">Executive Overview</div>
          <div class="brand-sub">Wildlife Trust of India</div>
        </div>
      </div>
      <div class="topbar-actions">
        <button class="btn board-toggle" @click="boardMode = !boardMode"><v-icon size="17">{{ boardMode ? 'mdi-view-dashboard-outline' : 'mdi-presentation' }}</v-icon><span>{{ boardMode ? 'Operations View' : 'Board View' }}</span></button>
        <button v-if="boardMode" class="btn ghost noprint" @click="toggleFullscreen" title="Present the Board View full screen"><v-icon size="17">mdi-fullscreen</v-icon><span>Present</span></button>
        <button v-if="!boardMode" class="btn ghost" @click="$router.push('/ceo-pip')"><v-icon size="17">mdi-clipboard-account-outline</v-icon><span>PIP Reviews</span></button>
        <v-menu v-if="!boardMode" offset-y left>
          <template v-slot:activator="{ on, attrs }"><button class="btn ghost" v-bind="attrs" v-on="on"><v-icon size="17">mdi-cog-outline</v-icon><span>Management</span><v-icon size="14">mdi-chevron-down</v-icon></button></template>
          <v-list dense class="management-menu">
            <v-list-item @click="$router.push('/ceo-employee-mapping')"><v-list-item-icon><v-icon size="18">mdi-account-switch-outline</v-icon></v-list-item-icon><v-list-item-title>Employee Mapping</v-list-item-title></v-list-item>
            <v-list-item @click="goPiManager()"><v-list-item-icon><v-icon size="18">mdi-account-star-outline</v-icon></v-list-item-icon><v-list-item-title>Project PIs</v-list-item-title></v-list-item>
            <v-list-item @click="directoryDialog = true"><v-list-item-icon><v-icon size="18">mdi-folder-network-outline</v-icon></v-list-item-icon><v-list-item-title>Projects &amp; Divisions</v-list-item-title></v-list-item>
            <v-list-item @click="openAutoManager"><v-list-item-icon><v-icon size="18">mdi-account-clock</v-icon></v-list-item-icon><v-list-item-title>Auto-attendance ({{ autoCount }})</v-list-item-title></v-list-item>
            <v-list-item @click="openExemptManager"><v-list-item-icon><v-icon size="18">mdi-file-remove-outline</v-icon></v-list-item-icon><v-list-item-title>No-KPI staff ({{ exemptCount }})</v-list-item-title></v-list-item>
            <v-list-item @click="openTestManager"><v-list-item-icon><v-icon size="18">mdi-flag-off-outline</v-icon></v-list-item-icon><v-list-item-title>Hidden test accounts ({{ hiddenTestCount }})</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
        <span class="fy-chip" v-if="financialYear">FY {{ financialYear }}</span>
        <span class="updated" v-if="generatedAt">
          <v-icon size="15" color="#8aa89a">mdi-clock-outline</v-icon>
          {{ formatDateTime(generatedAt) }}
        </span>
        <button v-if="!boardMode" class="btn analytics" @click="$router.push('/ceo-analytics')">
          <v-icon size="18" color="#fff">mdi-chart-box</v-icon><span>Performance Analytics</span>
          <v-icon size="16" color="#fff">mdi-arrow-right</v-icon>
        </button>
        <button class="btn ghost noprint" @click="printBoard" title="Save the whole dashboard as a board-ready PDF (choose 'Save as PDF' in the print dialog)">
          <v-icon size="16" color="#14663a">mdi-file-chart-outline</v-icon><span>Board PDF</span>
        </button>
        <button v-if="!boardMode" class="btn ghost noprint" :disabled="rosterLoading" @click="downloadRoster" title="Download employee roster (ID, name, division, role, projects) as PDF">
          <v-icon size="16" :class="{ spin: rosterLoading }" color="#14663a">{{ rosterLoading ? 'mdi-loading' : 'mdi-file-pdf-box' }}</v-icon><span>{{ rosterLoading ? 'Preparing…' : 'Employee roster (PDF)' }}</span>
        </button>
        <button class="btn ghost noprint" :disabled="loading" @click="fetchOverview">
          <v-icon size="17" :class="{ spin: loading }">mdi-refresh</v-icon><span>Refresh</span>
        </button>
        <button class="btn solid" @click="logout">
          <v-icon size="17">mdi-logout</v-icon><span>Logout</span>
        </button>
      </div>
    </header>

    <div v-if="loading" class="loader-wrap">
      <v-progress-circular indeterminate color="#14663a" size="52" width="4" />
    </div>

    <main v-else class="ceo-body">
      <div v-if="errorMsg" class="banner error">
        <v-icon size="18" color="#c62828">mdi-alert-circle-outline</v-icon> {{ errorMsg }}
      </div>

      <section v-if="boardMode" class="board-view">
        <div class="board-hero">
          <div><span class="board-kicker">BOARD BRIEFING · FY {{ financialYear }}</span><h1>Organisation performance at a glance</h1><p>Delivery, financial momentum, people and the matters requiring leadership attention.</p></div>
          <div class="board-asof"><span>Prepared</span><strong>{{ formatDateTime(generatedAt) }}</strong><small>{{ boardReadiness.ready }}/{{ boardReadiness.total }} data sources ready</small></div>
        </div>

        <div class="board-source-strip" aria-label="Data readiness">
          <div v-for="source in boardSources" :key="source.label" :class="['board-source', source.state]"><i></i><span><b>{{ source.label }}</b>{{ source.detail }}</span></div>
        </div>

        <div class="board-kpi-grid">
          <article v-for="kpi in boardKpis" :key="kpi.label" class="board-kpi" :class="kpi.tone">
            <span>{{ kpi.label }}</span><strong>{{ kpi.value }}</strong><em v-if="kpi.trend" :class="kpi.trend.tone">{{ kpi.trend.text }}</em><small>{{ kpi.note }}</small>
          </article>
        </div>

        <div class="board-narrative-grid">
          <section class="board-card board-highlights">
            <div class="board-card-title"><v-icon size="19" color="#14663a">mdi-star-circle-outline</v-icon> Performance highlights</div>
            <div class="board-statement" v-for="(item, index) in boardHighlights" :key="index"><b>{{ index + 1 }}</b><span>{{ item }}</span></div>
          </section>
          <section class="board-card board-priorities">
            <div class="board-card-title"><v-icon size="19" color="#c62828">mdi-clipboard-alert-outline</v-icon> Management action register</div>
            <button v-for="(item, index) in boardActions" :key="index" @click="item.act && item.act()"><span :style="{ background: item.color }"></span><div><strong>{{ item.title }}</strong><small>{{ item.detail }}</small><div class="board-action-meta"><b>{{ item.owner }}</b><i>{{ item.timing }}</i></div></div><v-icon v-if="item.act" size="16" color="#aebbb4">mdi-chevron-right</v-icon></button>
            <div v-if="!boardActions.length" class="all-clear"><v-icon size="22" color="#2e7d32">mdi-check-circle-outline</v-icon> No critical management actions are currently flagged.</div>
          </section>
        </div>

        <div class="board-section-head"><div><span>DELIVERY</span><h2>Programmes and performance</h2></div><p>Future-quarter PIP work is excluded from the due-delivery rate.</p></div>
        <div class="board-chart-grid">
          <section class="board-card"><div class="board-card-title spread"><span>Highest-risk PIP divisions</span><b>{{ pipDueDeliveryRate }}% due delivery</b></div><apexchart v-if="boardPipRows.length" type="bar" :height="Math.max(280, boardPipRows.length * 38 + 90)" :options="boardPipRiskChart.options" :series="boardPipRiskChart.series" /><div v-else class="empty">No due PIP assessments are available.</div><p class="board-chart-note">Scope: {{ pipScopeLabel }}. Up to eight divisions ranked by overdue and not-met work; future pending activities are excluded.</p></section>
          <section class="board-card"><div class="board-card-title spread"><span>Monthly target outcomes</span><b>{{ pace.closedPct }}% of due targets closed</b></div><apexchart v-if="hasTargetTrend" type="bar" height="330" :options="targetTrendChart.options" :series="targetTrendChart.series" /><div v-else class="empty">No dated targets are available.</div><p class="board-chart-note">All FY due-date months are shown; current results are measured through {{ dashboardDateLabel }}.</p></section>
        </div>

        <div class="board-section-head"><div><span>ACCOUNTABILITY</span><h2>Division performance comparison</h2></div><p>Effectiveness combines target achievement, timeliness, attendance, engagement and available PMR data.</p></div>
        <section class="board-card board-division-card">
          <div class="board-card-title spread"><span>Leading and watch-list divisions · FY to date</span><button class="board-inline-link noprint" @click="$router.push('/ceo-analytics')">Open full analytics <v-icon size="14">mdi-arrow-right</v-icon></button></div>
          <div v-if="boardDivisionLeaders.length" class="board-division-compare">
            <div><div class="board-division-label good"><v-icon size="16" color="#2e7d32">mdi-trending-up</v-icon> Leading</div><div class="board-division-row" v-for="(division, index) in boardDivisionLeaders" :key="'lead-' + division.division"><b>{{ index + 1 }}</b><span><strong>{{ division.division }}</strong><small>{{ division.staff }} target-sheet staff · {{ division.submissionRate }}% submitted</small></span><em>{{ division.score }}</em></div></div>
            <div><div class="board-division-label watch"><v-icon size="16" color="#c62828">mdi-eye-alert-outline</v-icon> Needs attention</div><div class="board-division-row" v-for="division in boardDivisionWatch" :key="'watch-' + division.division"><b>!</b><span><strong>{{ division.division }}</strong><small>{{ division.notMet }} not met · {{ division.avgAttendance }}% attendance</small></span><em>{{ division.score }}</em></div></div>
          </div>
          <div v-else class="empty">Division effectiveness data is unavailable.</div>
        </section>

        <div class="board-section-head"><div><span>RESILIENCE</span><h2>Financial and organisational health</h2></div></div>
        <div class="board-health-grid">
          <section class="board-card"><div class="board-card-title"><v-icon size="19" color="#14663a">mdi-hand-coin-outline</v-icon> Financial momentum</div><div class="board-health-value">{{ boardFundingHeadline }}</div><p>{{ boardFundingNarrative }}</p><div class="board-mini-row" v-if="data.targets.orgTarget"><span>Organisation target</span><strong>{{ formatMoney(data.targets.orgTarget) }}</strong></div><div class="board-mini-row" v-if="data.targets.orgTarget"><span>Raised / committed</span><strong>{{ formatMoney(data.targets.raised) }} / {{ formatMoney(data.targets.committed) }}</strong></div><div class="board-mini-row" v-if="data.targets.orgTarget"><span>Remaining to target</span><strong>{{ formatMoney(boardFundingGap) }}</strong></div><div class="board-mini-row"><span>Online fundraising YTD</span><strong>{{ boardFundraisingValue }}</strong></div><div class="board-mini-row"><span>Portal donations</span><strong>{{ formatMoney(data.donations.indianAmount) }}<template v-if="data.donations.foreignAmount"> + {{ formatUsd(data.donations.foreignAmount) }}</template></strong></div></section>
          <section class="board-card"><div class="board-card-title"><v-icon size="19" color="#14663a">mdi-account-group-outline</v-icon> People and capacity</div><div class="board-health-value">{{ num(data.headcount.total) }} staff</div><p>{{ retention ? retention.summary.attritionPct + '% attrition this FY · ' + retention.summary.avgTenureYears + ' years average tenure' : 'Workforce profile for the current financial year' }}</p><div class="board-mini-row"><span>Working-day attendance · FY</span><strong>{{ daily.avgWorkingDayPercent || data.attendance.presentPercent || 0 }}%</strong></div><div class="board-mini-row"><span>On probation · current</span><strong>{{ num(data.headcount.onProbation) }}</strong></div></section>
          <section class="board-card"><div class="board-card-title"><v-icon size="19" color="#14663a">mdi-bullhorn-outline</v-icon> Reputation and reach</div><div class="board-health-value">{{ mediaStats.positivePct || 0 }}% positive</div><p>{{ mediaStats.total || 0 }} external media items reviewed in the last 30 days.</p><div class="board-mini-row"><span>Monthly digital reach</span><strong>{{ hasDigital ? compactNum(digitalLive.reach.totalReach) : 'Unavailable' }}</strong></div><div class="board-mini-row"><span>SEO score</span><strong>{{ hasDigital ? digitalLive.seo.score + '%' : 'Unavailable' }}</strong></div></section>
        </div>

        <footer class="board-method"><strong>Method note:</strong> The Organisational Pulse uses the same four components and weights for the live score and monthly history: attendance 30%, due-target closure 25%, KPI adoption 20%, and retention 25%. PIP is reported separately using work already due. Suggested owners and timings in the action register are management prompts; the underlying records remain unchanged.</footer>
      </section>

      <div v-show="!boardMode" class="operational-dashboard">

      <section class="management-hub" aria-label="Project and people governance">
        <div class="management-hub-head">
          <div><span>CEO ADMINISTRATION</span><strong>Project &amp; people governance</strong><p>Review ownership and staff coverage, then open the exact tool that needs attention.</p></div>
          <span class="governance-status" :class="governanceStatus.tone"><i></i>{{ governanceStatus.label }}</span>
        </div>
        <div class="management-card-grid">
          <button class="management-card" type="button" @click="$router.push('/ceo-employee-mapping')">
            <span class="management-card-icon"><v-icon size="22" color="#14663a">mdi-account-switch-outline</v-icon></span>
            <span class="management-card-copy"><strong>Employee mappings</strong><small>Divisions and project assignments</small><span><b>{{ governance.employeesWithProjects }}/{{ governance.totalEmployees }}</b> active staff linked to a project</span></span>
            <span class="management-card-side" :class="{ attention: governance.employeesWithoutProjects }"><b>{{ governance.employeeProjectCoverageRate }}%</b><small>{{ governance.employeesWithoutProjects }} without project</small></span>
            <v-icon size="17" color="#9aaba1">mdi-chevron-right</v-icon>
          </button>
          <button class="management-card" type="button" @click="goPiManager()">
            <span class="management-card-icon pi"><v-icon size="22" color="#9a5a00">mdi-account-star-outline</v-icon></span>
            <span class="management-card-copy"><strong>Project PIs</strong><small>Accountable project ownership</small><span><b>{{ governance.projectsWithPi }}/{{ governance.totalProjects }}</b> active projects have a PI</span></span>
            <span class="management-card-side" :class="{ attention: governance.projectsMissingPi }"><b>{{ governance.piCoverageRate }}%</b><small>{{ governance.projectsMissingPi }} need PI</small></span>
            <v-icon size="17" color="#9aaba1">mdi-chevron-right</v-icon>
          </button>
          <button class="management-card" type="button" @click="directoryDialog = true">
            <span class="management-card-icon directory"><v-icon size="22" color="#1565c0">mdi-folder-network-outline</v-icon></span>
            <span class="management-card-copy"><strong>Projects &amp; divisions</strong><small>Master structure and shortcodes</small><span><b>{{ governance.totalProjects }}</b> projects across {{ governance.totalDivisions }} divisions</span></span>
            <span class="management-card-side" :class="{ attention: governance.unstaffedProjects || governance.projectsMissingDivision }"><b>{{ governance.projectStaffCoverageRate }}%</b><small>{{ governance.unstaffedProjects }} without staff</small></span>
            <v-icon size="17" color="#9aaba1">mdi-chevron-right</v-icon>
          </button>
        </div>
      </section>

      <section class="reporting-scope" aria-label="Dashboard reporting periods">
        <div class="reporting-scope-title"><v-icon size="18" color="#14663a">mdi-calendar-clock-outline</v-icon><span><strong>Reporting scope</strong><small>How to read the dashboard</small></span></div>
        <div class="reporting-scope-item"><span>Dashboard</span><strong>As of {{ dashboardAsOfLabel }}</strong></div>
        <div class="reporting-scope-item"><span>Financial year</span><strong>{{ fyScopeLabel }}</strong></div>
        <div class="reporting-scope-item"><span>Current month</span><strong>{{ currentMonthScopeLabel }}</strong></div>
        <div class="reporting-scope-item" v-if="pipReady"><span>PIP delivery</span><strong>{{ pipScopeLabel }}</strong></div>
        <div class="reporting-scope-item"><span>Media pulse</span><strong>Rolling last 30 days</strong></div>
        <div class="reporting-scope-item"><span>People &amp; projects</span><strong>Current active records</strong></div>
      </section>

      <!-- ═══ EXECUTIVE BRIEFING ════════════════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-lightbulb-on-outline</v-icon> Executive Briefing <span class="section-scope">as of {{ dashboardDateLabel }}</span></div>
      <section class="brief-grid">
        <!-- Organisational Pulse -->
        <div class="panel pulse-panel">
          <div class="panel-title mb">
            <v-icon size="18" :color="pulseView.status.color">mdi-heart-pulse</v-icon> Organisational Pulse
            <button v-if="pulseTimeline.length" class="pulse-play" @click="togglePulsePlay" :title="pulsePlaying ? 'Pause' : 'Play monthly progress'">
              <v-icon size="15" color="#14663a">{{ pulsePlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
              <span>{{ pulsePlaying ? 'Playing' : 'Play' }}</span>
            </button>
          </div>
          <div class="pulse-main">
            <div class="pulse-ring" :style="{ background: 'conic-gradient(' + pulseView.status.color + ' ' + (pulseView.score * 3.6) + 'deg, #eef2f0 0deg)' }">
              <div class="pulse-inner"><div class="pulse-score" :style="{ color: pulseView.status.color }">{{ pulseView.score }}</div><div class="pulse-den">/100</div></div>
            </div>
            <div class="pulse-status" :style="{ background: pulseView.status.color }">{{ pulseView.status.label }}</div>
            <div class="pulse-month" :class="{ live: pulseView.label === 'Now' }">{{ pulseView.label === 'Now' ? '● Live · now' : pulseView.label }}</div>
          </div>
          <div class="pulse-scrub" v-if="pulseTimeline.length">
            <button v-for="(m, i) in pulseTimeline" :key="i" class="ptick" :class="{ on: pulseIdx === i }" @click="scrubPulse(i)" :title="m.label + ' · pulse ' + m.score">{{ m.month }}</button>
            <button class="ptick now" :class="{ on: pulseIdx < 0 }" @click="scrubPulse(-1)" title="Live / now">Now</button>
          </div>
          <div class="pulse-bars">
            <div class="pb-row" v-for="c in pulseView.components" :key="c.key" :title="c.key === 'pip' ? 'Overall PIP completion across every applicable item in the financial year' : ''">
              <span class="pb-lbl">{{ c.label }}</span>
              <span class="pb-track"><span class="pb-fill" :style="{ width: Math.min(100, c.value) + '%', background: barColor(c.value) }"></span></span>
              <span class="pb-val">{{ c.value }}%</span>
            </div>
          </div>
          <div v-if="pulseView.label === 'Now'" class="pulse-method">Current score uses FY-to-date attendance and retention, due-target closure and current KPI adoption · overall PIP completion is shown alongside.</div>
        </div>

        <!-- Today's briefing + FY pace -->
        <div class="panel brief-panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-text-box-outline</v-icon> Today's Briefing <span class="hint">· auto-generated</span></div>
          <ul class="brief-list">
            <li v-for="(s, i) in briefing" :key="i"><v-icon size="16" color="#14663a">mdi-circle-medium</v-icon><span>{{ s }}</span></li>
            <li v-if="!briefing.length" class="dim">Gathering signals…</li>
          </ul>
          <div class="pace-box">
            <div class="pace-head"><span><v-icon size="15" color="#14663a">mdi-speedometer</v-icon> FY Pace</span><span class="pace-verdict" :class="paceClass">{{ pace.verdict }}</span></div>
            <div class="pace-line"><span class="pace-k">{{ pace.elapsedPct }}%</span><span class="pace-t"><span class="pace-f time" :style="{ width: pace.elapsedPct + '%' }"></span></span><span class="pace-cap">of FY elapsed · {{ pace.daysLeft }}d left</span></div>
            <div class="pace-line"><span class="pace-k">{{ pace.closedPct }}%</span><span class="pace-t"><span class="pace-f done" :style="{ width: pace.closedPct + '%' }"></span></span><span class="pace-cap">of due targets closed ({{ pace.closed }}/{{ pace.due }})</span></div>
          </div>
        </div>

        <!-- Needs your attention -->
        <div class="panel alert-panel">
          <div class="panel-title mb"><v-icon size="18" color="#c62828">mdi-bell-alert-outline</v-icon> Needs Your Attention <span class="alert-count" v-if="alerts.length">{{ alerts.length }}</span></div>
          <div class="alert-list">
            <button class="alert-item" v-for="(a, i) in visibleAlerts" :key="i" :class="['sev' + a.sev, { noact: !a.act }]" @click="a.act && a.act()">
              <span class="ai-ic" :style="{ background: a.color + '1f' }"><v-icon size="17" :color="a.color">{{ a.icon }}</v-icon></span>
              <span class="ai-body"><span class="ai-title">{{ a.title }}</span><span class="ai-detail">{{ a.detail }}</span></span>
              <v-icon v-if="a.act" size="16" color="#c9d6ce">mdi-chevron-right</v-icon>
            </button>
            <button v-if="alerts.length > 5" class="alerts-more" @click="alertsExpanded = !alertsExpanded">{{ alertsExpanded ? 'Show priority alerts only' : 'View ' + (alerts.length - 5) + ' more operational alerts' }} <v-icon size="15">{{ alertsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></button>
            <div v-if="!alerts.length" class="all-clear"><v-icon size="22" color="#2e7d32">mdi-check-circle-outline</v-icon> All clear — nothing urgent.</div>
          </div>
        </div>
      </section>

      <!-- ═══ PIP DELIVERY INTELLIGENCE ════════════════════════════════ -->
      <template v-if="pipReady">
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-clipboard-pulse-outline</v-icon> PIP Delivery Intelligence · FY {{ pip.financialYear }}</div>
        <section class="grid grid-2-1 pip-dashboard-grid">
          <div class="panel pip-chart-panel">
            <div class="panel-head">
              <div class="panel-title"><div class="ph-ic"><v-icon size="20" color="#14663a">mdi-chart-bar-stacked</v-icon></div><div><div class="pt-main">PIP Outcomes &amp; Delivery Rate by Division</div><div class="pt-sub">Each row shows its rate; click it for project, quarter and employee analysis</div></div></div>
              <button class="link-chip" @click="$router.push('/ceo-pip')">Open PIP review <v-icon size="14">mdi-arrow-right</v-icon></button>
            </div>
            <apexchart type="bar" :height="pipChartHeight" :options="pipDivisionChart.options" :series="pipDivisionChart.series" />
          </div>
          <div class="panel pip-snapshot">
            <div class="pip-snapshot-head"><div class="panel-title"><v-icon size="18" color="#14663a">mdi-clipboard-check-outline</v-icon> PIP Snapshot</div><span class="pip-period-badge"><v-icon size="13" color="#14663a">mdi-calendar-range</v-icon>{{ pipScopeLabel }}</span></div>
            <p class="pip-scope-note">Rate includes recorded outcomes and overdue pending items through this date. Future pending work is excluded.</p>
            <div class="pip-score-card">
              <div class="pip-score-ring" :style="{ background: 'conic-gradient(' + barColor(pipDueDeliveryRate) + ' ' + (pipDueDeliveryRate * 3.6) + 'deg, #edf2ef 0deg)' }"><div><strong>{{ pipDueDeliveryRate }}%</strong><span>due delivery</span></div></div>
              <div class="pip-score-copy"><strong>{{ num(pip.summary.mappedEmployees) }}</strong><span>employees linked across {{ pip.summary.divisionSources || 0 }} divisions · measured {{ pipScopeLabel.toLowerCase() }}</span></div>
            </div>
            <div class="pip-mini-grid">
              <button @click="$router.push('/ceo-pip')"><strong class="ok">{{ num(pip.summary.met) }}</strong><span>Met</span></button>
              <button @click="$router.push('/ceo-pip')"><strong class="warn">{{ num(pip.summary.delayedMet) }}</strong><span>Delayed</span></button>
              <button @click="$router.push('/ceo-pip')"><strong class="bad">{{ num(pip.summary.notMet) }}</strong><span>Not met</span></button>
              <button @click="$router.push({ path: '/ceo-pip', query: { tab: 'mapping' } })"><strong>{{ num(pip.summary.unresolvedLabels) }}</strong><span>Unresolved names</span></button>
            </div>
            <button class="pip-watch" v-if="pipWatchDivision" @click="openPipDivision(pipWatchDivision)"><v-icon size="17" color="#c62828">mdi-alert-circle-outline</v-icon><div><strong>{{ pipWatchDivision.name }} needs attention</strong><span>{{ pipWatchDivision.counts.notMet }} not met · {{ pipWatchDivision.counts.dueDeliveryRate || 0 }}% due delivery · {{ pipScopeLabel }} · view analysis</span></div><v-icon size="16" color="#c98f8f">mdi-chevron-right</v-icon></button>
          </div>
        </section>
      </template>

      <!-- Projections & Rankings -->
      <section class="grid grid-2" v-if="rankings">
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-trending-up</v-icon> Projections &amp; Momentum</div>
          <div class="proj-grid">
            <div class="proj"><div class="proj-v">{{ formatMoney(donationProjection.indian) }}</div><div class="proj-l">Projected Indian donations · year-end</div></div>
            <div class="proj"><div class="proj-v">{{ formatUsd(donationProjection.foreign) }}</div><div class="proj-l">Projected foreign · year-end</div></div>
            <div class="proj"><div class="proj-v">{{ pace.daysLeft }}</div><div class="proj-l">Days left in FY {{ financialYear }}</div></div>
          </div>
          <div class="proj-note"><v-icon size="14" color="#a7b3ac">mdi-information-outline</v-icon> Run-rate projection from {{ pace.elapsedPct }}% of the FY elapsed.</div>
          <button class="link-chip" style="margin-top:10px" @click="$router.push({ path: '/ceo-analytics', query: { tab: 'donations_yoy' } })">
            <v-icon size="15" color="#14663a">mdi-chart-timeline-variant</v-icon> Multi-Year YoY Donations
          </button>
        </div>
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-trophy-outline</v-icon> Insights &amp; Rankings</div>
          <div class="rank-grid">
            <div class="rank best" v-if="rankings.topDivision"><div class="rk-l">▲ Top division</div><div class="rk-v">{{ rankings.topDivision.division }}</div><div class="rk-s">effectiveness {{ rankings.topDivision.score }}</div></div>
            <div class="rank worst" v-if="rankings.bottomDivision"><div class="rk-l">▼ Needs support</div><div class="rk-v">{{ rankings.bottomDivision.division }}</div><div class="rk-s">effectiveness {{ rankings.bottomDivision.score }}</div></div>
            <div class="rank best" v-if="rankings.topManager"><div class="rk-l">▲ Top team</div><div class="rk-v">{{ rankings.topManager.name }}</div><div class="rk-s">team avg {{ rankings.topManager.teamAvgScore }}</div></div>
            <div class="rank worst" v-if="rankings.bottomManager"><div class="rk-l">▼ Lagging team</div><div class="rk-v">{{ rankings.bottomManager.name }}</div><div class="rk-s">team avg {{ rankings.bottomManager.teamAvgScore }}</div></div>
          </div>
          <div class="rank-emps" v-if="rankings.topEmployees.length">
            <span class="re-lbl">Top performers</span>
            <span class="re-chip" v-for="e in rankings.topEmployees" :key="e.id">{{ e.name }} <b>{{ e.score }}</b></span>
          </div>
          <div style="display:flex; gap:8px; margin-top:12px; flex-wrap:wrap;">
            <button class="link-chip" @click="$router.push('/ceo-analytics')"><v-icon size="15" color="#14663a">mdi-chart-box-outline</v-icon> Full Analytics</button>
            <button class="link-chip" @click="$router.push({ path: '/ceo-analytics', query: { tab: 'talent_matrix' } })"><v-icon size="15" color="#14663a">mdi-grid</v-icon> 9-Box Talent Matrix</button>
            <button class="link-chip" @click="$router.push({ path: '/ceo-analytics', query: { tab: 'projects' } })"><v-icon size="15" color="#14663a">mdi-compass-outline</v-icon> Project Benchmarks</button>
          </div>
        </div>
      </section>

      <!-- ═══ EXTERNAL MEDIA & NEWS PULSE ═══════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-newspaper-variant-outline</v-icon> External Media &amp; News Pulse</div>
      <section class="panel media-news-panel">
        <div class="panel-head">
          <div class="panel-title">
            <div class="ph-ic"><v-icon size="20" color="#14663a">mdi-bullhorn-outline</v-icon></div>
            <div>
              <div class="pt-main">Recent Third-Party Media Coverage</div>
              <div class="pt-sub">External coverage of WTI · weighted by channel authority · last 30 days</div>
            </div>
          </div>
          <div class="ph-actions">
            <span class="media-stat-chip pos" v-if="mediaStats.total">
              <v-icon size="14" color="#2e7d32">mdi-emoticon-happy-outline</v-icon> {{ mediaStats.positivePct }}% Positive ({{ mediaStats.positive }}/{{ mediaStats.total }})
            </span>
            <span class="media-stat-chip" v-if="mediaImpact && mediaImpact.tier1Pct != null" :class="mediaImpact.reputationRisk >= 40 ? 'risk' : 'tier'" :title="'Tier-1 national outlets ' + mediaImpact.tier1Pct + '% · reputation risk ' + mediaImpact.reputationRisk + '%'">
              <v-icon size="14" :color="mediaImpact.reputationRisk >= 40 ? '#c62828' : '#14663a'">mdi-bullseye-arrow</v-icon> {{ mediaImpact.tier1Pct }}% Tier-1 · risk {{ mediaImpact.reputationRisk }}%
            </span>
            <button class="link-chip" :disabled="mediaScraping" @click="fetchMediaScraper" title="Run live media news crawler">
              <v-icon size="15" :class="{ spin: mediaScraping }" color="#14663a">mdi-refresh</v-icon> {{ mediaScraping ? 'Scanning...' : 'Scrape Live News' }}
            </button>
            <button class="link-chip highlight" @click="$router.push('/ceo-media-news')">
              <v-icon size="15" color="#14663a">mdi-newspaper</v-icon> View More Media News
              <v-icon size="14" color="#14663a">mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>

        <div v-if="mediaLoading" class="media-loader">
          <v-progress-circular indeterminate color="#14663a" size="28" width="3" />
          <span>Fetching recent media news…</span>
        </div>

        <div v-else-if="mediaItems.length" class="media-news-grid">
          <div v-for="item in mediaItems.slice(0, 4)" :key="item._id" class="mn-card" :class="item.sentiment">
            <div class="mn-top">
              <span class="mn-pub">
                <v-icon size="14" color="#14663a">mdi-newspaper</v-icon> {{ item.publisher || 'External Media' }}
                <span class="mn-da" :class="tierClass(item.tier)" v-if="item.authority" :title="'Channel authority ~' + item.authority + ' · Tier ' + item.tier">{{ item.authority }}</span>
              </span>
              <span class="mn-badge" :class="item.sentiment">
                <v-icon size="12">{{ item.sentiment === 'positive' ? 'mdi-emoticon-happy' : (item.sentiment === 'negative' ? 'mdi-emoticon-sad' : 'mdi-minus-circle') }}</v-icon>
                {{ item.sentiment.toUpperCase() }}
              </span>
            </div>
            <a :href="item.articleUrl" target="_blank" rel="noopener noreferrer" class="mn-title">
              {{ item.title }}
              <v-icon size="13" color="#14663a" class="mn-ext">mdi-open-in-new</v-icon>
            </a>
            <p class="mn-snippet" v-if="item.snippet">{{ item.snippet }}</p>
            <div class="mn-footer">
              <span class="mn-date"><v-icon size="13" color="#8aa89a">mdi-clock-outline</v-icon> {{ formatMediaDate(item.publishedAt) }}</span>
              <span class="mn-author" v-if="item.author">by {{ item.author }}</span>
            </div>
          </div>
        </div>

        <div v-else class="media-empty">
          <v-icon size="32" color="#a7b3ac">mdi-newspaper-remove</v-icon>
          <span>No recent external media news items found.</span>
          <button class="btn ghost btn-sm" @click="fetchMediaScraper">Run Scraper</button>
        </div>
      </section>

      <!-- ═══ DIVISION LEAGUE TABLE ═════════════════════════════════════ -->
      <template v-if="divisionRows.length">
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-table-large</v-icon> Division League Table <span class="section-scope">FY to date</span></div>
        <section class="panel">
          <div class="panel-title mb">
            <v-icon size="18" color="#14663a">mdi-sitemap-outline</v-icon> Divisions ranked
            <span class="hint">· click a column to sort · green = strong, red = needs attention</span>
          </div>
          <div class="lg-wrap">
            <table class="lg-table">
              <thead>
                <tr>
                  <th class="lg-name" @click="sortDivisions('division')">Division <v-icon size="12" v-if="divSortKey==='division'">{{ divSortDir>0?'mdi-arrow-up':'mdi-arrow-down' }}</v-icon></th>
                  <th v-for="c in divCols" :key="c.key" @click="sortDivisions(c.key)" :title="c.hint">
                    {{ c.label }} <v-icon size="12" v-if="divSortKey===c.key">{{ divSortDir>0?'mdi-arrow-up':'mdi-arrow-down' }}</v-icon>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in divisionRows" :key="d.division">
                  <td class="lg-name clickable" @click="openDivision(d.division)" title="View the staff in this division">{{ d.division }} <v-icon size="13" color="#9bb0a4">mdi-account-search-outline</v-icon></td>
                  <td v-for="c in divCols" :key="c.key" class="lg-cell"
                      :style="{ background: divCellBg(c, d[c.key]), color: divCellFg(c, d[c.key]) }">
                    {{ divCellText(c, d[c.key]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>

      <!-- ═══ RETENTION & ATTRITION ═════════════════════════════════════ -->
      <template v-if="retention">
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-account-arrow-right-outline</v-icon> Retention &amp; Attrition <span class="section-scope">{{ fyScopeLabel }}</span></div>
        <section class="panel">
          <div class="rt-tiles">
            <div class="rt-tile">
              <div class="rt-v" :class="attritionClass">{{ retention.summary.attritionPct }}%</div>
              <div class="rt-l">Attrition this FY <span class="rt-hint">exits ÷ (staff + exits)</span></div>
            </div>
            <div class="rt-tile">
              <div class="rt-v">{{ retention.summary.annualizedPct }}%</div>
              <div class="rt-l">Annualised pace <span class="rt-hint">projected to year-end</span></div>
            </div>
            <div class="rt-tile">
              <div class="rt-v">{{ retention.summary.exits }}</div>
              <div class="rt-l">Exits (FY) <span class="rt-hint clickable" @click="goResigned">view list →</span></div>
            </div>
            <div class="rt-tile">
              <div class="rt-v">{{ retention.summary.avgTenureYears }} <small>yrs</small></div>
              <div class="rt-l">Avg tenure <span class="rt-hint">of active staff</span></div>
            </div>
          </div>

          <div class="rt-grid">
            <div class="rt-card">
              <div class="rt-title"><v-icon size="16" color="#14663a">mdi-swap-vertical</v-icon> Joiners vs Exits · by month</div>
              <apexchart type="bar" height="230" :options="retentionFlowChart.options" :series="retentionFlowChart.series" />
            </div>
            <div class="rt-card">
              <div class="rt-title"><v-icon size="16" color="#14663a">mdi-timer-sand</v-icon> Tenure of active staff</div>
              <div class="rt-bars">
                <div class="rt-bar-row" v-for="t in tenureBars" :key="t.label">
                  <span class="rt-bar-name">{{ t.label }}</span>
                  <span class="rt-bar-track"><span class="rt-bar-fill" :style="{ width: t.pct + '%' }"></span></span>
                  <span class="rt-bar-count">{{ t.count }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="rt-grid">
            <div class="rt-card" v-if="exitReasonBars.length">
              <div class="rt-title"><v-icon size="16" color="#c0562c">mdi-comment-question-outline</v-icon> Why people left</div>
              <div class="rt-bars">
                <div class="rt-bar-row" v-for="r in exitReasonBars" :key="r.label">
                  <span class="rt-bar-name" :title="r.label">{{ r.label }}</span>
                  <span class="rt-bar-track"><span class="rt-bar-fill neg" :style="{ width: r.pct + '%' }"></span></span>
                  <span class="rt-bar-count">{{ r.count }}</span>
                </div>
              </div>
            </div>
            <div class="rt-card" v-if="divisionAttritionRows.length">
              <div class="rt-title"><v-icon size="16" color="#14663a">mdi-sitemap-outline</v-icon> Attrition by division <span class="rt-hint">· exits · rate</span></div>
              <div class="rt-bars">
                <div class="rt-bar-row" v-for="d in divisionAttritionRows" :key="d.division">
                  <span class="rt-bar-name clickable" :title="'View ' + d.division" @click="openDivision(d.division)">{{ d.division }}</span>
                  <span class="rt-bar-track"><span class="rt-bar-fill" :class="d.attritionPct >= 20 ? 'neg' : (d.attritionPct >= 10 ? 'warn' : '')" :style="{ width: d.pct + '%' }"></span></span>
                  <span class="rt-bar-count">{{ d.exits }} · {{ d.attritionPct }}%</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>

      <!-- ═══ TARGETS (primary) ═════════════════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-bullseye-arrow</v-icon> Targets &amp; Performance <span class="section-scope">FY {{ financialYear }} to date</span></div>

      <!-- Current-month focus: what's due / pending / overdue right now -->
      <div class="cm-focus">
        <div class="cmf-lead">
          <div class="cmf-ic"><v-icon size="24" color="#b45309">mdi-calendar-alert</v-icon></div>
          <div>
            <div class="cmf-title">This Month · {{ cm.label || financialYear }}</div>
            <div class="cmf-sub">Targets due now and overdue items needing your attention</div>
          </div>
        </div>
        <div class="cmf-stats">
          <button class="cmf-stat due" @click="openEmployeeTargets" title="View this month's targets">
            <div class="cmf-v">{{ num(cm.due) }}</div><div class="cmf-l">Due this month</div>
          </button>
          <button class="cmf-stat pending" @click="openEmployeeTargets" title="View pending targets">
            <div class="cmf-v">{{ num(cm.pending) }}</div><div class="cmf-l">Still pending</div>
          </button>
          <button class="cmf-stat overdue" @click="goOutcome('notmet')" title="View overdue / delayed targets">
            <div class="cmf-v">{{ num(cm.overdue) }}</div><div class="cmf-l">Overdue · delayed</div>
          </button>
          <div class="cmf-stat met">
            <div class="cmf-v">{{ num(cm.met) }}</div><div class="cmf-l">Met this month</div>
          </div>
        </div>
      </div>

      <section class="grid grid-2-1">
        <!-- Performance targets (employee KPI) -->
        <div class="panel hero">
          <div class="panel-head">
            <div class="panel-title">
              <div class="ph-ic"><v-icon size="20" color="#14663a">mdi-clipboard-check-outline</v-icon></div>
              <div><div class="pt-main">Performance Targets</div><div class="pt-sub">Employee KPI target-sheet status</div></div>
            </div>
            <div class="ph-actions">
              <span class="year-chip">{{ num(data.performance.total) }} targets</span>
              <button class="link-chip" @click="openEmployeeTargets">
                <v-icon size="15" color="#14663a">mdi-account-details</v-icon> By employee
              </button>
              <button v-if="data.resignedExcluded" class="link-chip resigned" @click="goResigned" title="Staff who resigned/left this financial year — their targets are excluded from these totals. Click to view.">
                <v-icon size="15" color="#b45309">mdi-account-arrow-right-outline</v-icon> {{ data.resignedExcluded }} left this FY · excluded
              </button>
            </div>
          </div>

          <div class="perf-body">
            <div class="perf-left">
              <!-- Target OUTCOMES — primary, click to drill down -->
              <div class="perf-kpis">
                <div v-for="p in outcomeKpis" :key="p.label" class="perf-chip clickable" :style="{ '--c': p.color }" @click="goOutcome(p.key)" :title="'View ' + p.label + ' targets'">
                  <div class="perf-ic"><v-icon :color="p.color" size="20">{{ p.icon }}</v-icon></div>
                  <div>
                    <div class="perf-val">{{ p.value }}</div>
                    <div class="perf-lbl">{{ p.label }} · {{ p.pct }}%</div>
                  </div>
                </div>
              </div>

              <!-- Outcome composition bar -->
              <div class="seg-wrap">
                <div class="seg-bar">
                  <div v-for="s in outcomeSegments" :key="s.label" class="seg" :style="{ width: s.pct + '%', background: s.color }" :title="s.label + ': ' + s.count"></div>
                </div>
                <div class="seg-legend">
                  <span class="seg-met">{{ outcome.overallMetPct }}% met</span>
                  <span v-for="s in outcomeSegments" :key="s.label"><i :style="{ background: s.color }"></i>{{ s.label }} <b>{{ s.pct }}%</b></span>
                </div>
              </div>

              <!-- Submission status — secondary (moved down) -->
              <div class="outcome-block">
                <div class="ob-top"><span class="ob-label">Submission status · click for details</span></div>
                <div class="ob-chips">
                  <button class="ochip" @click="openEmployeeTargets"><span class="ocdot" style="background:#94a3b8"></span>Not Submitted <b>{{ num(data.performance.notSubmitted) }}</b></button>
                  <button class="ochip" @click="openEmployeeTargets"><span class="ocdot" style="background:#1565c0"></span>Pending Approval <b>{{ num(data.performance.pendingApproval) }}</b></button>
                  <button class="ochip" @click="openEmployeeTargets"><span class="ocdot" style="background:#2e7d32"></span>Approved <b>{{ num(data.performance.approved) }}</b></button>
                  <button class="ochip" @click="openEmployeeTargets"><span class="ocdot" style="background:#c62828"></span>Rejected <b>{{ num(data.performance.rejected) }}</b></button>
                </div>
              </div>
            </div>

            <div class="perf-right">
              <div class="hbars" v-if="data.performance.total">
                <div class="hbar-cap">Outcome breakdown</div>
                <div class="hbar-row" v-for="b in outcomeBars" :key="b.label">
                  <span class="hbar-lbl">{{ b.label }}</span>
                  <span class="hbar-track"><span class="hbar-fill" :style="{ width: barPct(b.value, outcomeBars) + '%', background: b.color }"></span></span>
                  <span class="hbar-num">{{ num(b.value) }}</span>
                </div>
              </div>
              <div v-else class="empty">No target data.</div>
            </div>
          </div>
        </div>

        <!-- Fundraising targets -->
        <div class="panel">
          <div class="panel-head">
            <div class="panel-title">
              <div class="ph-ic amber"><v-icon size="20" color="#b56b00">mdi-hand-coin-outline</v-icon></div>
              <div><div class="pt-main">Fundraising Targets</div><div class="pt-sub">Raised vs organisation target · this FY</div></div>
            </div>
          </div>
          <template v-if="hasFundraising">
            <div class="fund-gauge">
              <apexchart type="radialBar" height="210" :options="targetGauge.options" :series="targetGauge.series" />
            </div>
            <div class="fund-stats">
              <div class="fs"><div class="fs-v">{{ formatMoney(data.targets.raised) }}</div><div class="fs-l">Raised</div></div>
              <div class="fs"><div class="fs-v">{{ formatMoney(data.targets.committed) }}</div><div class="fs-l">Committed</div></div>
              <div class="fs"><div class="fs-v">{{ formatMoney(data.targets.assignedTotal) }}</div><div class="fs-l">Assigned · {{ data.targets.projectCount }} proj</div></div>
            </div>
          </template>
          <div v-else class="fund-empty">
            <v-icon size="34" color="#c9d6ce">mdi-hand-coin-outline</v-icon>
            <div class="fe-main">No fundraising targets set</div>
            <div class="fe-sub">for FY {{ financialYear }}</div>
          </div>
        </div>
      </section>

      <!-- ═══ TARGETS BY MONTH ══════════════════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-chart-bar-stacked</v-icon> Targets by Month · Apr → Mar {{ financialYear }}</div>
      <section class="panel">
        <div class="panel-head">
          <div class="panel-title">
            <div class="ph-ic"><v-icon size="20" color="#14663a">mdi-calendar-month-outline</v-icon></div>
            <div><div class="pt-main">Monthly Target Outcomes</div><div class="pt-sub">By due-date month · met on time / delayed met / not met / pending</div></div>
          </div>
          <span class="year-chip">FY {{ financialYear }}</span>
        </div>
        <apexchart v-if="hasTargetTrend" type="bar" height="330" :options="targetTrendChart.options" :series="targetTrendChart.series" />
        <div v-else class="empty">No dated targets to chart yet.</div>
      </section>

      <!-- ═══ DONATIONS (financial year) ════════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-gift-outline</v-icon> Donations via WTI Portal · FY {{ financialYear }}</div>
      <section class="grid grid-2">
        <div class="panel don-card">
          <div class="don-head">
            <div class="don-ic green"><v-icon color="#14663a" size="24">mdi-currency-inr</v-icon></div>
            <div>
              <div class="don-title">Indian Donations</div>
              <div class="don-sub">{{ num(data.donations.indianCount) }} successful · via portal</div>
            </div>
          </div>
          <div class="don-amount">{{ formatMoney(data.donations.indianAmount) }}</div>
        </div>
        <div class="panel don-card">
          <div class="don-head">
            <div class="don-ic amber"><v-icon color="#b56b00" size="24">mdi-earth</v-icon></div>
            <div>
              <div class="don-title">Foreign Donations</div>
              <div class="don-sub">{{ num(data.donations.foreignCount) }} successful · via portal</div>
            </div>
          </div>
          <div class="don-amount">{{ formatUsd(data.donations.foreignAmount) }}</div>
        </div>
      </section>

      <!-- ═══ FUNDRAISING · LIVE FROM GOOGLE SHEET ══════════════════════ -->
      <div class="section-eyebrow" v-if="hasFundraisingLive"><v-icon size="16" color="#14663a">mdi-hand-coin-outline</v-icon> Fundraising &amp; Donations</div>
      <section class="grid grid-2-1" v-if="hasFundraisingLive">
        <div class="panel">
          <div class="panel-head">
            <div class="panel-title">
              <div class="ph-ic"><v-icon size="20" color="#14663a">mdi-hand-coin</v-icon></div>
              <div><div class="pt-main">Online Fundraising · {{ fundraisingLive.online.fy }}</div><div class="pt-sub">Money raised online this financial year</div></div>
            </div>
            <span class="fr-live"><span class="fr-dot"></span> live</span>
          </div>
          <div class="fr-hero">
            <div class="fr-big">{{ formatMoney(fundraisingLive.online.ytd) }}</div>
            <div class="fr-cap">raised online · year-to-date</div>
          </div>
          <apexchart v-if="fundraisingLive.online.ytd" type="bar" height="210" :options="frMonthlyChart.options" :series="frMonthlyChart.series" />
          <div class="fr-platforms" v-if="fundraisingLive.online.platforms.length">
            <div class="fr-pf" v-for="p in fundraisingLive.online.platforms" :key="p.name">
              <span class="fr-pf-n">{{ p.name }}</span><span class="fr-pf-v">{{ formatMoney(p.amount) }}</span>
            </div>
          </div>
        </div>
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-chart-timeline-variant</v-icon> Donations Trend <span class="hint">· ₹ lakhs</span></div>
          <apexchart v-if="(fundraisingLive.trend || []).length" type="bar" height="240" :options="frTrendChart.options" :series="frTrendChart.series" />
          <div class="fr-latest" v-if="fundraisingLive.trend && fundraisingLive.trend.length">
            Latest <b>{{ fundraisingLive.trend[fundraisingLive.trend.length - 1].period }}</b>: ₹{{ fundraisingLive.trend[fundraisingLive.trend.length - 1].total }} L total
            <span class="dim">(Website ₹{{ fundraisingLive.trend[fundraisingLive.trend.length - 1].website }} L · Online ₹{{ fundraisingLive.trend[fundraisingLive.trend.length - 1].online }} L)</span>
          </div>
          <div class="fr-src"><v-icon size="12" color="#a7b3ac">mdi-update</v-icon> updated {{ formatDateTime(fundraisingLive.fetchedAt) }}</div>
        </div>
      </section>

      <!-- ═══ DIGITAL OUTREACH & BRAND ═════════════════════════════════ -->
      <div class="section-eyebrow" v-if="hasDigital"><v-icon size="16" color="#14663a">mdi-bullhorn-outline</v-icon> Digital Outreach &amp; Brand</div>
      <section class="grid grid-2-1" v-if="hasDigital">
        <div class="panel">
          <div class="panel-head">
            <div class="panel-title"><div class="ph-ic"><v-icon size="20" color="#14663a">mdi-bullhorn</v-icon></div>
              <div><div class="pt-main">Reach &amp; Audience · {{ digitalLive.reach.fy }}</div><div class="pt-sub">Total monthly reach + follower base</div></div></div>
            <span class="fr-live"><span class="fr-dot"></span> live</span>
          </div>
          <div class="fr-hero"><div class="fr-big">{{ num(digitalLive.reach.totalReach) }}</div><div class="fr-cap">total monthly reach</div></div>
          <apexchart v-if="digitalLive.reach.monthly.length" type="area" height="150" :options="reachTrendChart.options" :series="reachTrendChart.series" />
          <div class="fr-platforms">
            <div class="fr-pf" v-for="f in digitalLive.reach.followers" :key="f.name"><span class="fr-pf-n">{{ f.name }}</span><span class="fr-pf-v">{{ compactNum(f.value) }}</span></div>
          </div>
          <div class="dg-flag" v-if="digitalLive.reach.websiteVisitors && digitalLive.reach.websiteVisitorsFirst > digitalLive.reach.websiteVisitors">
            <v-icon size="15" color="#c62828">mdi-trending-down</v-icon> Website visitors falling — {{ num(digitalLive.reach.websiteVisitors) }}/mo (was {{ num(digitalLive.reach.websiteVisitorsFirst) }})
          </div>
          <div class="dg-flag ok" v-else-if="digitalLive.reach.websiteVisitors">
            <v-icon size="15" color="#2e7d32">mdi-web</v-icon> Website visitors {{ num(digitalLive.reach.websiteVisitors) }}/mo
          </div>
        </div>
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-magnify-scan</v-icon> SEO Scorecard <span class="hint">· {{ digitalLive.seo.fyLabel }}</span></div>
          <div class="seo-score"><div class="seo-score-v">{{ digitalLive.seo.score }}%</div><div class="seo-score-l">overall SEO score</div></div>
          <div class="seo-grid">
            <div class="seo-tile"><div class="seo-tv">{{ compactNum(digitalLive.seo.clicks) }}</div><div class="seo-tl">Clicks</div></div>
            <div class="seo-tile"><div class="seo-tv">{{ compactNum(digitalLive.seo.impressions) }}</div><div class="seo-tl">Impressions</div></div>
            <div class="seo-tile"><div class="seo-tv">{{ digitalLive.seo.ctr }}%</div><div class="seo-tl">Avg CTR</div></div>
            <div class="seo-tile"><div class="seo-tv">{{ digitalLive.seo.avgPosition }}</div><div class="seo-tl">Avg position</div></div>
          </div>
          <div class="dg-note" v-if="digitalLive.seo.brand"><v-icon size="13" color="#a7b3ac">mdi-information-outline</v-icon> Brand visibility <b>{{ digitalLive.seo.brand }}</b> · discovery leans brand-heavy</div>
        </div>
      </section>
      <section class="grid grid-2" v-if="hasDigital">
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-post-outline</v-icon> Content Output · {{ digitalLive.content.fy }} <span class="hint">· {{ num(digitalLive.content.ytd) }} updates YTD</span></div>
          <div class="hbars big" v-if="contentBars.length">
            <div class="hbar-row" v-for="b in contentBars" :key="b.name">
              <span class="hbar-lbl">{{ b.name }}</span>
              <span class="hbar-track"><span class="hbar-fill" :style="{ width: b.pct + '%', background: b.color }"></span></span>
              <span class="hbar-num">{{ b.count }}</span>
            </div>
          </div>
          <div v-else class="empty">No content data.</div>
        </div>
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-target-account</v-icon> Ads &amp; Media Hits</div>
          <div class="dg-tiles">
            <div class="dg-tile"><div class="dg-tv">{{ num(digitalLive.ads.reach) }}</div><div class="dg-tl">Ad reach · {{ digitalLive.ads.campaigns }} campaigns</div></div>
            <div class="dg-tile"><div class="dg-tv">{{ num(digitalLive.media.ytd) }}</div><div class="dg-tl">Media hits · {{ digitalLive.media.fy }}</div></div>
          </div>
          <apexchart v-if="(digitalLive.media.byYear || []).length" type="bar" height="170" :options="mediaYearChart.options" :series="mediaYearChart.series" />
          <div class="fr-src"><v-icon size="12" color="#a7b3ac">mdi-update</v-icon> updated {{ formatDateTime(digitalLive.fetchedAt) }}</div>
        </div>
      </section>

      <!-- ═══ EMPLOYEE SPOTLIGHT ════════════════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-account-search</v-icon> Employee Spotlight</div>
      <section class="panel">
        <div class="spotlight-search">
          <v-autocomplete v-model="selectedEmployeeId" :items="employees" item-text="text" item-value="value"
            label="Search an employee by name or ID" prepend-inner-icon="mdi-magnify" outlined dense hide-details
            clearable color="#14663a" @change="onSelectEmployee" @click:clear="empSummary = null" />
        </div>

        <div v-if="empLoading" class="spot-loading"><v-progress-circular indeterminate color="#14663a" size="34" width="3" /></div>

        <div v-else-if="empSummary" class="spot-body">
          <div class="spot-head">
            <div class="spot-avatar"><v-icon color="#fff" size="26">mdi-account</v-icon></div>
            <div class="spot-id">
              <div class="spot-name">
                {{ empSummary.employee.name }}
                <span v-if="sc && sc.onProbation" class="probation-pill">On probation</span>
              </div>
              <div class="spot-meta">{{ empSummary.employee.position || '—' }} · ID {{ empSummary.employee.employeeId || '—' }} · {{ empSummary.employee.status }}</div>
              <div class="spot-meta2" v-if="sc">
                <span v-if="sc.division"><v-icon size="13" color="#14663a">mdi-sitemap-outline</v-icon> {{ sc.division }}</span>
                <span v-if="sc.manager"><v-icon size="13" color="#64748b">mdi-account-tie-outline</v-icon> Reports to {{ sc.manager }}</span>
                <span v-if="sc.tenureYears != null"><v-icon size="13" color="#64748b">mdi-calendar-clock</v-icon> {{ sc.tenureYears }} yr tenure</span>
              </div>
            </div>
            <div class="score-hero" v-if="sc">
              <div class="sh-ring" :style="{ background: 'conic-gradient(' + scoreColor(sc.score) + ' ' + (sc.score * 3.6) + 'deg, #eef2f0 0deg)' }">
                <div class="sh-inner"><div class="sh-num" :style="{ color: scoreColor(sc.score) }">{{ sc.score }}</div><div class="sh-den">/100</div></div>
              </div>
              <div class="sh-right">
                <span class="sh-band" :style="{ background: scoreColor(sc.score) }">{{ sc.band }}</span>
                <div class="sh-rank">Rank <b>#{{ sc.rank }}</b> <span>of {{ sc.total }}</span></div>
                <div class="sh-top">Top {{ topPct(sc.rank, sc.total) }}% · effectiveness</div>
              </div>
            </div>
          </div>

          <!-- Headline supporting metrics -->
          <div class="spot-metrics-row">
            <div class="spot-metric metric-hero">
              <div class="sm-val">{{ empSummary.targets.metPercent }}%</div>
              <div class="sm-lbl">Targets Met · FY</div>
            </div>
            <div class="spot-metric">
              <div class="sm-val att">{{ empSummary.attendance.attendancePercent }}%</div>
              <div class="sm-lbl">Attendance · FY</div>
            </div>
            <div class="spot-metric" v-if="sc && sc.pendingApproval">
              <div class="sm-val" style="color:#e08600">{{ sc.pendingApproval }}</div>
              <div class="sm-lbl">Awaiting approval{{ sc.oldestPendingDays ? ' · ' + sc.oldestPendingDays + 'd' : '' }}</div>
            </div>
            <div class="spot-metric" v-if="sc && sc.overdue">
              <div class="sm-val" style="color:#c62828">{{ sc.overdue }}</div>
              <div class="sm-lbl">Overdue targets</div>
            </div>
          </div>

          <!-- Executive scorecard: peer comparison + score drivers -->
          <div class="scorecard" v-if="sc">
            <div class="scard-col">
              <div class="scard-title">How they compare</div>
              <div class="cmp-row">
                <span class="cmp-lbl">This employee</span>
                <span class="cmp-track"><span class="cmp-fill" :style="{ width: Math.min(100, sc.score) + '%', background: scoreColor(sc.score) }"></span></span>
                <span class="cmp-val" :style="{ color: scoreColor(sc.score) }">{{ sc.score }}</span>
              </div>
              <div class="cmp-row" v-if="sc.divisionAvg != null">
                <span class="cmp-lbl">{{ sc.division }} avg</span>
                <span class="cmp-track"><span class="cmp-fill muted" :style="{ width: Math.min(100, sc.divisionAvg) + '%' }"></span></span>
                <span class="cmp-val">{{ sc.divisionAvg }}</span>
              </div>
              <div class="cmp-row">
                <span class="cmp-lbl">Organisation avg</span>
                <span class="cmp-track"><span class="cmp-fill muted2" :style="{ width: Math.min(100, sc.orgAvg) + '%' }"></span></span>
                <span class="cmp-val">{{ sc.orgAvg }}</span>
              </div>
              <div class="cmp-foot" v-if="sc.divisionRank">Ranks <b>#{{ sc.divisionRank }}</b> of {{ sc.divisionSize }} in {{ sc.division }}</div>
            </div>
            <div class="scard-col">
              <div class="scard-title">What drives the score</div>
              <div class="drv-row" v-for="c in sc.components" :key="c.key">
                <span class="drv-lbl">{{ c.label }} <i>·{{ c.weight }}%</i></span>
                <span class="drv-track"><span class="drv-fill" v-if="c.value != null" :style="{ width: Math.min(100, c.value) + '%', background: driverColor(c.key) }"></span></span>
                <span class="drv-val" :class="{ na: c.value == null }">{{ c.value != null ? (c.key === 'pmr' ? c.value : c.value + '%') : 'N/A' }}</span>
              </div>
            </div>
          </div>

          <div class="spot-grid">
            <div class="spot-cell"><div class="sc-v" style="color:#33403a">{{ empSummary.targets.total }}</div><div class="sc-l">Targets (FY)</div></div>
            <div class="spot-cell"><div class="sc-v" style="color:#2e7d32">{{ empSummary.targets.approved }}</div><div class="sc-l">Approved</div></div>
            <div class="spot-cell"><div class="sc-v" style="color:#e08600">{{ empSummary.targets.pendingApproval }}</div><div class="sc-l">Pending</div></div>
            <div class="spot-cell"><div class="sc-v" style="color:#2e7d32">{{ empSummary.attendance.presentDays }}</div><div class="sc-l">Present days</div></div>
            <div class="spot-cell"><div class="sc-v" style="color:#8e24aa">{{ empSummary.attendance.weekendDaysWorked }}</div><div class="sc-l">Weekends worked</div></div>
            <div class="spot-cell"><div class="sc-v" style="color:#1565c0">{{ empSummary.attendance.leaveDays }}</div><div class="sc-l">Leave days</div></div>
            <div class="spot-cell"><div class="sc-v" style="color:#c62828">{{ empSummary.attendance.absentDays }}</div><div class="sc-l">Absent days</div></div>
          </div>
          <div class="spot-outcome" v-if="empSummary.targets.outcome && empSummary.targets.total">
            <div class="so-top">
              <span class="tag-label">Target outcomes · {{ empSummary.targets.outcome.overallMetPct }}% met</span>
              <button class="so-link" @click="viewEmployeeTargets(empSummary.employee.employeeId)">View targets <v-icon size="14">mdi-arrow-right</v-icon></button>
            </div>
            <div class="so-bar">
              <div class="soseg" :style="{ width: empSummary.targets.outcome.metOnTimePct + '%', background: '#2e7d32' }"></div>
              <div class="soseg" :style="{ width: empSummary.targets.outcome.delayedMetPct + '%', background: '#e08600' }"></div>
              <div class="soseg" :style="{ width: empSummary.targets.outcome.notMetPct + '%', background: '#c62828' }"></div>
              <div class="soseg" :style="{ width: empSummary.targets.outcome.pendingNotDuePct + '%', background: '#94a3b8' }"></div>
            </div>
            <div class="so-chips">
              <span class="stag t-green">Met on time <b>{{ empSummary.targets.outcome.metOnTime }}</b></span>
              <span class="stag t-amber">Delayed Met <b>{{ empSummary.targets.outcome.delayedMet }}</b></span>
              <span class="stag t-red">Not Met <b>{{ empSummary.targets.outcome.notMet }}</b></span>
              <span class="stag t-grey">Pending — not due <b>{{ empSummary.targets.outcome.pendingNotDue }}</b></span>
            </div>
          </div>
          <div class="spot-tags" v-if="empSummary.leave.byCategory.length">
            <span class="tag-label">Leave balance</span>
            <span class="stag" v-for="c in empSummary.leave.byCategory" :key="c.category">{{ c.category }} <b>{{ c.balance }}</b>/{{ c.total }}</span>
          </div>
        </div>

        <div v-else class="spot-empty">
          <v-icon size="30" color="#c9d6ce">mdi-account-search-outline</v-icon>
          <div>Select an employee to see their attendance %, leave balance and target status.</div>
        </div>
      </section>

      <!-- ═══ WORKFORCE KPIs ════════════════════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-account-group</v-icon> Workforce &amp; Attendance <span class="section-scope">current staff · attendance today · exits FY to date</span></div>
      <section class="stat-grid">
        <div v-for="k in kpis" :key="k.label" class="stat-card clickable" @click="openDetail(k.type, k.label)">
          <div class="stat-head">
            <span class="stat-label">{{ k.label }}</span>
            <v-icon class="stat-ic" :color="k.color" size="24">{{ k.icon }}</v-icon>
          </div>
          <div class="stat-num">{{ k.value }}</div>
          <div class="stat-foot" :style="{ color: k.color }">{{ k.caption }} <v-icon size="13" :color="k.color">mdi-chevron-right</v-icon></div>
        </div>
      </section>

      <!-- ═══ ANALYTICS (graphs) ════════════════════════════════════════ -->
      <section class="grid grid-2-1">
        <div class="panel">
          <div class="panel-head">
            <div class="panel-title">
              <div class="ph-ic"><v-icon size="20" color="#14663a">mdi-chart-bar</v-icon></div>
              <div><div class="pt-main">Monthly Attendance</div><div class="pt-sub">Month-wise attendance marked</div></div>
            </div>
            <span class="year-chip">{{ data.attendanceTrend.year }}</span>
          </div>
          <apexchart v-if="hasTrend" type="bar" height="290" :options="attendanceBar.options" :series="attendanceBar.series" />
          <div v-else class="empty">No attendance data.</div>
        </div>
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-account-clock</v-icon> Attendance Today</div>
          <div class="hbars big" v-if="data.attendance.totalEmployees">
            <button type="button" class="hbar-row attendance-bar" v-for="b in attendanceBars" :key="b.label" @click="openDetail(b.type, b.label + ' today')">
              <span class="hbar-lbl">{{ b.label }}</span>
              <span class="hbar-track"><span class="hbar-fill" :style="{ width: barPct(b.value, attendanceBars) + '%', background: b.color }"></span></span>
              <span class="hbar-num">{{ b.value }} <em>· {{ b.pct }}%</em></span>
            </button>
          </div>
          <div v-else class="empty">No attendance data.</div>
        </div>
      </section>

      <!-- ═══ DAILY ATTENDANCE (filter by month) ════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-chart-timeline-variant</v-icon> Daily Attendance</div>
      <section class="panel">
        <div class="panel-head">
          <div class="panel-title">
            <div class="ph-ic"><v-icon size="20" color="#14663a">mdi-calendar-check-outline</v-icon></div>
            <div><div class="pt-main">Daily Attendance %</div><div class="pt-sub">Present staff of {{ daily.headcount }} active · {{ daily.monthLabel }}</div></div>
          </div>
          <div class="ph-actions">
            <span class="year-chip">Avg {{ daily.avgWorkingDayPercent }}% · working days</span>
            <select class="month-select" v-model="dailyMonth" @change="fetchDaily(dailyMonth)">
              <option v-for="o in daily.options" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
        </div>
        <apexchart v-if="(daily.days || []).length" type="bar" height="300" :options="dailyChart.options" :series="dailyChart.series" />
        <div v-else class="empty">No attendance data for this month.</div>
        <div class="daily-legend">
          <span><i style="background:#14663a"></i> Working day</span>
          <span><i style="background:#cbd5d0"></i> Weekend / holiday</span>
          <span class="dl-note">Future days are not shown</span>
        </div>
      </section>

      <section class="grid grid-3">
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-gender-male-female</v-icon> Gender Distribution</div>
          <div class="hbars big" v-if="data.headcount.gender.length">
            <div class="hbar-row" v-for="b in genderBars" :key="b.label">
              <span class="hbar-lbl">{{ b.label }}</span>
              <span class="hbar-track"><span class="hbar-fill" :style="{ width: barPct(b.value, genderBars) + '%', background: b.color }"></span></span>
              <span class="hbar-num">{{ b.value }} <em>· {{ b.pct }}%</em></span>
            </div>
          </div>
          <div v-else class="empty">No gender data.</div>
        </div>
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-chart-areaspline</v-icon> Joining Trend (12 mo)</div>
          <apexchart v-if="data.headcount.growth.length" type="area" height="230"
            :options="growthChart.options" :series="growthChart.series" />
          <div v-else class="empty">No joining data.</div>
        </div>
        <div class="panel">
          <div class="panel-title mb"><v-icon size="18" color="#1565c0">mdi-target</v-icon> Assigned Targets by Status</div>
          <div class="big-metric">{{ formatMoney(data.targets.assignedTotal) }}</div>
          <div class="metric-sub">{{ data.targets.projectCount }} projects</div>
          <table class="mini-table mt">
            <thead><tr><th>Status</th><th class="r">Projects</th><th class="r">Assigned</th></tr></thead>
            <tbody>
              <tr v-for="row in data.targets.byStatus" :key="row.status">
                <td>{{ row.status }}</td><td class="r">{{ row.count }}</td><td class="r">{{ formatMoney(row.assigned) }}</td>
              </tr>
              <tr v-if="!data.targets.byStatus.length"><td colspan="3" class="empty small">No targets.</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ═══ ATTRITION + FINANCE ═══════════════════════════════════════ -->
      <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-finance</v-icon> Attrition &amp; Finance <span class="section-scope">attrition FY to date · loan balances current</span></div>
      <section class="grid grid-2">
        <div class="panel">
          <div class="panel-title mb spread">
            <span><v-icon size="18" color="#c62828">mdi-account-arrow-right</v-icon> Recent Exits</span>
            <span class="pill red">{{ data.attrition.exitsThisYear }} this FY</span>
          </div>
          <ul class="list">
            <li v-for="(ex, i) in data.attrition.recent" :key="i">
              <div class="li-main">{{ ex.name || 'Unknown' }}</div>
              <div class="li-sub">{{ ex.reason || '—' }} · {{ ex.date ? formatDate(ex.date) : '' }}</div>
            </li>
            <li v-if="!data.attrition.recent.length" class="empty small">No exits this year.</li>
          </ul>
        </div>
        <div class="panel">
          <div class="panel-title mb spread">
            <span><v-icon size="18" color="#e08600">mdi-cash-multiple</v-icon> Loans / Advances</span>
            <span class="pill amber">{{ data.finance.pendingRequests }} pending</span>
          </div>
          <table class="mini-table">
            <thead><tr><th>Status</th><th class="r">Count</th><th class="r">Amount</th></tr></thead>
            <tbody>
              <tr v-for="row in data.finance.loansByStatus" :key="row.status">
                <td>{{ row.status }}</td><td class="r">{{ row.count }}</td><td class="r">{{ formatMoney(row.totalAmount) }}</td>
              </tr>
              <tr v-if="!data.finance.loansByStatus.length"><td colspan="3" class="empty small">No loan records.</td></tr>
            </tbody>
          </table>
          <div class="foot-note">Released: <strong>{{ formatMoney(data.finance.totalReleased) }}</strong></div>
        </div>
      </section>
      </div>
    </main>

    <v-dialog v-model="directoryDialog" max-width="1200" scrollable>
      <ceo-directory v-if="directoryDialog" @close="directoryDialog = false" @changed="refreshCounts" />
    </v-dialog>
    <!-- ── Drill-down dialog (opens when a KPI card is clicked) ─────────── -->
    <v-dialog v-model="detailDialog" :max-width="isAttendanceDetail ? 1100 : 560" scrollable>
      <div class="dlg">
        <div class="dlg-head">
          <span>{{ detailTitle }} <span class="dlg-count" v-if="!detailLoading">· {{ detailRows.length }}</span></span>
          <v-icon color="#8a978f" @click="detailDialog = false" style="cursor:pointer">mdi-close</v-icon>
        </div>
        <div v-if="detailLoading" class="dlg-loading"><v-progress-circular indeterminate color="#14663a" size="30" width="3" /></div>
        <div v-else-if="isAttendanceDetail" class="dlg-body attendance-details">
          <div class="attendance-tools">
            <label>Search employee<input v-model="attendanceSearch" type="search" placeholder="Name or employee ID" /></label>
            <label>GPS status<select v-model="attendanceGpsFilter"><option value="all">All records</option><option value="flagged">Fake GPS flagged</option><option value="unflagged">Not flagged</option><option value="unknown">Not recorded</option></select></label>
            <label>Sort by<select v-model="attendanceSort"><option value="name">Employee name</option><option value="time">Earliest punch-in</option><option value="flagged">Flagged first</option></select></label>
          </div>
          <p class="attendance-note">Today's punch-ins · times in IST. “Flagged” means the device reported a mock location. “Not flagged” does not confirm genuine GPS.</p>
          <div v-if="detailError" class="banner error" role="alert">{{ detailError }} <button class="link-chip" @click="openDetail(detailType, detailTitle)">Retry</button></div>
          <table v-else class="dlg-table attendance-table">
            <thead><tr><th>Employee</th><th>Punch-in (IST)</th><th>Punch-in location</th><th>Fake GPS</th><th>Recorded via</th></tr></thead>
            <tbody>
              <tr v-for="r in attendanceDetailRows" :key="r.key">
                <td><strong>{{ r.name || '—' }}</strong><div class="attendance-meta">{{ r.employeeId || '—' }}</div></td>
                <td>{{ r.punch ? attendanceTime(r.punch.time) : 'No punch-in recorded' }}<div v-if="r.punch && r.punch.late" class="attendance-late">Late arrival {{ r.punch.lateArrivalBy }}</div><div v-if="!r.punch && r.auto" class="attendance-meta">Auto-attendance enabled</div></td>
                <td><template v-if="r.punch"><div>{{ r.punch.address || 'Address not recorded' }}</div><a v-if="r.punch.location" :href="attendanceMapUrl(r.punch.location)" target="_blank" rel="noopener noreferrer">{{ r.punch.location.latitude }}, {{ r.punch.location.longitude }} · View map</a><div v-else class="attendance-meta">Coordinates not recorded</div></template><span v-else>Not recorded</span></td>
                <td><span class="gps-badge" :class="r.punch && r.punch.mockLocation === true ? 'flagged' : 'neutral'">{{ !r.punch || r.punch.mockLocation == null ? 'Not recorded' : r.punch.mockLocation ? 'Flagged' : 'Not flagged' }}</span></td>
                <td>{{ r.punch ? r.punch.source : '—' }}<div v-if="r.punch && r.punch.offline" class="attendance-meta">Offline submission</div></td>
              </tr>
              <tr v-if="!attendanceDetailRows.length"><td colspan="5" class="dlg-empty">{{ detailRows.length ? 'No records match these filters.' : 'No attendance records for this group today.' }}</td></tr>
            </tbody>
          </table>
        </div>
        <div v-else class="dlg-body">
          <table class="dlg-table">
            <thead><tr><th>#</th><th>Name</th><th>Emp ID</th><th>Detail</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(r, i) in detailRows" :key="i">
                <td class="dim">{{ i + 1 }}</td><td>{{ r.name || '—' }}</td><td>{{ r.employeeId || '—' }}</td><td class="dim">{{ r.extra || '' }}</td>
                <td class="dlg-act">
                  <template v-if="r.id && detailType === 'testaccounts'">
                    <button class="mk-btn restore" @click="markTestAccount(r, false)" title="Restore to a real account (count again)"><v-icon size="14" color="#2e7d32">mdi-account-check-outline</v-icon> Restore</button>
                  </template>
                  <template v-else-if="r.id && detailType === 'autoattendance'">
                    <button class="mk-btn restore" @click="markAutoAttendance(r, false)" title="Remove auto-attendance"><v-icon size="14" color="#c0562c">mdi-close</v-icon> Remove</button>
                  </template>
                  <template v-else-if="r.id && detailType === 'targetexempt'">
                    <button class="mk-btn restore" @click="markTargetExempt(r, false)" title="Include again — expect a target sheet"><v-icon size="14" color="#2e7d32">mdi-account-check-outline</v-icon> Include</button>
                  </template>
                  <template v-else-if="r.id">
                    <button v-if="detailType === 'notargetsheet'" class="mk-btn exempt" @click="markTargetExempt(r, true)" title="Not expected to have a target sheet — remove from this gap"><v-icon size="14" color="#7a4a08">mdi-file-remove-outline</v-icon> No sheet needed</button>
                    <button class="mk-btn auto" :class="{ on: r.auto }" @click="markAutoAttendance(r, !r.auto)" :title="r.auto ? 'Auto-attendance ON — click to turn off' : 'Mark as auto-attendance senior (always present)'"><v-icon size="14" :color="r.auto ? '#2e7d32' : '#1565c0'">{{ r.auto ? 'mdi-account-clock' : 'mdi-account-clock-outline' }}</v-icon> {{ r.auto ? 'Auto ✓' : 'Auto' }}</button>
                    <button class="mk-btn flag" @click="markTestAccount(r, true)" title="Mark as test/system — exclude from all stats"><v-icon size="14" color="#c0562c">mdi-flag-outline</v-icon> Test</button>
                  </template>
                </td>
              </tr>
              <tr v-if="!detailRows.length"><td colspan="5" class="dlg-empty">No records.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import CeoDirectory from './CeoDirectory.vue';


const GREEN = "#14663a";

// ── Shared chart theme (keeps every graph visually consistent) ──────────────
const CHART_FONT = 'Poppins, "Segoe UI", sans-serif';
const CHART_FORE = "#5a6b62";
const CHART_GRID = "#eef2f0";
const CHART_LEGEND = {
  position: "bottom", fontSize: "12px", fontFamily: CHART_FONT,
  labels: { colors: CHART_FORE }, markers: { width: 9, height: 9, radius: 4 },
  itemMargin: { horizontal: 8, vertical: 3 },
};
function donutOptions(labels, colors, opts) {
  opts = opts || {};
  return {
    labels, colors,
    chart: { fontFamily: CHART_FONT, foreColor: CHART_FORE, animations: { speed: 400 } },
    legend: CHART_LEGEND,
    stroke: { width: 2, colors: ["#fff"] },
    dataLabels: {
      enabled: true, dropShadow: { enabled: false },
      style: { fontSize: "11px", fontFamily: CHART_FONT, fontWeight: 600, colors: ["#fff"] },
      formatter: (val) => Math.round(val) + "%",
    },
    plotOptions: { pie: { donut: Object.assign({ size: "72%" }, opts.donut || {}) } },
  };
}
function cartesianBase() {
  return {
    chart: {
      fontFamily: CHART_FONT, foreColor: CHART_FORE, toolbar: { show: false }, zoom: { enabled: false },
      animations: { enabled: true, easing: "easeinout", speed: 650, animateGradually: { enabled: true, delay: 80 }, dynamicAnimation: { enabled: true, speed: 350 } },
    },
    grid: { borderColor: CHART_GRID, strokeDashArray: 4, padding: { left: 8, right: 12, top: 0 },
      xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
    tooltip: { theme: "light", style: { fontSize: "12px", fontFamily: CHART_FONT } },
  };
}

function emptyData() {
  return {
    attendance: { totalEmployees: 0, presentCount: 0, leaveCount: 0, absentCount: 0, lateArrivals: 0 },
    headcount: { total: 0, confirmed: 0, onProbation: 0, gender: [], growth: [] },
    targets: { assignedTotal: 0, projectCount: 0, orgTarget: 0, raised: 0, committed: 0, achievementPercent: 0, byStatus: [] },
    performance: { total: 0, notSubmitted: 0, pendingApproval: 0, approved: 0, rejected: 0, achievement: { met: 0, delayedMet: 0, notMet: 0 }, outcome: { metOnTime: 0, delayedMet: 0, notMet: 0, pendingNotDue: 0, metOnTimePct: 0, delayedMetPct: 0, notMetPct: 0, pendingNotDuePct: 0, overallMetPct: 0 }, currentMonth: { label: "", due: 0, pending: 0, met: 0, overdue: 0 }, stages: [] },
    targetTrend: { year: "", months: [] },
    resignedExcluded: 0,
    attendanceTrend: { year: new Date().getFullYear(), trend: [] },
    attrition: { exitsThisYear: 0, recent: [] },
    finance: { loansByStatus: [], totalReleased: 0, pendingRequests: 0 },
    donations: { indianAmount: 0, indianCount: 0, foreignAmount: 0, foreignCount: 0, successCount: 0 },
    projectGovernance: { totalProjects: 0, totalDivisions: 0, totalEmployees: 0, projectsWithPi: 0, projectsMissingPi: 0, piCoverageRate: 0, staffedProjects: 0, unstaffedProjects: 0, projectStaffCoverageRate: 0, employeesWithProjects: 0, employeesWithoutProjects: 0, employeeProjectCoverageRate: 0, projectsMissingDivision: 0, activeAssignments: 0, attentionProjects: [] },
  };
}

export default {
  name: "CeoDashboard",
  components: { CeoDirectory },
  data() {
    return {
      loading: true, errorMsg: "", generatedAt: null, financialYear: "", boardMode: false, alertsExpanded: false,
      directoryDialog: false,
      data: emptyData(), wtiLogo: require("@/assets/images/wtilogo.png"),
      // Drill-down dialog
      detailDialog: false, detailLoading: false, detailTitle: "", detailType: "", detailRows: [],
      detailError: "", detailRequestId: 0,
      attendanceSearch: "", attendanceGpsFilter: "all", attendanceSort: "name",
      hiddenTestCount: 0, autoCount: 0, exemptCount: 0,
      rosterLoading: false,
      retention: null,
      // Division league table (all active staff — from /dashboard/ceo/divisions)
      divisionStats: [],
      divSortKey: "avgAttendance", divSortDir: -1,
      divCols: [
        { key: "staff", label: "Staff", kind: "neutral", hint: "Active staff in division (all, not just target-holders)" },
        { key: "avgAttendance", label: "Attendance", kind: "good", suffix: "%", hint: "Avg working-day attendance across all staff" },
        { key: "targets", label: "Targets", kind: "neutral", hint: "KPI targets set in division" },
        { key: "submissionRate", label: "Submission", kind: "good", suffix: "%", hint: "% of targets submitted for approval" },
        { key: "avgAchievement", label: "Met %", kind: "good", suffix: "%", hint: "Targets met + delayed-met" },
        { key: "notMet", label: "Not Met", kind: "bad", hint: "Overdue targets (past due, not met)" },
        { key: "exits", label: "Exits", kind: "bad", hint: "Staff who left this FY" },
      ],
      // Employee spotlight
      employees: [], selectedEmployeeId: null, empSummary: null, empLoading: false,
      // Daily attendance chart (filterable by month)
      daily: { month: "", monthLabel: "", headcount: 0, avgWorkingDayPercent: 0, days: [], options: [] },
      dailyMonth: null, dailyLoading: false,
      // Executive intelligence (analytics feed + prior-month attendance for deltas)
      insights: null, attnPrevAvg: null,
      // Live fundraising/donations from the org Google Sheet
      fundraisingLive: null,
      // Live digital / outreach metrics from the same sheet
      digitalLive: null,
      // Pulse monthly playback
      pulseTimeline: [], pulseIdx: -1, pulsePlaying: false, pulseTimer: null,
      // Staff with no FY target sheet created
      noSheetCount: 0,
      // External Media News
      mediaItems: [],
      mediaStats: { total: 0, positive: 0, negative: 0, neutral: 0, positivePct: 0 },
      mediaImpact: {},
      mediaLoading: false,
      mediaScraping: false,
      pip: { financialYear: '', summary: {}, divisions: [], dataStatus: {}, calculationScope: {} },
    };
  },
  computed: {
    governance() { return this.data.projectGovernance || emptyData().projectGovernance; },
    dashboardDateLabel() { return this.formatDate(this.generatedAt || new Date()); },
    dashboardAsOfLabel() { return this.formatDateTime(this.generatedAt || new Date()); },
    fyScopeLabel() {
      const bounds = this.fyBounds;
      const generated = this.generatedAt ? new Date(this.generatedAt).getTime() : Date.now();
      const through = Math.min(bounds.end, Math.max(bounds.start, generated));
      return this.formatDate(bounds.start) + ' – ' + this.formatDate(through);
    },
    currentMonthScopeLabel() {
      return this.cm.label || new Date(this.generatedAt || Date.now()).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    },
    pipScopeLabel() {
      const scope = this.pip.calculationScope || {};
      const period = scope.periodLabel || 'Current period';
      return scope.asOfDate ? period + ' · through ' + this.formatDate(scope.asOfDate) : period;
    },
    governanceStatus() {
      const g = this.governance;
      const critical = Number(g.projectsMissingDivision || 0);
      const needsReview = Number(g.projectsMissingPi || 0) + Number(g.unstaffedProjects || 0) + Number(g.employeesWithoutProjects || 0);
      if (critical) return { label: critical + ' project' + (critical === 1 ? '' : 's') + ' missing a division', tone: 'bad' };
      if (needsReview) return { label: needsReview + ' governance item' + (needsReview === 1 ? '' : 's') + ' to review', tone: 'warn' };
      return { label: 'Ownership and staffing complete', tone: 'good' };
    },
    pipDueDeliveryRate() { return Number(this.pip.summary.dueDeliveryRate || 0); },
    visibleAlerts() { return this.alertsExpanded ? this.alerts : this.alerts.slice(0, 5); },
    boardActions() {
      const ownerByIcon = {
        'mdi-clipboard-alert-outline': 'Division heads · PIP team',
        'mdi-account-question-outline': 'PIP coordinators',
        'mdi-account-star-outline': 'CEO · Division heads',
        'mdi-source-branch': 'CEO · Division heads',
        'mdi-account-multiple-outline': 'Division heads · HR',
        'mdi-alert-octagon-outline': 'Reporting managers',
        'mdi-timer-sand': 'Reporting managers',
        'mdi-file-alert-outline': 'Division heads',
        'mdi-file-remove-outline': 'HR · Division heads',
        'mdi-folder-alert-outline': 'Division heads',
        'mdi-account-clock-outline': 'HR',
        'mdi-account-arrow-right-outline': 'HR',
        'mdi-trending-down': 'Communications',
        'mdi-magnify-scan': 'Communications',
      };
      return this.alerts.slice(0, 3).map((item) => ({
        ...item,
        owner: ownerByIcon[item.icon] || 'Executive team',
        timing: item.sev >= 3 ? 'Immediate' : item.sev === 2 ? 'This month' : 'Monitor',
      }));
    },
    boardFundraisingValue() {
      return this.hasFundraisingLive ? this.formatMoney(this.fundraisingLive.online.ytd) : this.formatMoney(this.data.donations.indianAmount);
    },
    boardFundingGap() {
      return Math.max(0, Number(this.data.targets.orgTarget || 0) - Number(this.data.targets.raised || 0));
    },
    boardFundingHeadline() {
      return this.data.targets.orgTarget ? Number(this.data.targets.achievementPercent || 0) + '% of plan' : this.boardFundraisingValue;
    },
    boardFundingNarrative() {
      return this.data.targets.orgTarget
        ? this.formatMoney(this.data.targets.raised) + ' recorded against the organisation fundraising target.'
        : (this.hasFundraisingLive ? 'Raised online in the current financial year.' : 'Successful Indian donations recorded through the WTI portal.');
    },
    boardDivisionRows() {
      return this.insights ? (this.insights.divisions || []).filter((division) => division.staff > 0) : [];
    },
    boardDivisionLeaders() {
      return this.boardDivisionRows.filter((division) => String(division.division || '').toLowerCase() !== 'unassigned').slice(0, 3);
    },
    boardDivisionWatch() {
      const leaderNames = new Set(this.boardDivisionLeaders.map((division) => division.division));
      return this.boardDivisionRows.slice().reverse().filter((division) => !leaderNames.has(division.division)).slice(0, 3);
    },
    boardSources() {
      const pipStatus = this.pip.dataStatus || {};
      const pipFailed = Number(pipStatus.failedSources || 0);
      return [
        { label: 'Core operations', state: this.generatedAt ? 'ready' : 'missing', detail: this.generatedAt ? 'current as of ' + this.formatDateTime(this.generatedAt) : 'unavailable' },
        { label: 'PIP', state: pipFailed ? 'warning' : (this.pipReady ? 'ready' : 'missing'), detail: pipFailed ? pipFailed + ' workbook sync failure' + (pipFailed === 1 ? '' : 's') : (pipStatus.lastCheckedAt ? 'checked ' + this.formatDateTime(pipStatus.lastCheckedAt) : (this.pipReady ? 'loaded' : 'unavailable')) },
        { label: 'Project governance', state: this.governance.projectsMissingPi || this.governance.projectsMissingDivision ? 'warning' : (this.governance.totalProjects ? 'ready' : 'missing'), detail: this.governance.totalProjects ? this.governance.piCoverageRate + '% PI coverage · ' + this.governance.unstaffedProjects + ' unstaffed' : 'unavailable' },
        { label: 'Fundraising', state: this.hasFundraisingLive ? 'ready' : 'warning', detail: this.hasFundraisingLive ? 'updated ' + this.formatDateTime(this.fundraisingLive.fetchedAt) : 'live sheet unavailable' },
        { label: 'Digital', state: this.hasDigital ? 'ready' : 'warning', detail: this.hasDigital ? 'updated ' + this.formatDateTime(this.digitalLive.fetchedAt) : 'live sheet unavailable' },
        { label: 'Media', state: this.mediaItems.length ? 'ready' : 'warning', detail: this.mediaItems.length ? this.mediaStats.total + ' items in review window' : 'no current items' },
      ];
    },
    boardReadiness() {
      const sources = this.boardSources;
      return { ready: sources.filter((source) => source.state === 'ready').length, total: sources.length };
    },
    boardPulseTrend() {
      const currentMonth = new Date().toLocaleString('en-US', { month: 'short' });
      const completed = this.pulseTimeline.filter((month) => month.month !== currentMonth);
      const latest = completed.length ? completed[completed.length - 1] : null;
      if (!latest || !Number.isFinite(Number(latest.score))) return null;
      const delta = Math.round((this.pulse.score - Number(latest.score)) * 10) / 10;
      const label = latest.month || latest.label || 'latest month';
      return { text: (delta > 0 ? '▲ ' : delta < 0 ? '▼ ' : '→ ') + Math.abs(delta) + ' vs ' + label, tone: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat' };
    },
    boardAttendanceTrend() {
      if (this.attnDelta == null) return null;
      return { text: (this.attnDelta > 0 ? '▲ ' : this.attnDelta < 0 ? '▼ ' : '→ ') + Math.abs(this.attnDelta) + ' pts vs last month', tone: this.attnDelta > 0 ? 'up' : this.attnDelta < 0 ? 'down' : 'flat' };
    },
    boardFundraisingTrend() {
      const monthOrder = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
      const currentMonth = new Date().toLocaleString('en-US', { month: 'short' });
      const currentIndex = monthOrder.indexOf(currentMonth);
      const months = this.hasFundraisingLive
        ? (this.fundraisingLive.online.monthly || []).filter((month) => monthOrder.indexOf(month.month) >= 0 && monthOrder.indexOf(month.month) < currentIndex && Number.isFinite(Number(month.amount)))
        : [];
      if (months.length < 2) return null;
      const current = Number(months[months.length - 1].amount || 0);
      const previous = Number(months[months.length - 2].amount || 0);
      if (previous <= 0) return null;
      const delta = Math.round(((current - previous) / previous) * 1000) / 10;
      return { text: (delta > 0 ? '▲ ' : delta < 0 ? '▼ ' : '→ ') + Math.abs(delta) + '% vs prior completed month', tone: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat' };
    },
    boardKpis() {
      const retentionRate = this.retention ? Math.max(0, Math.round((100 - Number(this.retention.summary.attritionPct || 0)) * 10) / 10) : null;
      const attendance = this.daily.avgWorkingDayPercent || this.data.attendance.presentPercent || 0;
      const fundingAchievement = Number(this.data.targets.achievementPercent || 0);
      const fundingVariance = fundingAchievement - this.pace.elapsedPct;
      return [
        { label: 'Organisational pulse', value: this.pulse.score + '/100', note: this.pulse.status.label + ' · comparable with monthly history', tone: this.pulse.score >= 70 ? 'good' : this.pulse.score >= 45 ? 'warn' : 'bad', trend: this.boardPulseTrend },
        { label: 'Due targets closed', value: this.pace.due ? this.pace.closedPct + '%' : '—', note: this.pace.due ? this.pace.closed + ' of ' + this.pace.due + ' targets already due' : 'No targets are currently due', tone: this.pace.due ? (this.pace.closedPct >= 80 ? 'good' : this.pace.closedPct >= 50 ? 'warn' : 'bad') : 'neutral' },
        { label: 'PIP due delivery', value: this.pip.summary.due ? this.pipDueDeliveryRate + '%' : '—', note: this.pip.summary.due ? this.num(this.pip.summary.due) + ' assessments due · ' + this.pipScopeLabel : 'No PIP assessments are currently due', tone: this.pip.summary.due ? (this.pipDueDeliveryRate >= 80 ? 'good' : this.pipDueDeliveryRate >= 50 ? 'warn' : 'bad') : 'neutral' },
        { label: 'Working-day attendance', value: attendance + '%', note: 'Current FY working-day average', tone: attendance >= 80 ? 'good' : attendance >= 65 ? 'warn' : 'bad', trend: this.boardAttendanceTrend },
        { label: 'Staff retention', value: retentionRate == null ? '—' : retentionRate + '%', note: this.retention ? this.retention.summary.exits + ' exits this FY' : 'Retention data unavailable', tone: retentionRate == null ? 'neutral' : retentionRate >= 90 ? 'good' : retentionRate >= 80 ? 'warn' : 'bad' },
        this.data.targets.orgTarget
          ? { label: 'Fundraising plan achieved', value: fundingAchievement + '%', note: this.formatMoney(this.data.targets.raised) + ' of ' + this.formatMoney(this.data.targets.orgTarget) + ' · ' + this.pace.elapsedPct + '% of FY elapsed', tone: fundingVariance >= -10 ? 'good' : fundingVariance >= -25 ? 'warn' : 'bad', trend: this.boardFundraisingTrend }
          : { label: 'Fundraising YTD', value: this.boardFundraisingValue, note: this.hasFundraisingLive ? 'Online fundraising recorded this FY' : 'WTI portal donations recorded this FY', tone: 'neutral', trend: this.boardFundraisingTrend },
      ];
    },
    boardHighlights() {
      const rows = [];
      if (this.pace.due) rows.push(this.pace.closed + ' of ' + this.pace.due + ' due KPI targets have been completed, giving a ' + this.pace.closedPct + '% due-target closure rate.');
      if (this.rankings && this.rankings.topDivision) rows.push(this.rankings.topDivision.division + ' currently leads division effectiveness at ' + this.rankings.topDivision.score + '.');
      if (this.hasFundraisingLive) rows.push(this.formatMoney(this.fundraisingLive.online.ytd) + ' has been raised online in FY ' + this.fundraisingLive.online.fy + '.');
      else if (this.data.donations.indianAmount) rows.push(this.formatMoney(this.data.donations.indianAmount) + ' in successful Indian donations has been recorded through the portal.');
      if (this.mediaStats.total) rows.push(this.mediaStats.positivePct + '% of reviewed external coverage is positive.');
      if (this.governance.totalProjects && !this.governance.projectsMissingPi) rows.push('All ' + this.governance.totalProjects + ' active projects have an accountable PI assigned.');
      else if (this.governance.totalProjects) rows.push('Project PI coverage is ' + this.governance.piCoverageRate + '%, with ' + this.governance.projectsMissingPi + ' projects still needing an owner.');
      if (!rows.length) rows.push('Executive data is still loading; refresh before presenting this view.');
      return rows.slice(0, 3);
    },
    isAttendanceDetail() { return ["present", "leave", "absent", "late"].includes(this.detailType); },
    attendanceDetailRows() {
      const search = this.attendanceSearch.trim().toLowerCase();
      const rows = [];
      this.detailRows.forEach((employee) => {
        if (search && !(employee.name + " " + employee.employeeId).toLowerCase().includes(search)) return;
        const punches = employee.punchIns && employee.punchIns.length ? employee.punchIns : [null];
        punches.forEach((punch, index) => {
          const flag = punch ? punch.mockLocation : null;
          if (this.attendanceGpsFilter === "flagged" && flag !== true) return;
          if (this.attendanceGpsFilter === "unflagged" && flag !== false) return;
          if (this.attendanceGpsFilter === "unknown" && flag != null) return;
          rows.push({ ...employee, punch, key: employee.id + ":" + index });
        });
      });
      return rows.sort((a, b) => {
        if (this.attendanceSort === "flagged") {
          const flagged = Number(!!(b.punch && b.punch.mockLocation === true)) - Number(!!(a.punch && a.punch.mockLocation === true));
          if (flagged) return flagged;
        }
        if (this.attendanceSort === "time") {
          const timeA = a.punch && a.punch.time ? new Date(a.punch.time).getTime() : Infinity;
          const timeB = b.punch && b.punch.time ? new Date(b.punch.time).getTime() : Infinity;
          if (timeA !== timeB) return timeA - timeB;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
    },
    sc() { return (this.empSummary && this.empSummary.scorecard) || null; },
    cm() { return (this.data.performance && this.data.performance.currentMonth) || { label: "", due: 0, pending: 0, met: 0, overdue: 0 }; },
    // ── Executive intelligence ──────────────────────────────────────────────
    fyBounds() {
      const y = parseInt(String(this.financialYear || "").slice(0, 4), 10) || new Date().getUTCFullYear();
      return { start: Date.UTC(y, 3, 1, 0, 0, 0), end: Date.UTC(y + 1, 2, 31, 23, 59, 59, 999) };
    },
    submissionRate() {
      const p = this.data.performance, total = p.total || 0;
      return total > 0 ? Math.round(((total - p.notSubmitted) / total) * 1000) / 10 : 0;
    },
    pace() {
      const o = this.outcome;
      const closed = (o.metOnTime || 0) + (o.delayedMet || 0);
      const due = closed + (o.notMet || 0);
      const closedPct = due > 0 ? Math.round((closed / due) * 1000) / 10 : 0;
      const b = this.fyBounds, now = Date.now();
      const el = Math.min(1, Math.max(0, (now - b.start) / (b.end - b.start)));
      const elapsedPct = Math.round(el * 1000) / 10;
      const daysLeft = Math.max(0, Math.ceil((b.end - now) / 86400000));
      const verdict = due === 0 ? "No targets due yet" : (closedPct >= 80 ? "On track" : closedPct >= 50 ? "Slightly behind" : "Behind");
      return { closed, due, closedPct, elapsedPct, daysLeft, verdict };
    },
    paceClass() { const v = this.pace.verdict; return v === "On track" ? "on" : v === "Slightly behind" ? "warn" : v === "Behind" ? "bad" : "neutral"; },
    attnDelta() {
      if (this.attnPrevAvg == null) return null;
      return Math.round(((this.daily.avgWorkingDayPercent || 0) - this.attnPrevAvg) * 10) / 10;
    },
    pulse() {
      const attendance = this.daily.avgWorkingDayPercent || this.data.attendance.presentPercent || 0;
      const targets = this.pace.closedPct;
      const adoption = this.submissionRate;
      const exits = this.data.attrition.exitsThisYear || 0, head = this.data.headcount.total || 0;
      const retention = (head + exits) > 0 ? Math.round((1 - exits / (head + exits)) * 1000) / 10 : 100;
      const scoreComponents = [
        { key: "attendance", label: "Attendance", value: attendance, weight: 0.30 },
        { key: "targets", label: "Target progress", value: targets, weight: 0.25 },
        { key: "adoption", label: "KPI adoption", value: adoption, weight: 0.20 },
        { key: "retention", label: "Retention", value: retention, weight: 0.25 },
      ];
      const comps = this.pipReady
        ? scoreComponents.concat([{ key: "pip", label: "PIP overall", value: Number(this.pip.summary.completionRate || 0), weight: 0 }])
        : scoreComponents;
      const score = Math.round(scoreComponents.reduce((s, c) => s + c.value * c.weight, 0) * 10) / 10;
      const status = score >= 70 ? { label: "Healthy", color: "#2e7d32" } : score >= 45 ? { label: "Watch", color: "#e08600" } : { label: "At risk", color: "#c62828" };
      return { score, status, components: comps };
    },
    // What the pulse ring/bars actually show — a played-back month, or "Now".
    pulseView() {
      const band = (s) => s >= 70 ? { label: "Healthy", color: "#2e7d32" } : s >= 45 ? { label: "Watch", color: "#e08600" } : { label: "At risk", color: "#c62828" };
      if (this.pulseIdx >= 0 && this.pulseTimeline[this.pulseIdx]) {
        const t = this.pulseTimeline[this.pulseIdx];
        return {
          score: t.score, status: band(t.score), label: t.label,
          components: [
            { key: "attendance", label: "Attendance", value: t.attendance },
            { key: "targets", label: "Target progress", value: t.targets },
            { key: "adoption", label: "KPI adoption", value: t.adoption },
            { key: "retention", label: "Retention", value: t.retention },
          ],
        };
      }
      return { score: this.pulse.score, status: this.pulse.status, label: "Now", components: this.pulse.components };
    },
    donationProjection() {
      const el = (this.pace.elapsedPct / 100) || 0.0001;
      const d = this.data.donations;
      return { indian: Math.round((d.indianAmount || 0) / el), foreign: Math.round((d.foreignAmount || 0) / el) };
    },
    divisionRows() {
      const divs = (this.divisionStats || []).slice();
      const k = this.divSortKey, dir = this.divSortDir;
      divs.sort((a, b) => k === "division" ? dir * String(a.division).localeCompare(String(b.division)) : dir * ((a[k] || 0) - (b[k] || 0)));
      return divs;
    },
    attritionClass() {
      const v = this.retention ? this.retention.summary.annualizedPct : 0;
      return v >= 20 ? "neg" : v >= 12 ? "warn" : "pos";
    },
    retentionFlowChart() {
      const f = (this.retention && this.retention.monthlyFlow) || [];
      return {
        series: [
          { name: "Joiners", data: f.map((x) => x.joiners) },
          { name: "Exits", data: f.map((x) => x.exits) },
        ],
        options: {
          chart: { type: "bar", toolbar: { show: false }, foreColor: "#8a978f", fontFamily: "Poppins, sans-serif", animations: { speed: 500 } },
          colors: ["#2e7d32", "#c62828"],
          plotOptions: { bar: { borderRadius: 3, columnWidth: "58%" } },
          dataLabels: { enabled: false },
          legend: { position: "top", horizontalAlign: "left", fontSize: "12px", markers: { radius: 3 }, labels: { colors: "#33403a" } },
          grid: { borderColor: "#eef2f0", strokeDashArray: 4 },
          xaxis: { categories: f.map((x) => x.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px" } } },
          yaxis: { labels: { style: { fontSize: "11px" }, formatter: (v) => Math.round(v) } },
          tooltip: { theme: "light", shared: true, intersect: false },
        },
      };
    },
    exitReasonBars() {
      const r = (this.retention && this.retention.byReason) || [];
      const max = Math.max(1, ...r.map((x) => x.count));
      return r.slice(0, 8).map((x) => ({ ...x, pct: Math.max(6, Math.round((x.count / max) * 100)) }));
    },
    tenureBars() {
      const t = (this.retention && this.retention.tenure) || [];
      const max = Math.max(1, ...t.map((x) => x.count));
      return t.map((x) => ({ ...x, pct: Math.max(4, Math.round((x.count / max) * 100)) }));
    },
    divisionAttritionRows() {
      const d = (this.retention && this.retention.byDivision) || [];
      const rows = d.filter((x) => x.exits > 0).slice(0, 8);
      const max = Math.max(1, ...rows.map((x) => x.exits));
      return rows.map((x) => ({ ...x, pct: Math.max(6, Math.round((x.exits / max) * 100)) }));
    },
    rankings() {
      const d = this.insights; if (!d) return null;
      const divs = (d.divisions || []).filter((x) => x.staff > 0);
      const mgrs = (d.managers || []).filter((m) => m.teamSize >= 2).slice().sort((a, b) => b.teamAvgScore - a.teamAvgScore);
      const emps = d.employees || [];
      return {
        topDivision: divs[0] || null, bottomDivision: divs.length > 1 ? divs[divs.length - 1] : null,
        topManager: mgrs[0] || null, bottomManager: mgrs.length > 1 ? mgrs[mgrs.length - 1] : null,
        topEmployees: emps.slice(0, 3), adoption: d.adoption || null,
      };
    },
    alerts() {
      const a = [], p = this.data.performance, o = this.outcome;
      const g = this.governance;
      const missingPi = Number(g.projectsMissingPi || 0);
      const missingDivision = Number(g.projectsMissingDivision || 0);
      const unstaffed = Number(g.unstaffedProjects || 0);
      if (missingPi > 0 || missingDivision > 0 || unstaffed > 0) {
        const issueDetail = [];
        if (missingDivision && (missingPi || unstaffed)) issueDetail.push(missingDivision + ' missing a division');
        if (missingPi && missingDivision) issueDetail.push(missingPi + ' need a PI');
        if (unstaffed && (missingDivision || missingPi)) issueDetail.push(unstaffed + ' have no active staff');
        a.push({
          sev: missingDivision ? 3 : 2,
          icon: missingDivision ? "mdi-source-branch" : (missingPi ? "mdi-account-star-outline" : "mdi-account-multiple-outline"),
          color: missingDivision ? "#c62828" : "#b45309",
          title: missingDivision
            ? missingDivision + " project" + (missingDivision === 1 ? " is" : "s are") + " missing a division"
            : (missingPi
              ? missingPi + " project" + (missingPi === 1 ? "" : "s") + " need a PI"
              : unstaffed + " project" + (unstaffed === 1 ? " has" : "s have") + " no active staff"),
          detail: issueDetail.length ? issueDetail.join(' · ') : 'Review project governance',
          act: () => {
            if (missingDivision) this.directoryDialog = true;
            else if (missingPi) this.goPiManager(true);
            else this.$router.push('/ceo-employee-mapping');
          },
        });
      }
      if (this.pipReady && (this.pip.summary.notMet || 0) > 0) a.push({ sev: 3, icon: "mdi-clipboard-alert-outline", color: "#c62828", title: this.num(this.pip.summary.notMet) + " PIP items not met", detail: (this.pipWatchDivision ? this.pipWatchDivision.name + " currently needs the closest review" : "Review division outcomes"), act: () => this.$router.push({ path: '/ceo-pip', query: { tab: 'notmet' } }) });
      if (this.pipReady && (this.pip.summary.unresolvedLabels || 0) > 0) a.push({ sev: 2, icon: "mdi-account-question-outline", color: "#b45309", title: this.pip.summary.unresolvedLabels + " PIP names need mapping", detail: "Resolve ambiguous point-person labels", act: () => this.$router.push({ path: '/ceo-pip', query: { tab: 'mapping' } }) });
      if ((o.notMet || 0) > 0) a.push({ sev: 3, icon: "mdi-alert-octagon-outline", color: "#c62828", title: o.notMet + " target" + (o.notMet > 1 ? "s" : "") + " overdue", detail: "Past due date and not met", act: () => this.goOutcome("notmet") });
      if ((p.pendingApproval || 0) > 0) {
        const oldest = this.insights ? Math.max(0, ...(this.insights.managers || []).map((m) => m.oldestPendingDays || 0)) : 0;
        a.push({ sev: oldest >= 7 ? 3 : 2, icon: "mdi-timer-sand", color: "#1565c0", title: p.pendingApproval + " approval" + (p.pendingApproval > 1 ? "s" : "") + " awaiting", detail: oldest ? "Oldest pending " + oldest + " days" : "Managers to review", act: () => this.openEmployeeTargets() });
      }
      if ((p.total || 0) > 0 && this.submissionRate < 25) a.push({ sev: 3, icon: "mdi-file-alert-outline", color: "#e08600", title: "Only " + this.submissionRate + "% of targets submitted", detail: this.num(p.notSubmitted) + " KPI updates pending org-wide", act: () => this.openEmployeeTargets() });
      if (this.noSheetCount > 0) a.push({ sev: 3, icon: "mdi-file-remove-outline", color: "#c0562c", title: this.noSheetCount + " staff without a target sheet", detail: "No FY target sheet created yet — click to see who", act: () => this.openDetail("notargetsheet", "Staff without a target sheet") });
      const noSub = this.insights ? (this.insights.divisions || []).filter((x) => x.staff > 0 && x.submissionRate === 0) : [];
      if (noSub.length) a.push({ sev: 2, icon: "mdi-folder-alert-outline", color: "#b45309", title: noSub.length + " division" + (noSub.length > 1 ? "s" : "") + " with no submissions", detail: noSub.slice(0, 3).map((x) => x.division).join(", ") + (noSub.length > 3 ? "…" : ""), act: () => this.openEmployeeTargets() });
      const prob = this.data.headcount.onProbation || 0;
      if (prob > 0) a.push({ sev: 1, icon: "mdi-account-clock-outline", color: "#8e24aa", title: prob + " staff on probation", detail: "Confirmation reviews due", act: () => this.openDetail("probation", "On Probation") });
      if ((this.data.resignedExcluded || 0) > 0) a.push({ sev: 1, icon: "mdi-account-arrow-right-outline", color: "#64748b", title: this.data.resignedExcluded + " staff resigned / left this FY", detail: "Their targets are excluded from totals — review open items", act: () => this.goResigned() });
      if (this.hasDigital) {
        const r = this.digitalLive.reach, seo = this.digitalLive.seo;
        if (r.websiteVisitors && r.websiteVisitorsFirst > r.websiteVisitors * 1.3) a.push({ sev: 2, icon: "mdi-trending-down", color: "#c62828", title: "Website visitors falling", detail: this.num(r.websiteVisitorsFirst) + " → " + this.num(r.websiteVisitors) + "/mo this FY", act: null });
        if (seo.ctr && seo.ctr < 1) a.push({ sev: 1, icon: "mdi-magnify-scan", color: "#e08600", title: "SEO click-through low (" + seo.ctr + "%)", detail: "Discovery leans brand-heavy — capture more non-brand search", act: null });
      }
      return a.sort((x, y) => y.sev - x.sev);
    },
    briefing() {
      const out = [], cm = this.cm, p = this.data.performance, d = this.data.donations, total = p.total || 0;
      if (total) out.push(this.num(total) + " active KPI targets this FY — " + cm.due + " due this month, " + cm.overdue + " overdue, " + cm.met + " met.");
      const att = this.daily.avgWorkingDayPercent;
      if (att) { const dl = this.attnDelta; out.push("Working-day attendance averages " + att + "%" + (dl != null ? (dl >= 0 ? " (▲ " + dl + " vs last month)" : " (▼ " + Math.abs(dl) + " vs last month)") : "") + "."); }
      if (this.hasFundraisingLive) {
        const o = this.fundraisingLive.online;
        out.push(this.formatMoney(o.ytd) + " raised online this FY" + (o.platforms && o.platforms.length ? " (led by " + o.platforms[0].name + ")" : "") + ".");
      } else if ((d.indianAmount || 0) > 0 || (d.foreignAmount || 0) > 0) {
        out.push(this.formatMoney(d.indianAmount) + ((d.foreignAmount || 0) > 0 ? " + " + this.formatUsd(d.foreignAmount) : "") + " raised via the portal — ~" + this.formatMoney(this.donationProjection.indian) + " projected by year-end at current pace.");
      }
      if (total && this.submissionRate < 25) out.push("KPI adoption is low — only " + this.submissionRate + "% of targets submitted for approval across " + this.num(this.data.headcount.total) + " staff.");
      if (this.rankings && this.rankings.topDivision) { const t = this.rankings.topDivision, b = this.rankings.bottomDivision; out.push(t.division + " leads divisions (effectiveness " + t.score + ")" + (b ? ", while " + b.division + " trails (" + b.score + ")" : "") + "."); }
      if (this.pipReady) out.push("PIP due-delivery is " + this.pipDueDeliveryRate + "% across " + (this.pip.summary.divisionSources || 0) + " divisions as of " + this.pipScopeLabel + " — " + this.num(this.pip.summary.notMet) + " items are not met and " + this.pip.summary.unresolvedLabels + " names need mapping. Future pending work is excluded from this rate.");
      if (this.governance.totalProjects) out.push("Project governance is at " + this.governance.piCoverageRate + "% PI coverage — " + this.governance.projectsMissingPi + " projects need a PI, " + this.governance.unstaffedProjects + " have no active staff, and " + this.governance.employeesWithoutProjects + " employees have no project assignment.");
      if (this.hasDigital) {
        const dg = this.digitalLive;
        out.push("Brand reach ~" + this.compactNum(dg.reach.totalReach) + "/mo · SEO score " + dg.seo.score + "% · " + this.num(dg.content.ytd) + " content updates this FY.");
      }
      return out;
    },
    hasTrend() { return (this.data.attendanceTrend.trend || []).some((t) => t.count > 0); },
    pipReady() { return Number(this.pip.summary.total || 0) > 0; },
    pipWatchDivision() { return (this.pip.divisions || []).find((division) => division.counts.notMet > 0) || null; },
    pipChartHeight() { return Math.max(300, (this.pip.divisions || []).length * 34 + 90); },
    boardPipRows() {
      return (this.pip.divisions || []).filter((row) => Number(row.counts.due || 0) > 0).sort((a, b) => {
        const riskA = Number(a.counts.overdue || 0) * 2 + Number(a.counts.notMet || 0);
        const riskB = Number(b.counts.overdue || 0) * 2 + Number(b.counts.notMet || 0);
        return riskB - riskA || Number(a.counts.dueDeliveryRate || 0) - Number(b.counts.dueDeliveryRate || 0) || a.name.localeCompare(b.name);
      }).slice(0, 8);
    },
    boardPipRiskChart() {
      const rows = this.boardPipRows;
      const base = cartesianBase();
      return {
        series: [
          { name: 'Met', data: rows.map((row) => row.counts.met) },
          { name: 'Delayed Met', data: rows.map((row) => row.counts.delayedMet) },
          { name: 'Not Met', data: rows.map((row) => row.counts.notMet) },
          { name: 'Overdue', data: rows.map((row) => row.counts.overdue) },
        ],
        options: Object.assign(base, {
          colors: ['#2e7d32', '#d18a12', '#c62828', '#e45b2f'],
          chart: Object.assign({}, base.chart, { stacked: true, events: { dataPointSelection: (_event, _context, config) => { const row = rows[config.dataPointIndex]; if (row) this.openPipDivision(row); } } }),
          plotOptions: { bar: { horizontal: true, barHeight: '64%', borderRadius: 4 } },
          dataLabels: { enabled: false },
          stroke: { width: 0 },
          legend: { position: 'top', horizontalAlign: 'left', fontFamily: CHART_FONT, fontSize: '11px', markers: { radius: 4 } },
          xaxis: { categories: rows.map((row) => row.name + ' · ' + (row.counts.dueDeliveryRate || 0) + '%'), labels: { style: { fontSize: '10px', fontFamily: CHART_FONT } } },
          yaxis: { labels: { maxWidth: 175, style: { fontSize: '10.5px', fontFamily: CHART_FONT, fontWeight: 600 } } },
          tooltip: { shared: true, intersect: false, theme: 'light' },
        }),
      };
    },
    pipDivisionChart() {
      const rows = this.pip.divisions || [];
      const base = cartesianBase();
      return {
        series: [
          { name: "Met", data: rows.map((row) => row.counts.met) },
          { name: "Delayed Met", data: rows.map((row) => row.counts.delayedMet) },
          { name: "Not Met", data: rows.map((row) => row.counts.notMet) },
          { name: "Pending", data: rows.map((row) => row.counts.pending) },
        ],
        options: Object.assign(base, {
          colors: ["#2e7d32", "#e5a229", "#c62828", "#9aa8bd"],
          chart: Object.assign({}, base.chart, { stacked: true, events: { dataPointSelection: (_event, _context, config) => { const row = rows[config.dataPointIndex]; if (row) this.openPipDivision(row); } } }),
          plotOptions: { bar: { horizontal: true, barHeight: "66%", borderRadius: 4 } },
          dataLabels: { enabled: false },
          stroke: { width: 0 },
          legend: { position: "top", horizontalAlign: "left", fontFamily: CHART_FONT, fontSize: "12px", markers: { radius: 4 } },
          xaxis: { categories: rows.map((row) => row.name + " · " + (row.counts.dueDeliveryRate || 0) + "% due"), labels: { style: { fontSize: "10px", fontFamily: CHART_FONT } } },
          yaxis: { labels: { maxWidth: 180, style: { fontSize: "11px", fontFamily: CHART_FONT, fontWeight: 600 } } },
          tooltip: { shared: true, intersect: false, theme: "light" },
        }),
      };
    },
    hasTargetTrend() { return ((this.data.targetTrend && this.data.targetTrend.months) || []).some((m) => m.total > 0); },
    targetTrendChart() {
      const m = (this.data.targetTrend && this.data.targetTrend.months) || [];
      const base = cartesianBase();
      return {
        series: [
          { name: "Met on time", data: m.map((x) => x.metOnTime) },
          { name: "Delayed Met", data: m.map((x) => x.delayedMet) },
          { name: "Not Met", data: m.map((x) => x.notMet) },
          { name: "Pending", data: m.map((x) => x.pending) },
        ],
        options: Object.assign(base, {
          colors: ["#2e7d32", "#e08600", "#c62828", "#9aa8bd"],
          chart: Object.assign({}, base.chart, { stacked: true }),
          plotOptions: { bar: {
            columnWidth: "50%",
            borderRadius: 5, borderRadiusApplication: "end", borderRadiusWhenStacked: "last",
            dataLabels: { total: { enabled: true, offsetY: -3, style: { fontSize: "11px", fontFamily: CHART_FONT, fontWeight: 700, color: "#7a8a80" } } },
          } },
          dataLabels: { enabled: false },
          stroke: { width: 0 },
          fill: { type: "gradient", gradient: { shade: "light", type: "vertical", shadeIntensity: 0.12, opacityFrom: 1, opacityTo: 0.82, stops: [0, 100] } },
          legend: { position: "top", horizontalAlign: "left", fontFamily: CHART_FONT, fontSize: "12.5px", fontWeight: 600, markers: { radius: 4, width: 10, height: 10 }, itemMargin: { horizontal: 10 }, offsetY: 4 },
          xaxis: { categories: m.map((x) => x.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px", fontFamily: CHART_FONT, fontWeight: 600 } } },
          yaxis: { labels: { style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          tooltip: { shared: true, intersect: false, theme: "light" },
        }),
      };
    },
    dailyChart() {
      const days = (this.daily && this.daily.days) || [];
      const avg = this.daily.avgWorkingDayPercent || 0;
      const base = cartesianBase();
      return {
        series: [{
          name: "Attendance %",
          data: days.map((d) => ({ x: String(d.day), y: d.isFuture ? null : d.percent, fillColor: d.isWorking ? "#14663a" : "#d3ddd6" })),
        }],
        options: Object.assign(base, {
          plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: "end", columnWidth: "66%" } },
          dataLabels: { enabled: false },
          xaxis: { type: "category", tickPlacement: "on", axisBorder: { show: false }, axisTicks: { show: false }, labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "9.5px", fontFamily: CHART_FONT } } },
          yaxis: { min: 0, max: 100, tickAmount: 5, labels: { formatter: (v) => Math.round(v) + "%", style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          annotations: { yaxis: avg > 0 ? [{
            y: avg, borderColor: "#0f7a43", strokeDashArray: 5, opacity: 0.9,
            label: { text: "Avg " + avg + "%", position: "right", offsetX: -4, offsetY: -1,
              style: { background: "#0f7a43", color: "#fff", fontSize: "10px", fontWeight: 600, fontFamily: CHART_FONT, padding: { left: 6, right: 6, top: 2, bottom: 2 } } },
          }] : [] },
          tooltip: { theme: "light", custom: ({ dataPointIndex }) => {
            const d = days[dataPointIndex]; if (!d) return "";
            const dt = new Date(d.date);
            const wd = dt.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", timeZone: "UTC" });
            const tag = d.isWorking ? "Working day" : "Weekend / holiday";
            const dot = d.isWorking ? "#14663a" : "#94a3b8";
            return '<div style="padding:9px 12px;font-family:' + CHART_FONT + '">'
              + '<div style="font-weight:700;color:#23302a;font-size:12.5px">' + wd + '</div>'
              + '<div style="color:#33403a;font-size:13px;margin-top:3px"><b>' + d.percent + '%</b> · ' + d.present + ' present</div>'
              + '<div style="color:#9aa8a1;font-size:10.5px;margin-top:3px"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:' + dot + ';margin-right:4px"></span>' + tag + '</div></div>';
          } },
        }),
      };
    },
    hasFundraisingLive() { return !!(this.fundraisingLive && this.fundraisingLive.online); },
    frMonthlyChart() {
      const m = (this.fundraisingLive && this.fundraisingLive.online.monthly) || [];
      return {
        series: [{ name: "Raised", data: m.map((x) => x.amount) }],
        options: Object.assign(cartesianBase(), {
          colors: [GREEN],
          plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: "end", columnWidth: "52%" } },
          fill: { type: "gradient", gradient: { shade: "dark", type: "vertical", gradientToColors: ["#3ba368"], shadeIntensity: 0.2, opacityFrom: 1, opacityTo: 0.9, stops: [0, 100] } },
          stroke: { width: 0 }, dataLabels: { enabled: false },
          xaxis: { categories: m.map((x) => x.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px", fontFamily: CHART_FONT, fontWeight: 600 } } },
          yaxis: { labels: { formatter: (v) => this.frMoneyShort(v), style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          tooltip: { theme: "light", y: { formatter: (v) => this.formatMoney(v) } },
        }),
      };
    },
    frTrendChart() {
      const t = ((this.fundraisingLive && this.fundraisingLive.trend) || []).slice(-12);
      const base = cartesianBase();
      return {
        series: [
          { name: "Website", data: t.map((x) => x.website) },
          { name: "Online fundraising", data: t.map((x) => x.online) },
        ],
        options: Object.assign(base, {
          colors: ["#1565c0", "#14663a"],
          chart: Object.assign({}, base.chart, { stacked: true }),
          plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: "end", borderRadiusWhenStacked: "last", columnWidth: "62%" } },
          dataLabels: { enabled: false }, stroke: { width: 0 },
          legend: { position: "top", horizontalAlign: "left", fontFamily: CHART_FONT, fontSize: "12px", markers: { radius: 3 } },
          xaxis: { categories: t.map((x) => x.period), axisBorder: { show: false }, axisTicks: { show: false }, labels: { rotate: -45, style: { fontSize: "9.5px", fontFamily: CHART_FONT } } },
          yaxis: { labels: { formatter: (v) => "₹" + Math.round(v) + "L", style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          tooltip: { theme: "light", shared: true, intersect: false, y: { formatter: (v) => "₹" + v + " L" } },
        }),
      };
    },
    hasDigital() { return !!(this.digitalLive && this.digitalLive.reach); },
    reachTrendChart() {
      const m = (this.digitalLive && this.digitalLive.reach.monthly) || [];
      return {
        series: [{ name: "Total reach", data: m.map((x) => x.value) }],
        options: Object.assign(cartesianBase(), {
          colors: [GREEN], stroke: { curve: "smooth", width: 3 },
          markers: { size: 4, colors: ["#fff"], strokeColors: GREEN, strokeWidth: 2 },
          fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.03, stops: [0, 95] } },
          dataLabels: { enabled: false },
          xaxis: { categories: m.map((x) => x.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px", fontFamily: CHART_FONT, fontWeight: 600 } } },
          yaxis: { labels: { formatter: (v) => this.compactNum(v), style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          tooltip: { theme: "light", y: { formatter: (v) => this.num(v) + " reach" } },
        }),
      };
    },
    mediaYearChart() {
      const y = (this.digitalLive && this.digitalLive.media.byYear) || [];
      return {
        series: [{ name: "Media hits", data: y.map((x) => x.total) }],
        options: Object.assign(cartesianBase(), {
          colors: ["#1565c0"],
          plotOptions: { bar: { borderRadius: 5, borderRadiusApplication: "end", columnWidth: "55%" } },
          fill: { type: "gradient", gradient: { shade: "dark", type: "vertical", gradientToColors: ["#42a5f5"], shadeIntensity: 0.2, opacityFrom: 1, opacityTo: 0.9, stops: [0, 100] } },
          stroke: { width: 0 },
          dataLabels: { enabled: true, offsetY: -16, style: { fontSize: "10px", fontFamily: CHART_FONT, fontWeight: 600, colors: ["#7a8a80"] }, background: { enabled: false } },
          xaxis: { categories: y.map((x) => x.fy), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "10px", fontFamily: CHART_FONT } } },
          yaxis: { labels: { style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          tooltip: { theme: "light" },
        }),
      };
    },
    contentBars() {
      const b = (this.digitalLive && this.digitalLive.content.byPlatform) || [];
      const max = Math.max(1, ...b.map((x) => x.count));
      const colors = { Facebook: "#1565c0", Instagram: "#c2185b", LinkedIn: "#0a66c2", YouTube: "#c62828", Website: "#14663a", Twitter: "#546e7a", X: "#546e7a" };
      return b.map((x) => ({ name: x.name, count: x.count, pct: Math.max(4, Math.round((x.count / max) * 100)), color: colors[x.name] || "#64748b" }));
    },
    achievement() { return (this.data.performance && this.data.performance.achievement) || { met: 0, delayedMet: 0, notMet: 0 }; },
    outcome() { return (this.data.performance && this.data.performance.outcome) || { total: 0, metOnTime: 0, delayedMet: 0, notMet: 0, pendingNotDue: 0, metOnTimePct: 0, delayedMetPct: 0, notMetPct: 0, pendingNotDuePct: 0, overallMetPct: 0 }; },
    outcomeKpis() {
      const o = this.outcome;
      return [
        { label: "Met on time", value: this.num(o.metOnTime), pct: o.metOnTimePct, color: "#2e7d32", icon: "mdi-check-circle", key: "met" },
        { label: "Delayed Met", value: this.num(o.delayedMet), pct: o.delayedMetPct, color: "#e08600", icon: "mdi-clock-check", key: "delayed" },
        { label: "Not Met", value: this.num(o.notMet), pct: o.notMetPct, color: "#c62828", icon: "mdi-close-circle", key: "notmet" },
        { label: "Pending — not due", value: this.num(o.pendingNotDue), pct: o.pendingNotDuePct, color: "#64748b", icon: "mdi-timer-sand", key: "pending" },
      ];
    },
    outcomeSegments() {
      const o = this.outcome;
      return [
        { label: "Met", count: o.metOnTime, pct: o.metOnTimePct, color: "#2e7d32" },
        { label: "Delayed", count: o.delayedMet, pct: o.delayedMetPct, color: "#e08600" },
        { label: "Not Met", count: o.notMet, pct: o.notMetPct, color: "#c62828" },
        { label: "Pending", count: o.pendingNotDue, pct: o.pendingNotDuePct, color: "#94a3b8" },
      ];
    },
    outcomeBars() {
      const o = this.outcome;
      return [
        { label: "Met on time", value: o.metOnTime, color: "#2e7d32" },
        { label: "Delayed Met", value: o.delayedMet, color: "#e08600" },
        { label: "Not Met", value: o.notMet, color: "#c62828" },
        { label: "Pending", value: o.pendingNotDue, color: "#94a3b8" },
      ];
    },
    attendanceBars() {
      const a = this.data.attendance;
      const total = a.totalEmployees || (a.presentCount + a.leaveCount + a.absentCount) || 1;
      const mk = (label, value, color, type) => ({ label, value, color, type, pct: Math.round((value / total) * 1000) / 10 });
      return [mk("Present", a.presentCount, "#2e7d32", "present"), mk("On Leave", a.leaveCount, "#1565c0", "leave"), mk("Absent", a.absentCount, "#c62828", "absent")];
    },
    genderBars() {
      const g = this.data.headcount.gender || [];
      const total = g.reduce((s, x) => s + x.count, 0) || 1;
      const colors = ["#14663a", "#1565c0", "#e08600", "#8e24aa"];
      return g.map((x, i) => ({ label: x.label, value: x.count, color: colors[i % colors.length], pct: Math.round((x.count / total) * 1000) / 10 }));
    },
    hasFundraising() {
      const t = this.data.targets || {};
      return (t.orgTarget || 0) > 0 || (t.assignedTotal || 0) > 0 || (t.raised || 0) > 0;
    },
    kpis() {
      const a = this.data.attendance, h = this.data.headcount;
      return [
        { label: "TOTAL STAFF", value: this.num(h.total), color: GREEN, icon: "mdi-account-group", caption: "Active employees", type: "total" },
        { label: "ATTENDANCE RATE", value: (a.presentPercent || 0) + "%", color: "#00796b", icon: "mdi-percent", caption: "Present today", type: "present" },
        { label: "PRESENT TODAY", value: this.num(a.presentCount), color: "#2e7d32", icon: "mdi-account-check", caption: "Marked in", type: "present" },
        { label: "ON LEAVE", value: this.num(a.leaveCount), color: "#1565c0", icon: "mdi-airplane", caption: "Approved leave", type: "leave" },
        { label: "ABSENT", value: this.num(a.absentCount), color: "#c62828", icon: "mdi-account-off", caption: "Not marked", type: "absent" },
        { label: "LATE ARRIVALS", value: this.num(a.lateArrivals), color: "#e08600", icon: "mdi-clock-alert", caption: "Today", type: "late" },
        { label: "CONFIRMED", value: this.num(h.confirmed), color: "#00796b", icon: "mdi-check-decagram", caption: "Confirmed staff", type: "confirmed" },
        { label: "ON PROBATION", value: this.num(h.onProbation), color: "#e08600", icon: "mdi-account-clock", caption: "Under probation", type: "probation" },
        { label: "EXITS (FY)", value: this.num(this.data.attrition.exitsThisYear), color: "#c62828", icon: "mdi-exit-run", caption: "Attrition this FY", type: "exits" },
      ];
    },
    perfKpis() {
      const p = this.data.performance;
      return [
        { label: "Not Submitted", value: this.num(p.notSubmitted), color: "#64748b", icon: "mdi-file-outline" },
        { label: "Pending Approval", value: this.num(p.pendingApproval), color: "#1565c0", icon: "mdi-timer-sand" },
        { label: "Approved", value: this.num(p.approved), color: "#2e7d32", icon: "mdi-check-circle" },
        { label: "Rejected", value: this.num(p.rejected), color: "#c62828", icon: "mdi-close-circle" },
      ];
    },
    stageColor() {
      return { "Not Submitted": "#94a3b8", "Pending Approval": "#1565c0", "Approved": "#2e7d32", "Rejected": "#c62828" };
    },
    perfSegments() {
      const p = this.data.performance;
      const total = p.total || 1;
      return (p.stages || []).map((s) => ({
        label: s.status, count: s.count, color: this.stageColor[s.status] || "#94a3b8",
        pct: (s.count / total) * 100, pctLabel: Math.round((s.count / total) * 1000) / 10,
      }));
    },
    performanceDonut() {
      const p = this.data.performance;
      const stages = p.stages || [];
      const opts = donutOptions(stages.map((s) => s.status), stages.map((s) => this.stageColor[s.status] || "#94a3b8"), {
        donut: { labels: { show: true, total: { show: true, label: "Total", color: CHART_FORE, fontSize: "13px", formatter: () => p.total } } },
      });
      return { series: stages.map((s) => s.count), options: opts };
    },
    attendanceDonut() {
      const a = this.data.attendance;
      return {
        series: [a.presentCount, a.leaveCount, a.absentCount],
        options: donutOptions(["Present", "On Leave", "Absent"], ["#2e7d32", "#1565c0", "#c62828"]),
      };
    },
    genderDonut() {
      const g = this.data.headcount.gender;
      return {
        series: g.map((x) => x.count),
        options: donutOptions(g.map((x) => x.label), ["#14663a", "#2e7d32", "#1565c0", "#e08600", "#8e24aa"]),
      };
    },
    attendanceBar() {
      const t = this.data.attendanceTrend.trend || [];
      return {
        series: [{ name: "Attendance", data: t.map((x) => x.count) }],
        options: Object.assign(cartesianBase(), {
          colors: [GREEN],
          plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: "end", columnWidth: "48%" } },
          fill: { type: "gradient", gradient: { shade: "dark", type: "vertical", gradientToColors: ["#3ba368"], shadeIntensity: 0.2, opacityFrom: 1, opacityTo: 0.88, stops: [0, 100] } },
          stroke: { width: 0 },
          dataLabels: { enabled: true, offsetY: -18, background: { enabled: false }, style: { fontSize: "10px", fontFamily: CHART_FONT, fontWeight: 600, colors: ["#7a8a80"] },
            formatter: (v) => (v >= 1000 ? (v / 1000).toFixed(1) + "k" : (v || "")) },
          xaxis: { categories: t.map((x) => x.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px", fontFamily: CHART_FONT, fontWeight: 600 } } },
          yaxis: { labels: { formatter: (v) => (v >= 1000 ? v / 1000 + "k" : v), style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          tooltip: { theme: "light", y: { formatter: (v) => this.num(v) + " logins" } },
        }),
      };
    },
    growthChart() {
      const g = this.data.headcount.growth;
      return {
        series: [{ name: "Joined", data: g.map((x) => x.count) }],
        options: Object.assign(cartesianBase(), {
          colors: [GREEN], dataLabels: { enabled: false },
          stroke: { curve: "smooth", width: 3 },
          markers: { size: 0, colors: ["#fff"], strokeColors: GREEN, strokeWidth: 2, hover: { size: 6 } },
          fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.42, opacityTo: 0.03, stops: [0, 96] } },
          xaxis: { categories: g.map((x) => x.month), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "10px", fontFamily: CHART_FONT } } },
          yaxis: { labels: { style: { fontSize: "11px", fontFamily: CHART_FONT } } },
          tooltip: { theme: "light", y: { formatter: (v) => v + (v === 1 ? " joined" : " joined") } },
        }),
      };
    },
    targetGauge() {
      return {
        series: [this.data.targets.achievementPercent || 0],
        options: {
          chart: { fontFamily: CHART_FONT, foreColor: CHART_FORE },
          colors: ["#b56b00"],
          plotOptions: { radialBar: { hollow: { size: "62%" }, track: { background: "#f3ece0" },
            dataLabels: { name: { show: true, offsetY: 22, color: "#a08050", fontSize: "12px" },
              value: { offsetY: -12, fontSize: "26px", fontWeight: 700, color: "#1b2320", formatter: (v) => `${v}%` } } } },
          fill: { type: "gradient", gradient: { shade: "dark", type: "horizontal", gradientToColors: ["#e6a020"], stops: [0, 100] } },
          labels: ["Raised"],
        },
      };
    },
  },
  mounted() { this.boardMode = this.$route.query.board === '1'; this.fetchOverview(); this.fetchEmployees(); this.fetchDaily(); this.fetchInsights(); this.fetchPrevAttendance(); this.fetchPulseTimeline(); this.fetchNoSheetCount(); this.fetchHiddenTestCount(); this.fetchAutoCount(); this.fetchExemptCount(); this.fetchDivisions(); this.fetchRetention(); this.fetchFundraisingLive(); this.fetchDigitalLive(); this.fetchMediaNews(); },
  beforeDestroy() { this.stopPulseTimer(); },
  methods: {
    authHeaders() { return { "x-auth-token": localStorage.getItem("token") }; },
    goPiManager(missingOnly = false) { this.$router.push({ path: '/ceo-employee-mapping', query: { manage: 'pi', ...(missingOnly ? { ownership: 'missing' } : {}) } }); },
    barColor(v) { if (v == null) return "#94a3b8"; if (v >= 70) return "#2e7d32"; if (v >= 45) return "#e08600"; return "#c62828"; },
    sortDivisions(key) { if (this.divSortKey === key) { this.divSortDir *= -1; } else { this.divSortKey = key; this.divSortDir = key === "division" ? 1 : -1; } },
    openPipDivision(division) {
      if (!division || !division.id) return;
      this.$router.push({ path: '/ceo-pip', query: { tab: 'divisions', openDivision: division.id } });
    },
    divCellText(c, v) { if (v == null || v === "") return "—"; return c.suffix ? v + c.suffix : v; },
    divCellBg(c, v) {
      if (c.kind === "neutral" || v == null) return "transparent";
      if (c.kind === "good") return v >= 60 ? "#e2f1e8" : v >= 40 ? "#eef6ea" : v >= 20 ? "#fdf3e3" : "#fdecec";
      return v === 0 ? "#e2f1e8" : v <= 2 ? "#fdf3e3" : "#fdecec"; // bad: higher = worse
    },
    divCellFg(c, v) {
      if (c.kind === "neutral" || v == null) return "#33403a";
      if (c.kind === "good") return v >= 60 ? "#1b6b3a" : v >= 40 ? "#4b7a2e" : v >= 20 ? "#9a6a12" : "#b0342c";
      return v === 0 ? "#1b6b3a" : v <= 2 ? "#9a6a12" : "#b0342c";
    },
    async fetchPulseTimeline() {
      try { const res = await axios({ method: "GET", url: "dashboard/ceo/pulse-timeline", headers: this.authHeaders() }); if (res.data && res.data.status) this.pulseTimeline = res.data.months || []; } catch (e) { /* play button hidden */ }
    },
    async fetchNoSheetCount() {
      try { const res = await axios({ method: "GET", url: "dashboard/ceo/list", params: { type: "notargetsheet" }, headers: this.authHeaders() }); if (res.data && res.data.status) this.noSheetCount = res.data.count || 0; } catch (e) { /* alert hidden */ }
    },
    togglePulsePlay() {
      if (this.pulsePlaying) { this.stopPulseTimer(); return; }
      if (!this.pulseTimeline.length) return;
      this.pulsePlaying = true;
      this.pulseIdx = 0;
      this.pulseTimer = setInterval(() => {
        if (this.pulseIdx >= this.pulseTimeline.length - 1) { this.stopPulseTimer(); this.pulseIdx = -1; return; }
        this.pulseIdx += 1;
      }, 1000);
    },
    stopPulseTimer() { if (this.pulseTimer) { clearInterval(this.pulseTimer); this.pulseTimer = null; } this.pulsePlaying = false; },
    scrubPulse(i) { this.stopPulseTimer(); this.pulseIdx = i; },
    async fetchInsights() {
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/analytics", headers: this.authHeaders() });
        if (res.data && res.data.status) this.insights = res.data.data;
      } catch (e) { /* rankings/alerts degrade gracefully */ }
    },
    async fetchPipDashboard() {
      try {
        const res = await axios({ method: "GET", url: "/pip/admin/dashboard", headers: this.authHeaders() });
        if (res.data && res.data.status) this.pip = { financialYear: res.data.financialYear || '', summary: res.data.summary || {}, divisions: res.data.divisionAnalysis || [], dataStatus: res.data.dataStatus || {}, calculationScope: res.data.calculationScope || {} };
      } catch (e) { /* PIP panel and pulse component remain hidden */ }
    },
    async fetchDivisions() {
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/divisions", headers: this.authHeaders() });
        if (res.data && res.data.status) this.divisionStats = res.data.divisions || [];
      } catch (e) { /* league table hidden */ }
    },
    async fetchRetention() {
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/retention", headers: this.authHeaders() });
        if (res.data && res.data.status) this.retention = res.data;
      } catch (e) { /* section hidden */ }
    },
    async fetchFundraisingLive() {
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/fundraising-live", headers: this.authHeaders() });
        if (res.data && res.data.status) this.fundraisingLive = res.data;
      } catch (e) { /* section hidden if sheet unreachable */ }
    },
    async fetchDigitalLive() {
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/digital-live", headers: this.authHeaders() });
        if (res.data && res.data.status) this.digitalLive = res.data;
      } catch (e) { /* section hidden if sheet unreachable */ }
    },
    compactNum(v) {
      v = Number(v) || 0;
      if (v >= 10000000) return (v / 10000000).toFixed(2) + "Cr";
      if (v >= 100000) return (v / 100000).toFixed(2) + "L";
      if (v >= 1000) return (v / 1000).toFixed(1) + "k";
      return String(Math.round(v));
    },
    frMoneyShort(v) {
      v = Number(v) || 0;
      if (v >= 10000000) return "₹" + (v / 10000000).toFixed(1) + "Cr";
      if (v >= 100000) return "₹" + Math.round(v / 100000) + "L";
      if (v >= 1000) return "₹" + Math.round(v / 1000) + "k";
      return "₹" + v;
    },
    async fetchPrevAttendance() {
      try {
        const d = new Date();
        const py = d.getUTCMonth() === 0 ? d.getUTCFullYear() - 1 : d.getUTCFullYear();
        const pm = d.getUTCMonth() === 0 ? 12 : d.getUTCMonth(); // previous month, 1-based
        const month = py + "-" + String(pm).padStart(2, "0");
        const res = await axios({ method: "GET", url: "dashboard/ceo/attendance-daily", params: { month }, headers: this.authHeaders() });
        if (res.data && res.data.status) this.attnPrevAvg = res.data.avgWorkingDayPercent;
      } catch (e) { /* no delta shown */ }
    },
    async fetchDaily(month) {
      this.dailyLoading = true;
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/attendance-daily", params: month ? { month } : {}, headers: this.authHeaders() });
        if (res.data && res.data.status) { this.daily = res.data; this.dailyMonth = res.data.month; }
      } catch (e) { /* keep prior state */ }
      finally { this.dailyLoading = false; }
    },
    goResigned() {
      // Opens the Performance Targets page scoped to resigned / left staff.
      this.$router.push({ path: "/ceo-targets", query: { scope: "resigned" } });
    },
    scoreColor(v) { if (v == null) return "#94a3b8"; if (v >= 70) return "#2e7d32"; if (v >= 50) return "#7cb342"; if (v >= 30) return "#e08600"; return "#c62828"; },
    driverColor(key) { return { achievement: "#14663a", timeliness: "#1565c0", attendance: "#2e7d32", engagement: "#8e24aa", pmr: "#e08600" }[key] || "#64748b"; },
    topPct(rank, total) { if (!total) return 0; return Math.max(1, Math.round((rank / total) * 100)); },
    async openDetail(type, title) {
      if (!type) return;
      const requestId = ++this.detailRequestId;
      this.detailDialog = true; this.detailLoading = true; this.detailTitle = title; this.detailType = type; this.detailRows = [];
      this.detailError = ""; this.attendanceSearch = ""; this.attendanceGpsFilter = "all"; this.attendanceSort = "name";
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/list", params: { type }, headers: this.authHeaders() });
        if (requestId !== this.detailRequestId) return;
        if (!res.data || !res.data.status) throw new Error((res.data && res.data.msg) || "Unable to load attendance details.");
        this.detailRows = (res.data && res.data.status) ? res.data.data : [];
      } catch (e) { if (requestId === this.detailRequestId) { this.detailRows = []; this.detailError = e.message || "Unable to load attendance details."; } }
      finally { if (requestId === this.detailRequestId) this.detailLoading = false; }
    },
    attendanceTime(value) {
      const date = new Date(value);
      return value && !Number.isNaN(date.getTime()) ? date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Kolkata" }) : "Time not recorded";
    },
    attendanceMapUrl(location) {
      return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(location.latitude + "," + location.longitude);
    },
    printBoard() {
      this.boardMode = true;
      this.$nextTick(() => setTimeout(() => window.print(), 250));
    },
    async toggleFullscreen() {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else if (this.$el && this.$el.requestFullscreen) await this.$el.requestFullscreen();
      } catch (e) { /* Browser may block full screen outside a direct click. */ }
    },
    async downloadRoster() {
      if (this.rosterLoading) return;
      this.rosterLoading = true;
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/employee-roster.pdf", responseType: "blob", headers: this.authHeaders() });
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "WTI-Employee-Roster.pdf";
        document.body.appendChild(a); a.click();
        a.remove(); window.URL.revokeObjectURL(url);
      } catch (e) {
        window.alert("Could not generate the roster PDF. Please try again.");
      } finally {
        this.rosterLoading = false;
      }
    },
    async openDivision(division) {
      if (!division) return;
      ++this.detailRequestId;
      const title = division === "Unassigned" ? "Unassigned — staff with no division set" : division + " · staff";
      this.detailDialog = true; this.detailLoading = true; this.detailTitle = title; this.detailType = "division"; this.detailRows = [];
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/list", params: { type: "division", division }, headers: this.authHeaders() });
        this.detailRows = (res.data && res.data.status) ? res.data.data : [];
      } catch (e) { this.detailRows = []; }
      finally { this.detailLoading = false; }
    },
    async markTestAccount(row, isTest) {
      if (!row || !row.id) return;
      const msg = isTest
        ? 'Mark "' + row.name + '" as a test/system account?\n\nIt will be excluded from every dashboard statistic (headcount, attendance, targets, pulse…). You can restore it later.'
        : 'Restore "' + row.name + '" to a real account?\n\nIt will count in the dashboard again.';
      if (!window.confirm(msg)) return;
      try {
        await axios({ method: "POST", url: "dashboard/ceo/mark-test-account", data: { employeeId: row.id, isTest }, headers: this.authHeaders() });
        await this.openDetail(this.detailType, this.detailTitle); // refresh the open list
        this.refreshCounts();
      } catch (e) { /* ignore */ }
    },
    refreshCounts() { this.fetchOverview(); this.fetchNoSheetCount(); this.fetchInsights(); this.fetchPulseTimeline(); this.fetchHiddenTestCount(); this.fetchAutoCount(); this.fetchExemptCount(); this.fetchEmployees(); this.fetchDaily(this.dailyMonth); this.fetchPrevAttendance(); this.fetchDivisions(); this.fetchMediaNews(); },
    async fetchMediaNews() {
      this.mediaLoading = true;
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/media-news", params: { limit: 6, dateFilter: "30days" }, headers: this.authHeaders() });
        if (res.data && res.data.status) {
          // Show recent items; if nothing in 30 days, fall back to the latest available.
          this.mediaItems = res.data.items || [];
          this.mediaStats = res.data.stats || { total: 0, positive: 0, negative: 0, neutral: 0, positivePct: 0 };
          this.mediaImpact = res.data.mediaImpact || {};
          if (!this.mediaItems.length && (this.mediaStats.total || 0) > 0) {
            const r2 = await axios({ method: "GET", url: "dashboard/ceo/media-news", params: { limit: 6, dateFilter: "all" }, headers: this.authHeaders() });
            if (r2.data && r2.data.status) this.mediaItems = r2.data.items || [];
          }
        }
      } catch (e) {
        console.log("fetchMediaNews error:", e.message);
      } finally {
        this.mediaLoading = false;
      }
    },
    async fetchMediaScraper() {
      this.mediaScraping = true;
      try {
        const res = await axios({ method: "POST", url: "dashboard/ceo/media-news/scrape", headers: this.authHeaders() });
        if (res.data && res.data.status) {
          this.fetchMediaNews();
        }
      } catch (e) {
        console.log("fetchMediaScraper error:", e.message);
      } finally {
        this.mediaScraping = false;
      }
    },
    formatMediaDate(d) {
      if (!d) return "Recent";
      const m = moment(d);
      if (m.isSame(moment(), "day")) return "Today, " + m.format("h:mm A");
      if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday, " + m.format("h:mm A");
      return m.format("MMM D, YYYY");
    },
    tierClass(tier) { return tier === 1 ? "t1" : tier === 2 ? "t2" : "t3"; },
    mediaImgError(ev) { if (ev && ev.target) ev.target.style.display = "none"; },
    async fetchHiddenTestCount() {
      try { const res = await axios({ method: "GET", url: "dashboard/ceo/list", params: { type: "testaccounts" }, headers: this.authHeaders() }); if (res.data && res.data.status) this.hiddenTestCount = res.data.count || 0; } catch (e) { /* hide chip */ }
    },
    async fetchAutoCount() {
      try { const res = await axios({ method: "GET", url: "dashboard/ceo/list", params: { type: "autoattendance" }, headers: this.authHeaders() }); if (res.data && res.data.status) this.autoCount = res.data.count || 0; } catch (e) { /* hide chip */ }
    },
    openTestManager() { this.openDetail("testaccounts", "Test / system accounts (hidden)"); },
    openAutoManager() { this.openDetail("autoattendance", "Auto-attendance seniors"); },
    openExemptManager() { this.openDetail("targetexempt", "Staff exempt from target sheets"); },
    async fetchExemptCount() {
      try { const res = await axios({ method: "GET", url: "dashboard/ceo/list", params: { type: "targetexempt" }, headers: this.authHeaders() }); if (res.data && res.data.status) this.exemptCount = res.data.count || 0; } catch (e) { /* hide chip */ }
    },
    async markTargetExempt(row, value) {
      if (!row || !row.id) return;
      const msg = value
        ? 'Mark "' + row.name + '" as not needing a target sheet?\n\nThey will be removed from the "staff without a target sheet" gap. You can undo this later.'
        : 'Include "' + row.name + '" again?\n\nThey will be expected to have a target sheet.';
      if (!window.confirm(msg)) return;
      try {
        await axios({ method: "POST", url: "dashboard/ceo/mark-target-exempt", data: { employeeId: row.id, value }, headers: this.authHeaders() });
        await this.openDetail(this.detailType, this.detailTitle);
        this.refreshCounts();
      } catch (e) { /* ignore */ }
    },
    async markAutoAttendance(row, value) {
      if (!row || !row.id) return;
      const msg = value
        ? 'Mark "' + row.name + '" as an auto-attendance senior?\n\nThey will be counted present on every working day (exempt from punching in). You can undo this later.'
        : 'Remove auto-attendance from "' + row.name + '"?\n\nTheir attendance will be counted from actual punches again.';
      if (!window.confirm(msg)) return;
      try {
        await axios({ method: "POST", url: "dashboard/ceo/mark-auto-attendance", data: { employeeId: row.id, value }, headers: this.authHeaders() });
        await this.openDetail(this.detailType, this.detailTitle);
        this.refreshCounts();
      } catch (e) { /* ignore */ }
    },
    openEmployeeTargets() {
      // Opens the full Performance Targets page (filters, drill-down, by-project).
      this.$router.push("/ceo-targets");
    },
    goOutcome(key) {
      // Opens the Performance Targets page filtered to a specific outcome bucket.
      this.$router.push({ path: "/ceo-targets", query: { outcome: key } });
    },
    viewEmployeeTargets(empId) {
      // Opens the Performance Targets page pre-filtered to this employee.
      this.$router.push({ path: "/ceo-targets", query: { emp: empId || "" } });
    },
    async fetchEmployees() {
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/employees", headers: this.authHeaders() });
        this.employees = (res.data && res.data.status) ? res.data.data.map((e) => ({ text: (e.name || "").trim() + (e.employeeId ? " · " + e.employeeId : ""), value: e.id })) : [];
      } catch (e) { this.employees = []; }
    },
    async onSelectEmployee() {
      if (!this.selectedEmployeeId) { this.empSummary = null; return; }
      this.empLoading = true; this.empSummary = null;
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/employee-summary", params: { employeeId: this.selectedEmployeeId }, headers: this.authHeaders() });
        this.empSummary = (res.data && res.data.status) ? res.data.data : null;
      } catch (e) { this.empSummary = null; }
      finally { this.empLoading = false; }
    },
    async fetchOverview() {
      this.loading = true; this.errorMsg = "";
      this.fetchPipDashboard();
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/overview", headers: { "x-auth-token": localStorage.getItem("token") } });
        if (res.data && res.data.status) {
          this.data = Object.assign(emptyData(), res.data.data);
          this.generatedAt = res.data.generatedAt;
          this.financialYear = res.data.financialYear || "";
        } else { this.errorMsg = (res.data && res.data.msg) || "Failed to load overview."; }
      } catch (e) { this.errorMsg = "Unable to reach the server."; }
      finally { this.loading = false; }
    },
    logout() { this.$store.commit("logoutUser"); },
    num(n) { return Number(n || 0).toLocaleString("en-IN"); },
    barPct(value, arr) {
      const max = Math.max(1, ...arr.map((x) => x.value || 0));
      const pct = Math.round((value / max) * 100);
      return value > 0 ? Math.max(4, pct) : 0; // keep tiny non-zero bars visible
    },
    formatMoney(n) {
      if (n === null || n === undefined) return "₹0";
      const v = Number(n);
      if (Math.abs(v) >= 10000000) return "₹" + (v / 10000000).toFixed(2) + " Cr";
      if (Math.abs(v) >= 100000) return "₹" + (v / 100000).toFixed(2) + " L";
      return "₹" + v.toLocaleString("en-IN");
    },
    formatUsd(n) {
      const v = Number(n || 0);
      if (Math.abs(v) >= 1000000) return "$" + (v / 1000000).toFixed(2) + "M";
      if (Math.abs(v) >= 1000) return "$" + (v / 1000).toFixed(1) + "K";
      return "$" + v.toLocaleString("en-US");
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""; },
    formatDateTime(d) { return d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""; },
  },
};
</script>

<style scoped>
.ceo-root { min-height: 100vh; width: 100%;
  background: radial-gradient(1100px 460px at 92% -8%, rgba(20,102,58,0.08), transparent 60%), linear-gradient(180deg, #f3f7f4 0%, #e9f0eb 100%);
  font-family: "Poppins", "Segoe UI", system-ui, sans-serif; color: #1b2320; }

/* Top bar */
.ceo-topbar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between;
  padding: 12px 26px; background: rgba(255,255,255,0.94); backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 1px solid rgba(20,102,58,0.12); }
.brand { display: flex; align-items: center; gap: 13px; }
.brand-logo-box { width: 52px; height: 52px; border-radius: 13px; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #14663a, #2e7d32); box-shadow: 0 6px 16px rgba(20,102,58,0.3); padding: 7px; }
.brand-logo { max-width: 100%; max-height: 100%; object-fit: contain; }
.brand-title { font-size: 18px; font-weight: 700; letter-spacing: -0.2px; }
.brand-sub { font-size: 11.5px; color: #6b7a72; margin-top: -2px; }
.topbar-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 12px; }
.updated { font-size: 12px; color: #6b7a72; display: inline-flex; align-items: center; gap: 5px; }
.fy-chip { font-size: 12px; font-weight: 700; color: #14663a; background: #e5f1ea; border: 1px solid #cfe5d8; padding: 5px 12px; border-radius: 20px; letter-spacing: 0.3px; }
.btn { display: inline-flex; align-items: center; gap: 7px; border: none; cursor: pointer; font-size: 13px; font-weight: 600;
  padding: 8px 14px; border-radius: 10px; transition: all 0.18s ease; font-family: inherit; }
.btn.ghost { background: #fff; color: #14663a; border: 1px solid rgba(20,102,58,0.2); }
.btn.ghost:hover { background: #eef6f0; }
.btn.solid { background: linear-gradient(135deg, #14663a, #2e7d32); color: #fff; box-shadow: 0 4px 12px rgba(20,102,58,0.25); }
.btn.solid:hover { filter: brightness(1.06); }
.btn.board-toggle { background: #173f2a; color: #fff; border: 1px solid #173f2a; box-shadow: 0 4px 12px rgba(18,63,42,.18); }
.btn.board-toggle:hover { background: #0f5131; }
.management-menu .v-list-item__icon { margin-right: 12px; }
/* Prominent primary CTA — stands out from the green header/logout */
.btn.analytics {
  background: linear-gradient(135deg, #e6820a, #f5a524); color: #fff; font-weight: 700; font-size: 13.5px;
  padding: 9px 18px; box-shadow: 0 6px 18px rgba(230,130,10,0.38); border: none;
  animation: analyticsPulse 2.6s ease-in-out infinite;
}
.btn.analytics:hover { filter: brightness(1.06); transform: translateY(-1px); }
@keyframes analyticsPulse {
  0%, 100% { box-shadow: 0 6px 18px rgba(230,130,10,0.35); }
  50% { box-shadow: 0 6px 22px rgba(230,130,10,0.6); }
}
.btn:disabled { opacity: 0.6; cursor: default; }
.spin { animation: spin 0.9s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }

.loader-wrap { display: flex; justify-content: center; align-items: center; min-height: 70vh; }
.ceo-body { max-width: 1400px; margin: 0 auto; padding: 20px 26px 44px; }
.board-mode .ceo-body { max-width: 1640px; padding-top: 26px; }
.board-view { display: flex; flex-direction: column; gap: 18px; }
.board-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; padding: 27px 30px; border-radius: 22px; color: #fff; background: linear-gradient(120deg,#0e4d2d 0%,#14663a 58%,#2d8050 100%); box-shadow: 0 16px 38px rgba(14,77,45,.18); }
.board-kicker { display: block; color: #bce0ca; font-size: 12px; font-weight: 800; letter-spacing: 1.3px; }
.board-hero h1 { margin: 7px 0 4px; font-size: 32px; line-height: 1.15; letter-spacing: -.7px; }
.board-hero p { margin: 0; color: #d9ebe0; font-size: 14px; }
.board-asof { min-width: 230px; padding-left: 22px; border-left: 1px solid rgba(255,255,255,.26); display: flex; flex-direction: column; }
.board-asof span,.board-asof small { color: #c6dfcf; font-size: 11px; }.board-asof strong{font-size:17px;margin:3px 0}
.board-source-strip { display: grid; grid-template-columns: repeat(6,minmax(0,1fr)); gap: 8px; }
.board-source { display: flex; gap: 8px; align-items: flex-start; background: rgba(255,255,255,.82); border: 1px solid #dfe9e2; border-radius: 10px; padding: 9px 11px; min-width: 0; }
.board-source i { width: 8px; height: 8px; border-radius: 50%; margin-top: 4px; flex: 0 0 8px; background: #94a3b8; }.board-source.ready i{background:#2e7d32}.board-source.warning i{background:#e08600}.board-source.missing i{background:#c62828}
.board-source span { min-width: 0; color: #78877f; font-size: 10.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }.board-source b{display:block;color:#263b30;font-size:11.5px}
.board-kpi-grid { display: grid; grid-template-columns: repeat(6,minmax(0,1fr)); gap: 12px; }
.board-kpi { position: relative; overflow: hidden; background:#fff; border:1px solid #e1e9e4; border-top:4px solid #64748b; border-radius:15px; padding:16px 17px; box-shadow:0 7px 20px rgba(29,64,44,.055); min-height:125px; }
.board-kpi.good{border-top-color:#2e7d32}.board-kpi.warn{border-top-color:#e08600}.board-kpi.bad{border-top-color:#c62828}.board-kpi.neutral{border-top-color:#1565c0}
.board-kpi>span{display:block;color:#66776d;font-size:11.5px;font-weight:700;text-transform:uppercase;letter-spacing:.35px}.board-kpi strong{display:block;color:#173b28;font-size:28px;line-height:1.1;margin:12px 0 7px;letter-spacing:-.5px}.board-kpi small{display:block;color:#7e8e85;font-size:10.5px;line-height:1.4}.board-kpi em{display:inline-block;border-radius:999px;padding:3px 7px;margin:-1px 0 7px;font-size:9.5px;font-style:normal;font-weight:750}.board-kpi em.up{background:#e4f3e9;color:#23713e}.board-kpi em.down{background:#fdeaea;color:#b72e2e}.board-kpi em.flat{background:#edf1ef;color:#64746b}
.board-narrative-grid,.board-chart-grid { display:grid; grid-template-columns:1fr 1fr; gap:18px; }
.board-card { background:#fff; border:1px solid #dde8e1; border-radius:17px; padding:20px 22px; box-shadow:0 8px 24px rgba(29,64,44,.055); min-width:0; }
.board-card-title { display:flex;align-items:center;gap:7px;color:#1c3e2a;font-size:15px;font-weight:750;margin-bottom:14px}.board-card-title.spread{justify-content:space-between}.board-card-title.spread b{font-size:12px;color:#14663a;background:#eaf4ee;border-radius:999px;padding:5px 10px}
.board-statement { display:flex;gap:12px;align-items:flex-start;padding:11px 0;border-bottom:1px solid #edf2ef;color:#374a40;font-size:13px;line-height:1.5}.board-statement:last-child{border-bottom:0}.board-statement b{display:grid;place-items:center;width:23px;height:23px;flex:0 0 23px;border-radius:50%;background:#e8f3ec;color:#14663a;font-size:11px}
.board-priorities button { width:100%;border:0;border-bottom:1px solid #edf2ef;background:transparent;padding:10px 0;display:flex;gap:10px;align-items:flex-start;text-align:left;font-family:inherit;cursor:pointer}.board-priorities button:last-child{border-bottom:0}.board-priorities button>span{width:7px;height:48px;border-radius:5px;flex:0 0 7px}.board-priorities button>div{flex:1}.board-priorities button strong,.board-priorities button small{display:block}.board-priorities button strong{font-size:13px;color:#303f37}.board-priorities button small{font-size:11px;color:#7c8982;margin-top:2px}.board-action-meta{display:flex;gap:7px;align-items:center;margin-top:6px}.board-action-meta b,.board-action-meta i{font-size:9.5px;font-style:normal;border-radius:999px;padding:2px 7px}.board-action-meta b{color:#365444;background:#eaf3ed}.board-action-meta i{color:#8a5c14;background:#fff3df}
.board-section-head { display:flex;justify-content:space-between;align-items:flex-end;margin-top:8px;padding:0 2px}.board-section-head span{font-size:10.5px;font-weight:800;letter-spacing:1.1px;color:#2e7d32}.board-section-head h2{font-size:21px;color:#173b28;margin:2px 0 0}.board-section-head p{margin:0;color:#718078;font-size:11.5px}
.board-inline-link{border:0;background:#edf6f0;color:#14663a;border-radius:999px;padding:6px 10px;font-family:inherit;font-size:10.5px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:4px}.board-inline-link:hover{background:#dfefe5}.board-division-compare{display:grid;grid-template-columns:1fr 1fr;gap:28px}.board-division-label{display:flex;align-items:center;gap:6px;font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px}.board-division-label.good{color:#2e7d32}.board-division-label.watch{color:#c62828}.board-division-row{display:grid;grid-template-columns:25px 1fr auto;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid #edf2ef}.board-division-row:last-child{border-bottom:0}.board-division-row>b{display:grid;place-items:center;width:23px;height:23px;border-radius:7px;background:#eaf3ed;color:#14663a;font-size:10px}.board-division-compare>div:last-child .board-division-row>b{background:#fdecec;color:#c62828}.board-division-row span strong,.board-division-row span small{display:block}.board-division-row span strong{font-size:12.5px;color:#2c4035}.board-division-row span small{font-size:10px;color:#829087;margin-top:2px}.board-division-row>em{font-size:17px;font-weight:800;font-style:normal;color:#173b28}
.board-health-grid { display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}.board-health-value{font-size:29px;font-weight:800;color:#14663a;letter-spacing:-.6px}.board-card>p{font-size:12px;color:#748279;min-height:36px}.board-mini-row{display:flex;justify-content:space-between;gap:15px;padding:9px 0;border-top:1px solid #edf2ef;font-size:11.5px;color:#718078}.board-mini-row strong{color:#273c30;text-align:right}
.board-chart-note{min-height:0!important;margin:7px 0 0!important;padding-top:8px;border-top:1px solid #edf2ef;font-size:10px!important}
.board-method { background:#eaf3ed;border:1px solid #d5e5da;border-radius:12px;padding:11px 14px;color:#5e7066;font-size:10.5px;line-height:1.5}.board-method strong{color:#294b36}
.alerts-more{width:100%;border:0;background:#f4f8f5;color:#14663a;border-radius:9px;padding:8px;font-family:inherit;font-size:11px;font-weight:700;cursor:pointer;margin-top:4px}
.banner.error { display: flex; align-items: center; gap: 8px; background: #fdecec; color: #c62828; border: 1px solid #f5c6c6;
  padding: 10px 14px; border-radius: 12px; margin-bottom: 16px; font-size: 13px; }
.management-hub { margin-bottom: 18px; padding: 17px; border: 1px solid #cee4d6; border-radius: 18px; background: linear-gradient(115deg, #fff 0%, #f1f8f3 100%); box-shadow: 0 7px 22px rgba(20,102,58,.07); }
.management-hub-head { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 13px; }
.management-hub-head > div { min-width: 0; display: flex; flex-direction: column; }
.management-hub-head > div > span { color: #2d7a4c; font-size: 9px; font-weight: 800; letter-spacing: 1.1px; }
.management-hub-head strong { color: #183c28; font-size: 16px; }
.management-hub-head p { margin: 1px 0 0; color: #718078; font-size: 10.5px; }
.governance-status { display: inline-flex; align-items: center; gap: 7px; flex: 0 0 auto; padding: 6px 10px; border-radius: 999px; font-size: 10px; font-weight: 700; background: #edf1ef; color: #64748b; }
.governance-status i { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.governance-status.good { background: #e2f1e8; color: #23713e; }.governance-status.warn { background: #fff1d9; color: #9a5a00; }.governance-status.bad { background: #fdeaea; color: #b72e2e; }
.management-card-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.management-card { min-width: 0; display: grid; grid-template-columns: 40px minmax(0,1fr) auto 18px; align-items: center; gap: 10px; padding: 12px; border: 1px solid #dfe9e2; border-radius: 13px; background: rgba(255,255,255,.9); text-align: left; font-family: inherit; cursor: pointer; transition: border-color .16s ease, box-shadow .16s ease, transform .16s ease; }
.management-card:hover { transform: translateY(-1px); border-color: #aacdb7; box-shadow: 0 6px 15px rgba(20,70,40,.08); }
.management-card-icon { width: 40px; height: 40px; display: grid; place-items: center; border-radius: 11px; background: #e4f2e9; }.management-card-icon.pi { background: #fff0d8; }.management-card-icon.directory { background: #e8f1fb; }
.management-card-copy { min-width: 0; display: flex; flex-direction: column; }
.management-card-copy strong { color: #263d31; font-size: 12px; }.management-card-copy small { color: #849289; font-size: 9px; margin-top: 1px; }.management-card-copy span { color: #61736a; font-size: 9.5px; margin-top: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }.management-card-copy span b { color: #234d34; }
.management-card-side { min-width: 67px; padding-left: 10px; border-left: 1px solid #e7eee9; text-align: right; }.management-card-side b,.management-card-side small { display: block; }.management-card-side b { color: #247142; font-size: 16px; }.management-card-side small { color: #849289; font-size: 8.5px; white-space: nowrap; }.management-card-side.attention b { color: #b56b00; }.management-card-side.attention small { color: #a36f22; }
.reporting-scope { display: grid; grid-template-columns: 1.15fr repeat(6,minmax(0,1fr)); gap: 0; margin: -4px 0 20px; overflow: hidden; border: 1px solid #dfe9e2; border-radius: 13px; background: #fff; box-shadow: 0 3px 12px rgba(25,65,43,.04); }
.reporting-scope-title,.reporting-scope-item { min-width: 0; padding: 10px 12px; }
.reporting-scope-title { display: flex; align-items: center; gap: 8px; background: #eef6f1; }
.reporting-scope-title span,.reporting-scope-item { display: flex; flex-direction: column; }
.reporting-scope-title strong { color: #214c31; font-size: 10.5px; }.reporting-scope-title small { color: #7d8c83; font-size: 8.5px; }
.reporting-scope-item { justify-content: center; border-left: 1px solid #e8efea; }
.reporting-scope-item span { color: #819087; font-size: 8px; font-weight: 700; letter-spacing: .35px; text-transform: uppercase; }
.reporting-scope-item strong { margin-top: 2px; overflow: hidden; color: #344b3e; font-size: 9px; font-weight: 650; line-height: 1.35; text-overflow: ellipsis; }

.section-eyebrow { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 700; letter-spacing: 0.5px;
  text-transform: uppercase; color: #14663a; margin: 8px 2px 12px; }
.section-scope { margin-left: auto; border-radius: 999px; padding: 4px 8px; background: #e9f2ec; color: #668074; font-size: 8.5px; font-weight: 700; letter-spacing: .2px; text-transform: none; }

/* Grids + panels */
.grid { display: grid; gap: 18px; margin-bottom: 20px; }
.grid-2 { grid-template-columns: 1fr 1fr; }
.grid-2-1 { grid-template-columns: 2fr 1fr; }
.grid-3 { grid-template-columns: repeat(3, 1fr); }
.panel { background: #fff; border-radius: 18px; padding: 20px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); border: 1px solid #edf2ee; }
.panel.hero { background: linear-gradient(180deg, #ffffff 0%, #fbfefb 100%); border-color: #e2ede6; }
.panel-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.panel-title { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; color: #23302a; }
.panel-title.mb { margin-bottom: 14px; }
.panel-title.spread { justify-content: space-between; width: 100%; }
.ph-ic { width: 38px; height: 38px; border-radius: 11px; background: #eaf4ee; display: flex; align-items: center; justify-content: center; }
.ph-ic.amber { background: #fbf1e2; }
.pt-main { font-size: 15px; font-weight: 700; color: #1b3a2a; }
.pt-sub { font-size: 11.5px; color: #8a978f; font-weight: 400; margin-top: -1px; }
.year-chip { font-size: 12px; font-weight: 600; color: #14663a; background: #eef6f0; padding: 5px 12px; border-radius: 20px; }
.empty { color: #9aa8a1; font-size: 13px; text-align: center; padding: 30px 0; }

/* Plain horizontal bars (cleaner than donuts for these breakdowns) */
.hbars { display: flex; flex-direction: column; gap: 12px; padding: 6px 2px; }
.hbars.big { gap: 16px; padding: 18px 2px; }
.hbar-cap { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #8a978f; margin-bottom: 2px; }
.hbar-row { display: grid; grid-template-columns: 92px 1fr auto; align-items: center; gap: 10px; }
.hbars.big .hbar-row { grid-template-columns: 80px 1fr auto; }
.hbar-lbl { font-size: 12.5px; color: #5a6b62; font-weight: 500; }
.hbar-track { height: 12px; background: #eef2f0; border-radius: 7px; overflow: hidden; }
.hbars.big .hbar-track { height: 16px; }
.hbar-fill { display: block; height: 100%; border-radius: 7px; transition: width 0.5s ease; }
.hbar-num { font-size: 13px; font-weight: 700; color: #23302a; white-space: nowrap; }
.hbar-num em { font-style: normal; font-weight: 500; color: #9aa8a1; font-size: 11.5px; }
.empty.small { padding: 14px 0; font-size: 12.5px; }

/* Performance hero */
.perf-body { display: grid; grid-template-columns: 1.5fr 1fr; gap: 20px; align-items: center; }
.perf-kpis { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.perf-chip { display: flex; align-items: center; gap: 12px; padding: 14px; border-radius: 14px;
  background: color-mix(in srgb, var(--c) 8%, #fff); border: 1px solid color-mix(in srgb, var(--c) 20%, #fff); }
.perf-ic { width: 40px; height: 40px; border-radius: 11px; display: flex; align-items: center; justify-content: center;
  background: color-mix(in srgb, var(--c) 15%, #fff); flex: 0 0 40px; }
.perf-val { font-size: 24px; font-weight: 700; color: #14432a; line-height: 1; }
.perf-lbl { font-size: 12px; color: #6b7a72; margin-top: 3px; }
.seg-wrap { margin-top: 16px; }
.seg-bar { display: flex; height: 14px; border-radius: 8px; overflow: hidden; background: #eef2f0; }
.seg { transition: width 0.4s ease; }
.seg-legend { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 9px; font-size: 12px; color: #5a6b62; }
.seg-legend i { display: inline-block; width: 9px; height: 9px; border-radius: 3px; margin-right: 5px; }
.seg-legend b { color: #23302a; }
.seg-met { font-weight: 700; color: #14663a; }
.outcome-block { margin-top: 16px; }
.ob-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.ob-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #8a978f; }
.ob-met { font-size: 13px; font-weight: 700; color: #14663a; }
.ob-bar { display: flex; height: 12px; border-radius: 7px; overflow: hidden; background: #eef2f0; }
.obseg { transition: width 0.4s ease; }
.ob-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.ochip { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; font-family: inherit; font-size: 12px; color: #5a6b62; background: #fff; border: 1px solid #e2e8e4; padding: 5px 11px; border-radius: 16px; transition: all 0.15s ease; }
.ochip:hover { border-color: #14663a; background: #f4faf6; transform: translateY(-1px); }
.ochip b { color: #23302a; }
.ocdot { width: 9px; height: 9px; border-radius: 3px; }

.approval-row { display: flex; align-items: center; gap: 16px; margin-top: 15px; font-size: 12.5px; color: #5a6b62; flex-wrap: wrap; }
.approval-row b { color: #23302a; margin-left: 3px; }
.ar-label { font-weight: 600; color: #33403a; }
.ap { display: flex; align-items: center; gap: 6px; }
.ap-dot { width: 9px; height: 9px; border-radius: 50%; } .ap-dot.g { background: #2e7d32; } .ap-dot.a { background: #e08600; } .ap-dot.b { background: #1565c0; } .ap-dot.r { background: #c62828; }

/* Fundraising */
.fund-gauge { text-align: center; }
.fund-empty { text-align: center; padding: 44px 0; }
.fe-main { font-size: 14px; font-weight: 600; color: #7a8a80; margin-top: 8px; }
.fe-sub { font-size: 12px; color: #9aa8a1; margin-top: 2px; }
.fund-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 6px; }
.fs { text-align: center; padding: 8px 4px; border-radius: 10px; background: #f7faf8; }
.fs-v { font-size: 14px; font-weight: 700; color: #14432a; }
.fs-l { font-size: 10.5px; color: #8a978f; margin-top: 2px; }

/* Donation cards */
.don-card { display: flex; flex-direction: column; gap: 14px; }
.don-head { display: flex; align-items: center; gap: 13px; }
.don-ic { width: 46px; height: 46px; border-radius: 13px; display: flex; align-items: center; justify-content: center; flex: 0 0 46px; }
.don-ic.green { background: #e5f1ea; } .don-ic.amber { background: #fbf1e2; }
.don-title { font-size: 14.5px; font-weight: 700; color: #1b3a2a; }
.don-sub { font-size: 12px; color: #8a978f; margin-top: 1px; }
.don-amount { font-size: 32px; font-weight: 700; color: #14432a; letter-spacing: -0.5px; }

/* Live fundraising (Google Sheet) */
.fr-live { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: #2e7d32; background: #eaf4ee; border: 1px solid #cfe5d8; border-radius: 999px; padding: 4px 11px; }
.fr-dot { width: 7px; height: 7px; border-radius: 50%; background: #2e7d32; animation: frPulse 1.6s ease-in-out infinite; }
@keyframes frPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
.fr-hero { display: flex; align-items: baseline; gap: 10px; margin: 6px 0 10px; }
.fr-big { font-size: 34px; font-weight: 800; color: #14663a; letter-spacing: -0.5px; line-height: 1; }
.fr-cap { font-size: 12.5px; color: #8a978f; font-weight: 600; }
.fr-platforms { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.fr-pf { display: flex; align-items: center; gap: 7px; background: #f7faf8; border: 1px solid #eef2f0; border-radius: 10px; padding: 6px 12px; }
.fr-pf-n { font-size: 12px; color: #5a6b62; font-weight: 600; }
.fr-pf-v { font-size: 13px; color: #14663a; font-weight: 700; }
.fr-latest { font-size: 12.5px; color: #33403a; margin-top: 10px; }
.fr-latest b { color: #14663a; }
.fr-latest .dim { color: #9aa8a1; font-weight: 500; }
.fr-src { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #a7b3ac; margin-top: 8px; }

/* Digital outreach */
.dg-flag { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #c62828; background: #fdf3f3; border: 1px solid #f3d6d6; border-radius: 10px; padding: 7px 11px; margin-top: 10px; }
.dg-flag.ok { color: #2e7d32; background: #f0f8f2; border-color: #d6ebdd; }
.seo-score { text-align: center; padding: 6px 0 12px; }
.seo-score-v { font-size: 40px; font-weight: 800; color: #14663a; line-height: 1; }
.seo-score-l { font-size: 12px; color: #8a978f; font-weight: 600; margin-top: 3px; }
.seo-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.seo-tile { background: #f7faf8; border: 1px solid #eef2f0; border-radius: 12px; padding: 12px; text-align: center; }
.seo-tv { font-size: 20px; font-weight: 700; color: #1b3a2a; }
.seo-tl { font-size: 10.5px; color: #8a978f; font-weight: 600; margin-top: 2px; }
.dg-note { display: flex; align-items: center; gap: 5px; font-size: 11.5px; color: #8a978f; margin-top: 12px; }
.dg-note b { color: #14663a; }
.dg-tiles { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 12px; }
.dg-tile { background: #f7faf8; border: 1px solid #eef2f0; border-radius: 12px; padding: 12px; text-align: center; }
.dg-tv { font-size: 22px; font-weight: 800; color: #14663a; line-height: 1; }
.dg-tl { font-size: 10.5px; color: #8a978f; font-weight: 600; margin-top: 4px; }

/* Stat cards */
.stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 22px; }
.stat-card { background: #fff; border-radius: 16px; padding: 20px 20px 16px; box-shadow: 0 3px 14px rgba(20,40,30,0.05);
  border: 1px solid #edf2ee; transition: transform 0.18s ease, box-shadow 0.18s ease; }
.stat-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(20,40,30,0.1); }
.stat-head { display: flex; align-items: center; justify-content: space-between; }
.stat-label { font-size: 12px; font-weight: 600; letter-spacing: 0.4px; color: #6b7a72; }
.stat-num { font-size: 34px; font-weight: 700; color: #14432a; line-height: 1.15; margin-top: 8px; }
.stat-foot { font-size: 12.5px; font-weight: 600; margin-top: 4px; display: flex; align-items: center; }
.stat-card.clickable { cursor: pointer; }
.perf-chip.clickable { cursor: pointer; transition: transform 0.15s ease, box-shadow 0.15s ease; }
.perf-chip.clickable:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(20, 40, 30, 0.1); }

/* Drill-down dialog */
.dlg { background: #fff; border-radius: 16px; overflow: hidden; }
.dlg-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; font-size: 15px; font-weight: 700; color: #1b3a2a; border-bottom: 1px solid #eef2f0; }
.dlg-count { color: #8a978f; font-weight: 500; font-size: 13px; }
.dlg-loading { display: flex; justify-content: center; padding: 40px; }
.dlg-body { max-height: 60vh; overflow-y: auto; }
.attendance-bar { width: 100%; text-align: left; cursor: pointer; border: 0; background: transparent; font: inherit; }
.attendance-bar:hover, .attendance-bar:focus-visible { background: #eef6f0; outline: 2px solid #14663a; border-radius: 6px; }
.attendance-tools { display: flex; flex-wrap: wrap; gap: 12px; padding: 16px 20px 8px; }
.attendance-tools label { display: flex; flex-direction: column; gap: 5px; color: #526359; font-size: 12px; flex: 1 1 180px; }
.attendance-tools input, .attendance-tools select { border: 1px solid #ccd9d0; border-radius: 6px; padding: 9px; background: #fff; color: #23302a; min-height: 40px; }
.attendance-note { padding: 4px 20px 12px; margin: 0; color: #64748b; font-size: 12px; }
.attendance-table { min-width: 800px; }
.attendance-table td { vertical-align: top; }
.attendance-table td:nth-child(3) { min-width: 220px; max-width: 340px; overflow-wrap: anywhere; }
.attendance-table a { display: inline-block; margin-top: 5px; color: #14663a; font-size: 12px; }
.attendance-meta { margin-top: 4px; color: #64748b; font-size: 11px; }
.attendance-late { margin-top: 4px; color: #a65e00; font-size: 11px; }
.gps-badge { display: inline-block; padding: 4px 8px; border-radius: 5px; white-space: nowrap; font-size: 11px; font-weight: 700; }
.gps-badge.flagged { color: #b42318; background: #ffebe9; }
.gps-badge.neutral { color: #526359; background: #edf2ef; }
.dlg-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dlg-table th { text-align: left; position: sticky; top: 0; background: #f7faf8; color: #8a978f; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; padding: 8px 20px; }
.dlg-table td { padding: 9px 20px; border-bottom: 1px solid #f4f7f5; color: #33403a; }
.dlg-table td.dim { color: #9aa8a1; }
.dlg-table th.r, .dlg-table td.r { text-align: right; }
.dlg-empty { text-align: center; color: #9aa8a1; padding: 24px; }
.dlg-table td.dlg-act { text-align: right; padding: 6px 16px; white-space: nowrap; }
.mk-btn { display: inline-flex; align-items: center; gap: 4px; font-family: inherit; font-size: 11.5px; font-weight: 600; border-radius: 999px; padding: 3px 10px; cursor: pointer; border: 1px solid transparent; transition: background 0.14s ease; }
.mk-btn.flag { color: #c0562c; background: #fdf3ef; border-color: #f2d8cd; }
.mk-btn.flag:hover { background: #fbe6dd; }
.mk-btn.restore { color: #2e7d32; background: #eef7f0; border-color: #cfe5d8; }
.mk-btn.restore:hover { background: #e0f0e6; }
.mk-btn.auto { color: #1565c0; background: #eef4fb; border-color: #cfe0f2; margin-right: 6px; }
.mk-btn.exempt { color: #7a4a08; background: #fdf6ec; border-color: #f2d19b; margin-right: 6px; }
.mk-btn.exempt:hover { background: #fbecd4; }
.mk-btn.auto:hover { background: #e2edf9; }
.mk-btn.auto.on { color: #2e7d32; background: #eef7f0; border-color: #cfe5d8; }
.btn.ghost.test-hidden { color: #c0562c; border-color: #f2d8cd; }
.btn.ghost.test-hidden:hover { background: #fdf3ef; }
.btn.ghost.auto-chip { color: #1565c0; border-color: #cfe0f2; }
.btn.ghost.auto-chip:hover { background: #eef4fb; }
.btn.ghost.exempt-chip { color: #7a4a08; border-color: #f2d19b; }
.btn.ghost.exempt-chip:hover { background: #fdf6ec; }

/* Performance header actions */
.ph-actions { display: flex; align-items: center; gap: 8px; }
.link-chip { display: inline-flex; align-items: center; gap: 5px; cursor: pointer; font-family: inherit;
  font-size: 12px; font-weight: 600; color: #14663a; background: #fff; border: 1px solid #cfe5d8;
  padding: 5px 11px; border-radius: 20px; transition: background 0.15s ease; }
.link-chip:hover { background: #eef6f0; }
.link-chip.resigned { color: #b45309; border-color: #f2d19b; background: #fdf6ec; }
.link-chip.resigned:hover { background: #fbecd4; }

/* Current-month focus banner */
.cm-focus { display: flex; align-items: center; justify-content: space-between; gap: 18px; flex-wrap: wrap;
  background: linear-gradient(135deg, #fff7ec 0%, #fffdf9 60%); border: 1px solid #f3dcb6;
  border-left: 5px solid #e0820a; border-radius: 16px; padding: 16px 20px; margin-bottom: 16px;
  box-shadow: 0 6px 20px rgba(224,130,10,0.08); }
.cmf-lead { display: flex; align-items: center; gap: 13px; }
.cmf-ic { width: 46px; height: 46px; border-radius: 13px; background: #fdecd0; display: flex; align-items: center; justify-content: center; flex: 0 0 46px; }
.cmf-title { font-size: 16px; font-weight: 800; color: #7a4a08; letter-spacing: 0.2px; }
.cmf-sub { font-size: 12px; color: #a9865a; margin-top: 1px; }
.cmf-stats { display: flex; gap: 10px; flex-wrap: wrap; }
.cmf-stat { text-align: center; min-width: 104px; padding: 9px 16px; border-radius: 13px; border: 1px solid #eee0c8;
  background: #fff; font-family: inherit; cursor: default; transition: transform 0.12s ease, box-shadow 0.12s ease; }
button.cmf-stat { cursor: pointer; }
button.cmf-stat:hover { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(0,0,0,0.08); }
.cmf-v { font-size: 27px; font-weight: 800; line-height: 1.05; }
.cmf-l { font-size: 10.5px; font-weight: 700; color: #8a978f; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.4px; }
.cmf-stat.due .cmf-v { color: #1565c0; }
.cmf-stat.pending .cmf-v { color: #b45309; }
.cmf-stat.overdue { border-color: #f3c9c9; background: #fdf3f3; }
.cmf-stat.overdue .cmf-v { color: #c62828; }
.cmf-stat.met .cmf-v { color: #2e7d32; }

/* Month select (daily attendance) */
.month-select { font-family: inherit; font-size: 12.5px; font-weight: 600; color: #14663a; background: #fff;
  border: 1px solid #cfe5d8; border-radius: 20px; padding: 5px 12px; cursor: pointer; outline: none; }
.month-select:hover { background: #f4faf6; }

/* Daily attendance legend */
.daily-legend { display: flex; align-items: center; gap: 18px; margin-top: 8px; padding-left: 6px; font-size: 11.5px; color: #6b7a72; font-weight: 600; }
.daily-legend i { display: inline-block; width: 11px; height: 11px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; }
.daily-legend .dl-note { color: #a7b3ac; font-weight: 500; font-style: italic; margin-left: auto; }

/* ── Executive Briefing ─────────────────────────────────────────────────── */
.brief-grid { display: grid; grid-template-columns: 290px 1fr 350px; gap: 16px; margin-bottom: 20px; }
.hint { font-size: 11px; color: #a7b3ac; font-weight: 500; }
/* Pulse */
.pulse-panel { display: flex; flex-direction: column; }
.pulse-main { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 4px 0 12px; }
.pulse-ring { width: 118px; height: 118px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: background 0.6s ease; }
.pulse-inner { width: 92px; height: 92px; border-radius: 50%; background: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: inset 0 0 0 1px #eef2f0; }
.pulse-score { font-size: 34px; font-weight: 800; line-height: 1; }
.pulse-den { font-size: 11px; color: #9aa8a0; font-weight: 600; }
.pulse-status { color: #fff; font-size: 12px; font-weight: 700; padding: 3px 14px; border-radius: 999px; letter-spacing: 0.3px; }
.pulse-play { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; font-family: inherit; font-size: 11.5px; font-weight: 700; color: #14663a; background: #eef6f0; border: 1px solid #cfe5d8; border-radius: 999px; padding: 3px 11px 3px 8px; cursor: pointer; transition: background 0.14s ease; }
.pulse-play:hover { background: #e2f0e8; }
.pulse-month { font-size: 11.5px; font-weight: 700; color: #5a6b62; letter-spacing: 0.2px; }
.pulse-month.live { color: #2e7d32; }
.pulse-scrub { display: flex; flex-wrap: wrap; gap: 4px; justify-content: center; margin: 12px 0 4px; }
.ptick { font-family: inherit; font-size: 10.5px; font-weight: 600; color: #8a978f; background: #f4f7f5; border: 1px solid transparent; border-radius: 7px; padding: 3px 7px; cursor: pointer; transition: all 0.14s ease; }
.ptick:hover { background: #e8f0eb; color: #14663a; }
.ptick.on { background: #14663a; color: #fff; }
.ptick.now { font-weight: 700; }
.ptick.now.on { background: #2e7d32; }
.pulse-bars { display: flex; flex-direction: column; gap: 8px; margin-top: auto; }
.pb-row { display: flex; align-items: center; gap: 9px; }
.pb-lbl { flex: 0 0 96px; font-size: 11.5px; color: #5a6b62; font-weight: 600; }
.pb-track { flex: 1; height: 8px; background: #eef2f0; border-radius: 5px; overflow: hidden; }
.pb-fill { display: block; height: 100%; border-radius: 5px; transition: width 0.6s ease; }
.pb-val { flex: 0 0 38px; text-align: right; font-size: 12px; font-weight: 700; color: #33403a; }
.pulse-method{font-size:9.5px;color:#91a098;text-align:center;margin-top:9px}
/* Briefing narrative + pace */
.brief-panel { display: flex; flex-direction: column; }
.brief-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.brief-list li { display: flex; align-items: flex-start; gap: 6px; font-size: 13px; color: #33403a; line-height: 1.5; }
.brief-list li .v-icon { margin-top: 2px; flex-shrink: 0; }
.brief-list li.dim { color: #9aa8a1; }
.pace-box { margin-top: auto; padding-top: 14px; border-top: 1px dashed #e5ece8; }
.pace-head { display: flex; align-items: center; justify-content: space-between; font-size: 12px; font-weight: 700; color: #14663a; margin-bottom: 9px; }
.pace-head span:first-child { display: inline-flex; align-items: center; gap: 5px; }
.pace-verdict { font-size: 11px; padding: 2px 10px; border-radius: 999px; font-weight: 700; }
.pace-verdict.on { color: #2e7d32; background: #eaf4ee; } .pace-verdict.warn { color: #b45309; background: #fdf3e3; }
.pace-verdict.bad { color: #c62828; background: #fdecec; } .pace-verdict.neutral { color: #64748b; background: #eef2f0; }
.pace-line { display: flex; align-items: center; gap: 9px; margin-bottom: 7px; }
.pace-k { flex: 0 0 44px; font-size: 13px; font-weight: 700; color: #33403a; text-align: right; }
.pace-t { flex: 0 0 130px; height: 8px; background: #eef2f0; border-radius: 5px; overflow: hidden; }
.pace-f { display: block; height: 100%; border-radius: 5px; }
.pace-f.time { background: #94a3b8; } .pace-f.done { background: #14663a; }
.pace-cap { font-size: 11.5px; color: #8a978f; font-weight: 500; }
/* Alerts */
.alert-panel { display: flex; flex-direction: column; }
.alert-count { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px; background: #c62828; color: #fff; font-size: 11px; font-weight: 700; border-radius: 999px; margin-left: 4px; }
.alert-list { display: flex; flex-direction: column; gap: 8px; }
.alert-item { display: flex; align-items: center; gap: 11px; width: 100%; text-align: left; font-family: inherit; cursor: pointer;
  background: #fbfdfc; border: 1px solid #eef2f0; border-left: 3px solid #cbd5d0; border-radius: 11px; padding: 9px 11px; transition: background 0.14s ease, transform 0.1s ease; }
.alert-item:hover { background: #f2f8f4; transform: translateX(2px); }
.alert-item.noact { cursor: default; }
.alert-item.noact:hover { background: #fbfdfc; transform: none; }
.alert-item.sev3 { border-left-color: #c62828; } .alert-item.sev2 { border-left-color: #1565c0; } .alert-item.sev1 { border-left-color: #94a3b8; }
.ai-ic { width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex: 0 0 34px; }
.ai-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ai-title { font-size: 13px; font-weight: 700; color: #23302a; }
.ai-detail { font-size: 11.5px; color: #8a978f; }
.all-clear { display: flex; align-items: center; gap: 8px; color: #2e7d32; font-size: 13px; font-weight: 600; padding: 18px 4px; }
/* Projections + rankings */
.pip-dashboard-grid{align-items:start}.pip-chart-panel{min-width:0}.pip-snapshot{position:relative;overflow:hidden}.pip-snapshot:after{content:"";position:absolute;width:150px;height:150px;border-radius:50%;background:#eef7f1;right:-65px;top:-70px;z-index:0}.pip-snapshot>*{position:relative;z-index:1}.pip-snapshot-head{display:flex;align-items:center;justify-content:space-between;gap:10px}.pip-period-badge{display:inline-flex;align-items:center;gap:5px;padding:5px 8px;border:1px solid #cae2d2;border-radius:999px;background:#f0f8f3;color:#256f44;font-size:9px;font-weight:700;white-space:nowrap}.pip-scope-note{margin:7px 0 12px;padding:8px 10px;border-left:3px solid #58a474;border-radius:0 8px 8px 0;background:#f5faf7;color:#65776d;font-size:9.5px;line-height:1.45}.pip-score-card{display:flex;align-items:center;gap:17px;padding:2px 0 17px}.pip-score-ring{width:104px;height:104px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex:0 0 104px}.pip-score-ring>div{width:80px;height:80px;border-radius:50%;background:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;box-shadow:inset 0 0 0 1px #edf2ef}.pip-score-ring strong{font-size:25px;color:#14432a;line-height:1}.pip-score-ring span{font-size:9px;text-transform:uppercase;color:#8a978f;font-weight:700;margin-top:4px}.pip-score-copy{display:flex;flex-direction:column}.pip-score-copy strong{font-size:27px;color:#14663a}.pip-score-copy span{font-size:11.5px;color:#6b7a72;line-height:1.4}.pip-mini-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}.pip-mini-grid button{border:1px solid #e6eee9;background:#f8fbf9;border-radius:11px;padding:11px;text-align:left;cursor:pointer;font-family:inherit}.pip-mini-grid button:hover{background:#eef7f1}.pip-mini-grid strong{display:block;font-size:20px;color:#33403a}.pip-mini-grid strong.ok{color:#2e7d32}.pip-mini-grid strong.warn{color:#b56b00}.pip-mini-grid strong.bad{color:#c62828}.pip-mini-grid span{display:block;font-size:10.5px;color:#7a8a80;margin-top:2px}.pip-watch{display:flex;width:100%;gap:9px;align-items:flex-start;background:#fdf3f3;border:1px solid #f3d6d6;border-radius:11px;padding:10px;margin-top:10px;text-align:left;cursor:pointer;font-family:inherit}.pip-watch>div{flex:1}.pip-watch:hover{background:#f9e8e8}.pip-watch strong,.pip-watch span{display:block}.pip-watch strong{font-size:12px;color:#9f2525}.pip-watch span{font-size:10.5px;color:#9b6b6b;margin-top:2px}
.proj-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.proj { background: #f7faf8; border: 1px solid #eef2f0; border-radius: 12px; padding: 14px 12px; text-align: center; }
.proj-v { font-size: 22px; font-weight: 800; color: #14663a; line-height: 1.1; }
.proj-l { font-size: 10.5px; color: #8a978f; font-weight: 600; margin-top: 4px; }
.proj-note { display: flex; align-items: center; gap: 5px; font-size: 11.5px; color: #a7b3ac; margin-top: 11px; }
.rank-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.rank { border-radius: 12px; padding: 11px 13px; border: 1px solid #eef2f0; }
.rank.best { background: #f0f8f2; border-color: #d6ebdd; } .rank.worst { background: #fdf5f3; border-color: #f3ddd6; }
.rk-l { font-size: 10.5px; font-weight: 700; letter-spacing: 0.3px; text-transform: uppercase; color: #7a8a80; }
.rank.best .rk-l { color: #2e7d32; } .rank.worst .rk-l { color: #c0562c; }
.rk-v { font-size: 14.5px; font-weight: 700; color: #23302a; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rk-s { font-size: 11.5px; color: #8a978f; margin-top: 1px; }
.rank-emps { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin-top: 13px; }
.re-lbl { font-size: 11px; font-weight: 700; color: #6b7a72; text-transform: uppercase; letter-spacing: 0.3px; }
.re-chip { font-size: 12px; color: #33403a; background: #eef6f0; border-radius: 999px; padding: 3px 11px; font-weight: 500; }
.re-chip b { color: #14663a; }
@media (max-width: 1080px) { .brief-grid { grid-template-columns: 1fr; } }
@media (max-width: 720px) { .proj-grid { grid-template-columns: 1fr; } .rank-grid { grid-template-columns: 1fr; } }

/* Division league table */
.lg-wrap { overflow-x: auto; }
.lg-table { width: 100%; border-collapse: separate; border-spacing: 3px; font-size: 13px; }
.lg-table th { text-align: center; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; color: #8a978f; padding: 6px 10px; cursor: pointer; white-space: nowrap; user-select: none; }
.lg-table th:hover { color: #14663a; }
.lg-table th.lg-name { text-align: left; }
.lg-table td.lg-name { text-align: left; font-weight: 600; color: #23302a; white-space: nowrap; padding: 8px 12px; background: #f7faf8; border-radius: 8px; }
.lg-table td.lg-name.clickable { cursor: pointer; transition: background 0.12s ease; }
.lg-table td.lg-name.clickable:hover { background: #eaf4ee; color: #14663a; }
.lg-cell { text-align: center; font-weight: 700; padding: 8px 10px; border-radius: 8px; min-width: 60px; }

/* Retention & attrition */
.rt-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
.rt-tile { background: #f7faf8; border: 1px solid #eef2f0; border-radius: 12px; padding: 13px 15px; }
.rt-v { font-size: 26px; font-weight: 800; color: #14663a; line-height: 1; }
.rt-v small { font-size: 13px; font-weight: 700; color: #6b7d73; }
.rt-v.pos { color: #2e7d32; } .rt-v.warn { color: #e08600; } .rt-v.neg { color: #c62828; }
.rt-l { font-size: 11.5px; color: #5a6b62; font-weight: 600; margin-top: 6px; display: flex; flex-direction: column; gap: 1px; }
.rt-hint { font-size: 10px; color: #9aa8a1; font-weight: 500; }
.rt-hint.clickable { color: #14663a; cursor: pointer; font-weight: 600; }
.rt-hint.clickable:hover { text-decoration: underline; }
.rt-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 14px; }
.rt-grid:last-child { margin-bottom: 0; }
.rt-card { background: #fff; border: 1px solid #eef2f0; border-radius: 12px; padding: 14px 16px; }
.rt-title { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 700; color: #16321f; margin-bottom: 12px; }
.rt-title .rt-hint { margin-left: 2px; }
.rt-bars { display: flex; flex-direction: column; gap: 9px; }
.rt-bar-row { display: flex; align-items: center; gap: 10px; }
.rt-bar-name { flex: 0 0 130px; font-size: 12px; color: #33403a; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rt-bar-name.clickable { cursor: pointer; }
.rt-bar-name.clickable:hover { color: #14663a; }
.rt-bar-track { flex: 1; height: 10px; background: #eef2f0; border-radius: 5px; overflow: hidden; }
.rt-bar-fill { display: block; height: 100%; border-radius: 5px; background: #14663a; transition: width 0.6s ease; }
.rt-bar-fill.warn { background: #e08600; } .rt-bar-fill.neg { background: #c62828; }
.rt-bar-count { flex: 0 0 66px; text-align: right; font-size: 12px; font-weight: 700; color: #33403a; }
@media (max-width: 860px) { .rt-tiles { grid-template-columns: repeat(2, 1fr); } .rt-grid { grid-template-columns: 1fr; } }

/* Employee spotlight */
.spotlight-search { max-width: 460px; }
.spot-loading { display: flex; justify-content: center; padding: 30px; }
.spot-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #9aa8a1; font-size: 13px; padding: 26px 0; }
.spot-body { margin-top: 18px; }
.spot-head { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 15px; border-bottom: 1px solid #eef2f0; }
.spot-avatar { width: 52px; height: 52px; border-radius: 14px; background: linear-gradient(135deg, #14663a, #2e7d32); display: flex; align-items: center; justify-content: center; flex: 0 0 52px; }
.spot-id { flex: 1; min-width: 0; }
.spot-name { font-size: 17px; font-weight: 700; color: #1b3a2a; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.spot-meta { font-size: 12.5px; color: #8a978f; margin-top: 2px; }
.spot-meta2 { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 7px; font-size: 12px; color: #5a6b62; font-weight: 600; }
.spot-meta2 span { display: inline-flex; align-items: center; gap: 4px; }
.probation-pill { font-size: 10.5px; font-weight: 700; color: #c62828; background: #fdecec; border: 1px solid #f6cccc; border-radius: 999px; padding: 2px 9px; letter-spacing: 0.3px; }
/* Effectiveness score hero */
.score-hero { display: flex; align-items: center; gap: 12px; flex: 0 0 auto; padding-left: 6px; }
.sh-ring { width: 74px; height: 74px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex: 0 0 74px; }
.sh-inner { width: 58px; height: 58px; border-radius: 50%; background: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: inset 0 0 0 1px #eef2f0; }
.sh-num { font-size: 24px; font-weight: 800; line-height: 1; }
.sh-den { font-size: 10px; color: #9aa8a0; font-weight: 600; margin-top: 1px; }
.sh-right { display: flex; flex-direction: column; gap: 3px; }
.sh-band { align-self: flex-start; color: #fff; font-size: 11px; font-weight: 700; border-radius: 999px; padding: 2px 10px; letter-spacing: 0.2px; }
.sh-rank { font-size: 14px; color: #1b3a2a; font-weight: 600; }
.sh-rank b { font-size: 16px; }
.sh-rank span { color: #8a978f; font-weight: 500; }
.sh-top { font-size: 11px; color: #8a978f; font-weight: 600; }
.spot-metrics-row { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px; }
.spot-metrics { display: flex; align-items: stretch; gap: 10px; }
.spot-metric { text-align: center; padding: 6px 16px; border-radius: 12px; background: #f7faf8; border: 1px solid #eef2f0; min-width: 92px; }
.spot-metric.metric-hero { background: #eaf4ee; border: 1px solid #cfe5d8; }
.spot-metric .sm-val { font-size: 28px; font-weight: 700; color: #14663a; line-height: 1.1; }
.spot-metric .sm-val.att { font-size: 24px; color: #5a6b62; }
.spot-metric .sm-lbl { font-size: 10.5px; color: #8a978f; margin-top: 2px; font-weight: 600; letter-spacing: 0.2px; }
/* Scorecard: compare + drivers */
.scorecard { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 18px; background: #f7faf8; border: 1px solid #eef2f0; border-radius: 14px; padding: 16px 18px; }
.scard-title { font-size: 11px; font-weight: 700; color: #6b7a72; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px; }
.cmp-row, .drv-row { display: flex; align-items: center; gap: 10px; margin-bottom: 9px; }
.cmp-lbl { flex: 0 0 118px; font-size: 12px; color: #5a6b62; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cmp-track, .drv-track { flex: 1; height: 9px; background: #e8eeeb; border-radius: 6px; overflow: hidden; }
.cmp-fill, .drv-fill { display: block; height: 100%; border-radius: 6px; transition: width .5s ease; }
.cmp-fill.muted { background: #94a3b8; } .cmp-fill.muted2 { background: #cbd5d0; }
.cmp-val, .drv-val { flex: 0 0 40px; text-align: right; font-size: 13px; font-weight: 700; color: #33403a; }
.drv-val.na { color: #b6c1ba; font-weight: 600; font-size: 11.5px; }
.cmp-foot { font-size: 11.5px; color: #8a978f; margin-top: 6px; font-weight: 600; }
.cmp-foot b { color: #14663a; }
.drv-lbl { flex: 0 0 152px; font-size: 12px; color: #5a6b62; font-weight: 600; }
.drv-lbl i { color: #a7b3ac; font-style: normal; font-weight: 600; }
@media (max-width: 720px) { .scorecard { grid-template-columns: 1fr; } .score-hero { padding-left: 0; margin-top: 10px; } .spot-head { flex-wrap: wrap; } }
.spot-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(108px, 1fr)); gap: 12px; margin-top: 16px; }
.spot-cell { text-align: center; background: #f7faf8; border-radius: 12px; padding: 14px 6px; }
.sc-v { font-size: 22px; font-weight: 700; }
.sc-l { font-size: 11px; color: #8a978f; margin-top: 3px; }
.spot-tags { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 14px; }
.tag-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #8a978f; margin-right: 4px; }
.stag { font-size: 11.5px; color: #5a6b62; background: #eef6f0; border: 1px solid #dcebe1; padding: 4px 10px; border-radius: 16px; }
.stag b { color: #14663a; }
.stag.t-green { background: #eaf4ee; border-color: #cfe5d8; } .stag.t-green b { color: #2e7d32; }
.stag.t-amber { background: #fdf3e3; border-color: #f2ddb8; } .stag.t-amber b { color: #b56b00; }
.stag.t-grey { background: #f1f4f2; border-color: #e2e8e4; } .stag.t-grey b { color: #64748b; }
.stag.t-red { background: #fdecec; border-color: #f5c6c6; } .stag.t-red b { color: #c62828; }

.spot-outcome { margin-top: 16px; }
.so-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.so-link { display: inline-flex; align-items: center; gap: 3px; font-family: inherit; font-size: 12px; font-weight: 600; color: #14663a; background: none; border: none; cursor: pointer; }
.so-link:hover { text-decoration: underline; }
.so-bar { display: flex; height: 12px; border-radius: 7px; overflow: hidden; background: #eef2f0; }
.soseg { transition: width 0.4s ease; }
.so-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
@media (max-width: 960px) { .spot-grid { grid-template-columns: repeat(2, 1fr); } }

.pill { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px; }
.pill.red { background: #fdecec; color: #c62828; }
.pill.amber { background: #fdf3e3; color: #b56b00; }
.list { list-style: none; margin: 0; padding: 0; }
.list li { padding: 9px 0; border-bottom: 1px solid #f1f4f2; } .list li:last-child { border-bottom: none; }
.li-main { font-size: 13.5px; font-weight: 600; color: #23302a; }
.li-sub { font-size: 12px; color: #8a978f; margin-top: 1px; }
.mini-table { width: 100%; border-collapse: collapse; font-size: 12.8px; }
.mini-table th { text-align: left; color: #8a978f; font-weight: 600; padding: 6px 4px; border-bottom: 1px solid #eef2f0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
.mini-table td { padding: 8px 4px; border-bottom: 1px solid #f4f7f5; color: #33403a; }
.mini-table tr:last-child td { border-bottom: none; }
.mini-table .r { text-align: right; } .mini-table.mt { margin-top: 10px; }
.foot-note { margin-top: 10px; font-size: 12.5px; color: #6b7a72; } .foot-note strong { color: #14663a; }
.big-metric { font-size: 24px; font-weight: 700; color: #14663a; }
.metric-sub { font-size: 12px; color: #6b7a72; margin-top: 2px; }

/* External Media News Section Styles */
.media-news-panel { margin-top: 16px; }
.media-stat-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; background: #eef6ea; color: #1b6b3a; border: 1px solid #cce4d6; }
.media-stat-chip.tier { background: #eef4fb; color: #1565c0; border-color: #cfe0f2; }
.media-stat-chip.risk { background: #fdecec; color: #c62828; border-color: #f3cccc; }
.mn-da { font-size: 10px; font-weight: 800; border-radius: 4px; padding: 1px 5px; margin-left: 2px; }
.mn-da.t1 { color: #1b6b3a; background: #e2f1e8; } .mn-da.t2 { color: #1565c0; background: #eaf1fb; } .mn-da.t3 { color: #64748b; background: #eef2f0; }
.link-chip.highlight { background: #14663a; color: #fff; border-color: #1a7a46; }
.link-chip.highlight:hover { background: #1a7a46; color: #fff; }
.media-loader { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 24px; color: #8aa89a; font-size: 13px; }
.media-news-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; margin-top: 16px; }
.mn-card { background: #f9fbf9; border: 1px solid #e5ebe7; border-radius: 10px; padding: 14px 16px; display: flex; flex-direction: column; justify-content: space-between; transition: all 0.2s ease; }
.mn-card:hover { border-color: #14663a; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
.mn-card.positive { border-left: 4px solid #2e7d32; }
.mn-card.negative { border-left: 4px solid #c62828; }
.mn-card.neutral { border-left: 4px solid #64748b; }
.mn-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; font-size: 11.5px; }
.mn-pub { font-weight: 700; color: #14663a; display: flex; align-items: center; gap: 4px; }
.mn-badge { font-size: 10px; font-weight: 800; padding: 2px 6px; border-radius: 4px; display: inline-flex; align-items: center; gap: 3px; }
.mn-badge.positive { background: #e2f1e8; color: #1b6b3a; }
.mn-badge.negative { background: #fdecec; color: #c62828; }
.mn-badge.neutral { background: #f1f5f9; color: #475569; }
.mn-title { font-size: 13.5px; font-weight: 700; color: #1e293b; text-decoration: none; line-height: 1.35; margin-bottom: 6px; }
.mn-title:hover { color: #14663a; text-decoration: underline; }
.mn-snippet { font-size: 12px; color: #64748b; line-height: 1.4; margin: 0 0 10px 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.mn-footer { font-size: 11px; color: #8aa89a; border-top: 1px dashed #e2e8e4; padding-top: 8px; display: flex; align-items: center; justify-content: space-between; }
.mn-date { display: flex; align-items: center; gap: 3px; color: #64748b; }
.mn-author { color: #8a978f; }
.media-empty { text-align: center; padding: 24px; color: #6b7a72; display: flex; flex-direction: column; align-items: center; gap: 8px; font-size: 13px; }

@media (max-width: 960px) {
  .grid-3, .grid-2, .grid-2-1, .perf-body, .perf-kpis, .fund-stats, .media-news-grid { grid-template-columns: 1fr; }
  .board-source-strip,.board-kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.board-narrative-grid,.board-chart-grid,.board-health-grid,.board-division-compare{grid-template-columns:1fr}.board-hero{align-items:flex-start;flex-direction:column}.board-asof{border-left:0;border-top:1px solid rgba(255,255,255,.26);padding:12px 0 0;width:100%}
  .ceo-topbar { padding: 12px 16px; } .ceo-body { padding: 16px; } .updated { display: none; }
  .management-card-grid { grid-template-columns: 1fr; }
  .reporting-scope { grid-template-columns: repeat(3,minmax(0,1fr)); }.reporting-scope-title { grid-column: 1 / -1; }.reporting-scope-item:nth-child(2),.reporting-scope-item:nth-child(5) { border-left: 0; }
}
@media (max-width: 620px) { .management-hub-head { align-items: flex-start; flex-direction: column; }.governance-status { align-self: flex-start; }.management-card { grid-template-columns: 40px minmax(0,1fr) 18px; }.management-card-side { grid-column: 2 / 3; padding: 7px 0 0; border-left: 0; border-top: 1px solid #e7eee9; text-align: left; }.management-card-side b,.management-card-side small { display: inline; }.management-card-side small { margin-left: 5px; }.reporting-scope { grid-template-columns: 1fr 1fr; }.reporting-scope-item:nth-child(n) { border-left: 1px solid #e8efea; border-top: 1px solid #e8efea; }.reporting-scope-item:nth-child(even) { border-left: 0; }.section-eyebrow { align-items: flex-start; flex-wrap: wrap; }.section-scope { width: 100%; margin-left: 23px; background: transparent; padding: 0; } }

/* Board-ready PDF: print-optimised layout (triggered by the Board PDF button) */
@media print {
  @page { size: A4 landscape; margin: 10mm; }
  .noprint, .btn { display: none !important; }
  .ceo-topbar { position: static; box-shadow: none; border-bottom: 2px solid #14663a; }
  .ceo-body { padding: 0; }
  .panel, .section, .rt-card, .stat-card, .cm-focus { box-shadow: none !important; break-inside: avoid; page-break-inside: avoid; }
  .section-eyebrow { break-after: avoid; page-break-after: avoid; }
  .rt-tiles, .rt-grid, .grid-3, .grid-2 { break-inside: avoid; page-break-inside: avoid; }
  .board-mode .operational-dashboard { display:none !important; }
  .board-view{gap:10px}.board-hero{padding:18px 22px;box-shadow:none}.board-hero h1{font-size:26px}.board-source-strip{gap:5px}.board-source{padding:6px 8px}.board-kpi-grid{gap:7px}.board-kpi{padding:10px 12px;min-height:93px;box-shadow:none}.board-kpi strong{font-size:22px;margin:7px 0 4px}.board-card{box-shadow:none;padding:13px 15px}.board-narrative-grid,.board-chart-grid,.board-health-grid{gap:10px}.board-section-head{margin-top:2px}.board-method{font-size:9px}.board-view>*{break-inside:avoid;page-break-inside:avoid}
  body, .ceo-dashboard { background: #fff !important; }
  a[href]:after { content: ""; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
</style>
